/*
 * 通用文档预览器（纯前端）
 *
 * 职责：
 *   - 接收 File（本地）或 string URL（已上传）
 *   - 自动按扩展名分发到 .docx / .xlsx 渲染器
 *   - 不做业务解析，纯展示
 *
 * 关键交互：
 *   - 用户在预览区选中文字 → 选区上方弹悬浮菜单
 *   - 菜单点击「加入到会话」→ onContentSelected(content)
 *   - 父组件把内容塞进聊天输入区（以引用样式展示）
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PictureOutlined,
  RestOutlined,
  SyncOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined
} from '@ant-design/icons'
import { ImagePreview } from './ImagePreview'
import { loadDocxPreview } from '../hooks/useDocxPreview'
import ExcelJS from 'exceljs'
import JSZip from 'jszip'
import {
  useFileLoader,
  detectDocType,
  getFileSourceKey
} from '../hooks/useFileLoader'
import { SelectionMenu, QuickAction } from './SelectionMenu'
import type { FileSource } from '../types'
import { trackClick, readSessionId } from '../utils/track'
import { pickAiFillSelectionKind } from '../utils/selectionKind'
import './DocumentPreview.scss'

/**
 * QuickAction.id → AI 填写交互页埋点 clickItem 的映射.
 * 工具栏 / quick edit menu 的 ID 命名(连字符)与埋点文档 spec(下划线)不同,
 * 一对多场景下(`delete_cell` / `replace_image` / `edit_cell`)合并归并.
 * 不在表里的 action(`add-image` 等) → 不上报,等 spec 后续完善.
 */
const QA_ID_TO_TRACK_ITEM: Record<string, string> = {
  // 列工具栏
  'insert-col-left': 'insert_col_left',
  'insert-col-right': 'insert_col_right',
  'clear-col': 'clear_content',
  'delete-col': 'delete_col',
  // 行工具栏
  'insert-row-above': 'insert_row_above',
  'insert-row-below': 'insert_row_below',
  'clear-row': 'clear_content',
  'delete-row': 'delete_row',
  // 修改文字
  'modify-text': 'edit_cell',
  'modify-text-selection': 'edit_cell',
  // 删除单元格(图片 / 文字 / 单选 / 多选)
  'delete-cell-image': 'delete_cell',
  'delete-cell-text': 'delete_cell',
  'delete-multi-image': 'delete_cell',
  'delete-multi-text': 'delete_cell',
  'delete-image-selection': 'delete_cell',
  'delete-text-selection': 'delete_cell',
  // 换图(单格 / 多格 / 非表格 selection)
  'replace-image': 'replace_image',
  'replace-images-batch': 'replace_image',
  'replace-image-selection': 'replace_image',
  // 查看原图
  'preview-image': 'view_original_image',
  'preview-image-selection': 'view_original_image'
}

// pickAiFillSelectionKind 已抽到 utils/selectionKind.ts(work/index.tsx 复用)

export interface SelectedContent {
  /** 用户选中的文本（给 UI chip 展示用，以及在没有 rangePayload 时作为后备） */
  text: string
  /** 来源 */
  source: 'text-selection' | 'cell-click' | 'cell-range' | 'image-selection'
  /**
   * 非表格图片选区(source==='image-selection')专用:被选中图片的 src 列表。
   * 单张时只有 1 项;多张为拖选 rubber-band 命中的全部 src。
   * AI 通过此 src 在文档里定位图片元素进行换图 / 删除。
   */
  imageSrcs?: string[]
  /**
   * imageSrcs 中每张图片的 ViewPath,跟 imageSrcs 等长一一对应。
   * 格式: body/p[N]/img[M]
   *   - N = 该图所在段落在 <w:body> 顶层 <w:p> 里的 1-based 序号(跳过 tables)
   *   - M = 该图在该段里 <w:drawing> 的 1-based 序号
   * agent 后端按这个路径在 docx XML tree 里一一对应定位元素;裸 "第 N 张"
   * 在 OOXML 里没有对应字段,agent 找不到。
   */
  imageViewPaths?: string[]
  /** 文档类型 */
  docType: 'docx' | 'xlsx'
  /** xlsx 单元格 ref（单 cell 是 "A1"，区域是 "A1:C3"） */
  cellRef?: string
  /** xlsx sheet 名 */
  sheetName?: string
  /** 结构化区域 payload（只在 cell-range 时有，发给 AI 时会被序列化进 prompt） */
  rangePayload?: RangePayload
  /**
   * 选区形状:
   *   - 'cell'(默认)  - 拖选 / 单击 cell
   *   - 'whole-col'   - 点击列号 A/B/C 触发,选中整列
   *   - 'whole-row'   - 点击行号 1/2/3 触发,选中整行
   * 决定浮窗 quick action 的可见性
   */
  selectionShape?: 'cell' | 'whole-col' | 'whole-row'
  /**
   * 内容状态:
   *   单 cell(source==='cell-click'):
   *     - 'empty' - 空 cell
   *     - 'image' - 图片 cell
   *     - 'text'  - 文字 cell
   *   多 cell 区域(source==='cell-range'):
   *     - 'all-empty' - 全部为空
   *     - 'all-image' - 全部图片
   *     - 'all-text'  - 全部文字
   *     - 'mixed'     - 混合类型(空/图/文 至少两种)
   */
  cellState?:
    | 'empty'
    | 'image'
    | 'text'
    | 'all-empty'
    | 'all-image'
    | 'all-text'
    | 'mixed'
  /**
   * quick-edit-modal 模式 hint:从浮窗"添加图片/换图/添加文字/修改"
   * 触发 onCellQuickEdit 时携带,让上层 modal 强制按对应模式渲染
   *   - 'image'       - 单格图片(添加/换图)
   *   - 'image-batch' - 多 cell 批量图片(N 张映射到 N 格,不足循环)
   *   - 'text'        - 单格文字(添加/修改)
   * (空 cell 也能"添加图片",不需要 cell 自身有 image)
   */
  quickEditMode?: 'image' | 'image-batch' | 'text'
}

/** 选区的结构化语义 payload —— 核心目标是帮 AI 理解用户意图 */
export interface RangePayload {
  sheet: string
  range: string // "A1:C3"（即使只选 1 个也用这个形式）
  rows: number
  cols: number
  /** 每列对应的表头文本：{ B: "整机机型", C: "整机编号" } */
  headers: Record<string, string>
  /** 每列的类型推断：{ B: "text", C: "text", E: "image" } */
  columnTypes: Record<string, CellDataType>
  /** 单元格矩阵（[行][列]） */
  cells: Array<Array<CellSnapshot>>
  /** 选区里为空的 cell refs（AI 填表的目标） */
  emptyRefs: string[]
  /** 选区上方 2 行同列的已填样本，帮 AI 理解填写模式 */
  sampleRows?: Array<Array<CellSnapshot>>
  /** 检测到的表头行号（1-indexed）；用于判断"选区是否包含表头" */
  headerRowR?: number | null
  /** 选区每一列在「选区+下方」是否已有非空数据；让 quick action 判断
   *  是"首次填写"还是"修改已填值"，避免把"帮我填"用在已有数据的列上 */
  columnHasData?: Record<string, boolean>
}

export interface CellSnapshot {
  ref: string // "B3"
  val: string | number | null
  displayText: string // 格式化后的展示文本
  type: CellDataType
  isEmpty: boolean
  /** 该 cell 是否是合并区的一部分（非 master 位置会被跳过） */
  isMergedPart?: boolean
  /** 若 cell 是图片（IMAGE() 函数 或 浮动图片锚定在此 cell），这是可展示的 src */
  imageSrc?: string
}

export type CellDataType =
  | 'empty'
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'image' // IMAGE() 函数 或 图片浮动锚定
  | 'formula'
  | 'hyperlink'

/**
 * xlsx 列宽 / 行高策略(仅全屏预览这类场景用;不传=保持文件原始尺寸,其他调用方不受影响)。
 * 列宽 = max(原始列宽, minColWidthPx, fitWidthPx / min(列数, fitMaxCols));
 * 行高 = max(原始行高, minRowHeightPx, fitHeightPx / min(行数, fitMaxRows))。
 */
interface ColSizing {
  /** 绝对列宽下限(px) */
  minColWidthPx?: number
  /**
   * 可用宽度(px,通常取预览窗口宽度)。列按它铺开:列数 ≤ fitMaxCols 时铺满该宽度,
   * 超过则每列取 fitWidthPx / fitMaxCols 宽、整体横向滚动。
   */
  fitWidthPx?: number
  /** 一屏目标最多铺几列(默认 10),用于 fitWidthPx 的分配 */
  fitMaxCols?: number
  /** 绝对行高下限(px) */
  minRowHeightPx?: number
  /**
   * 可用高度(px,通常取预览窗口高度)。行按它铺开:行数 ≤ fitMaxRows 时铺满该高度,
   * 超过则每行取 fitHeightPx / fitMaxRows 高、整体纵向滚动。
   */
  fitHeightPx?: number
  /** 一屏目标最多铺几行(默认 10),用于 fitHeightPx 的分配 */
  fitMaxRows?: number
  /**
   * 是否把单元格里的浮动图收进单元格渲染(object-fit:contain + overflow:hidden),
   * 避免图片跨列溢出。仅照片王(AI 问照片)预览开启;其他调用方不传 → 浮动图仍按
   * 原始 anchor 精确 px 绝对定位(行为不变)。
   */
  containImages?: boolean
}

interface DocumentPreviewProps {
  source: FileSource | null | undefined
  className?: string
  /**
   * xlsx 列宽策略。不传=保持文件原始列宽(默认行为,其他调用方不受影响)。
   * 仅影响 xlsx 表格渲染,对 docx/pdf/图片无效。详见 ColSizing。
   */
  colSizing?: ColSizing
  /** 用户点击「加入到会话」后回调（仅加 quote，不预填输入框） */
  onContentSelected?: (content: SelectedContent) => void
  /**
   * Quick action 被点击:把 quote 加进会话,根据 mode 决定如何下发 prompt:
   *   - mode='prefill'(默认): 预填 prompt 到输入框,等用户编辑后手动按发送
   *   - mode='send-now':     直接发送(命令式操作如插入列/删除/清除),不再过输入框
   */
  onQuickAction?: (
    content: SelectedContent,
    prefilledPrompt: string,
    mode?: 'prefill' | 'send-now'
  ) => void
  /**
   * 触发"快捷编辑"弹窗(quick-edit-modal):
   *   - 双击单元格(不带 mode,由 cell 自身 image/text 状态推断)
   *   - 浮窗"添加图片/换图/添加文字/修改"点击(带 mode 强制指定弹窗形态)
   */
  onCellQuickEdit?: (
    content: SelectedContent,
    mode?: 'image' | 'image-batch' | 'text',
    /** 锚点 rect:用于把弹层 popover 定位到被双击的 cell 附近(可选) */
    anchor?: { left: number; top: number; right: number; bottom: number }
  ) => void
  /** 整体缩放（默认 0.7） */
  zoom?: number
  fallback?: React.ReactNode
  onLoadingStateChange?: (state: {
    busy: boolean
    phase: 'idle' | 'downloading' | 'rendering'
    progress: number | null
  }) => void
  /**
   * 编辑中蒙版:为 true 时整层覆盖一层半透明遮罩,阻止用户在预览区上交互
   * (避免 AI 还在改、用户已经在选区/双击,触发的 prompt 基于的是过期内容).
   * 由父组件根据 docActionState.phase === 'editing' 控制.
   *
   * 蒙版有两态:
   *   - editing=true: 文案"AI 正在编辑文档…",无关闭按钮(还在跑 tool,不能 dismiss)
   *   - syncing=true: 文案"正在同步最新文档…",右侧带"知道了"按钮 — 后台下载
   *     无法真的中止(浏览器 fetch 已经触发,abort 也救不回带宽),所以按钮只是
   *     dismiss 蒙版让用户跳出,点击后由父组件用 message toast 告知用户:
   *     "下载完成会自动刷新,建议等新版就绪再做下一轮修改".
   *
   * editing / syncing 同时为 true 时 editing 优先(AI 还没真正写完,先报"编辑中").
   */
  editing?: boolean
  syncing?: boolean
  onDismissSync?: () => void
}

// ─── Quick actions 定义（预览组件是意图识别器，帮用户表达要填什么） ──
// prompt 生成函数接收当前选区 content，用 rangePayload 的 headers 自动拼结构化模板
/** 选区里是否包含图片 cell — 用于"查看原图" action 的 isVisible 判断 */
function findImageSrcInContent(content: SelectedContent): string | null {
  // 非表格图片选区:直接拿 imageSrcs[0]
  if (content.imageSrcs && content.imageSrcs[0]) return content.imageSrcs[0]
  const p = content.rangePayload
  if (!p) return null
  for (const row of p.cells) {
    for (const cell of row) {
      if (cell.imageSrc) return cell.imageSrc
    }
  }
  return null
}

/** 选区是否覆盖到表头行（buildPrompt / 可见性判断都要用） */
function selectionIncludesHeader(p: SelectedContent['rangePayload']): boolean {
  if (!p || p.headerRowR == null) return false
  const m = p.range.match(/^[A-Z]+(\d+)/)
  const minR = m ? parseInt(m[1], 10) : 0
  return minR > 0 && minR <= p.headerRowR
}

/**
 * 给容器内一张「非表格 img」算 ViewPath: body/p[N]/img[M]
 *   - N = 该图所在段落在 <w:body> 顶层 <w:p> 里的 1-based 序号(跳过 tables)
 *   - M = 该图在该段所有 <img> 里的 1-based 序号
 *
 * docx-preview 把 OOXML <w:body> 渲染为 <section.aiClerk-docx > article>,
 * 顶层 <w:p> 渲染为 <article> 的直接 <p> 子节点(<table> 不算 p,会被跳过)。
 * 多 page 文档会有多个 <section>,这里把所有 <article> 的顶层 <p> 拼成一个
 * 列表,按 DOM 顺序数,跟 OOXML body 段落顺序对齐。
 *
 * 找不到时返回空字符串,调用方按 imageViewPaths[i] 是否有值决定是否带路径。
 */
function getNonTableImgViewPath(
  container: HTMLElement,
  target: HTMLImageElement
): string {
  // 1) 找到 target 所属的"顶层 <p>"(article 的直接子节点)
  let pAncestor: HTMLElement | null = null
  let cur: HTMLElement | null = target.parentElement
  while (cur && container.contains(cur)) {
    if (cur.tagName === 'P' && cur.parentElement?.tagName === 'ARTICLE') {
      pAncestor = cur
      break
    }
    cur = cur.parentElement
  }
  if (!pAncestor) return ''

  // 2) p[N]: 跨 article 收集所有顶层 <p>,找 pAncestor 的位置
  const allTopLevelPs: HTMLElement[] = []
  container.querySelectorAll<HTMLElement>('article').forEach((article) => {
    article.querySelectorAll<HTMLElement>(':scope > p').forEach((p) => {
      allTopLevelPs.push(p)
    })
  })
  const pIdx = allTopLevelPs.indexOf(pAncestor) + 1
  if (pIdx === 0) return ''

  // 3) img[M]: target 在 pAncestor 内全部 <img> 的位置
  const imgsInP = Array.from(
    pAncestor.querySelectorAll<HTMLImageElement>('img')
  )
  const imgIdx = imgsInP.indexOf(target) + 1
  if (imgIdx === 0) return ''

  return `body/p[${pIdx}]/img[${imgIdx}]`
}

// AI 帮填 prompt 生成器:浮窗主按钮(SelectionMenu primary)点击时调用
// 主按钮承担"AI 帮填"语义,不再是"加入到会话"的纯 quote 行为
//
// 按 selectionShape + cellState 分 5 种场景:
//   - 空 cell:    「填 A3 这一格。要求：」
//   - 图片 cell:  「改 A3 这一格（当前是图片，可替换）。要求：」
//   - 文字 cell:  「改 A3 这一格，要求：」
//   - 整列:       「填 C 列 这一列。要求：」
//   - 整行:       「填第 5 行。要求：」
//   - 其他(多 cell 拖选): 保留原有按表头/已填数据的智能分支
function buildAutoFillPrompt(content: SelectedContent): string {
  // 非表格图片选区:AI 帮填语义 = "基于这张/这些图给出建议或描述"。
  // ViewPath 在 prompt 里点明定位(body/p[N]/img[M]),agent 后端按这个路径
  // 在 docx XML tree 里取节点;src URL 不进 prompt(太长),走 quote 块。
  if (content.source === 'image-selection') {
    const paths = (content.imageViewPaths || []).filter(Boolean)
    const n = content.imageSrcs?.length ?? 0
    if (n > 1 && paths.length === n)
      return `基于文档里以下 ${n} 张图片(${paths.join(', ')})。要求：`
    if (n > 1) return `基于文档里这 ${n} 张图片。要求：`
    if (paths.length === 1) return `基于文档里图片 ${paths[0]}。要求：`
    return `基于文档里这张图片。要求：`
  }
  const p = content.rangePayload
  if (!p) return '填这一格。要求：'

  // 1) 整列(点列号触发)
  if (content.selectionShape === 'whole-col') {
    const col = colLetterFromContent(content)
    return `填 ${col} 列 这一列。要求：`
  }

  // 2) 整行(点行号触发)
  if (content.selectionShape === 'whole-row') {
    const row = rowNumberFromContent(content)
    return `填第 ${row} 行。要求：`
  }

  // 3) 单 cell(按 cellState 区分空/图/文)
  if (content.source === 'cell-click' && content.cellState) {
    const ref = singleCellRef(content)
    if (content.cellState === 'empty') return `填 ${ref} 这一格。要求：`
    if (content.cellState === 'image')
      return `改 ${ref} 这一格（当前是图片，可替换）。要求：`
    if (content.cellState === 'text') return `改 ${ref} 这一格，要求：`
  }

  // 3.5) 多 cell 区域 + 同质化 cellState(all-image/all-text):走更精确的语义
  if (content.source === 'cell-range' && content.cellState) {
    const range = p.range
    if (content.cellState === 'all-image')
      return `改 ${range} 区域的图片。要求：`
    if (content.cellState === 'all-text')
      return `改 ${range} 区域的文字。要求：`
    // all-empty / mixed 继续走下面"按表头/已填"的智能分支
  }

  // 4) 多 cell 区域(拖选):保留原"按表头/已填"的智能分支
  // 跨列合并块会让每个被覆盖列都返回 master 值,Set 去重
  const TAIL = '。补充要求：'
  const names = Array.from(new Set(Object.values(p.headers).filter(Boolean)))
  const includesHeader = selectionIncludesHeader(p)
  const anyColHasData = Object.values(p.columnHasData || {}).some(Boolean)
  const verb = anyColHasData ? '帮我填（修改）' : '帮我填'

  if (includesHeader) {
    const quantifier = (names.length || p.cols) > 1 ? '这些列' : '这一列'
    if (names.length > 0) {
      return `${verb}${quantifier}：${names.join('、')}${TAIL}`
    }
    return `${verb}${quantifier}${TAIL}`
  }

  const totalCells = p.cells.reduce((n, row) => n + row.length, 0)
  const allEmpty = totalCells > 0 && p.emptyRefs.length === totalCells
  if (allEmpty) {
    // 量词:整列才说"这一列/这些列",否则用具体 range 范围(避免用户
    // 选两格被说成"这一列")。整列由 whole-col selectionShape 早就走专门
    // 分支,所以走到这里的多 cell 拖选都用 range 表达更准确.
    if (names.length > 0) {
      return `${verb} ${p.range} 区域(${names.join('、')})。数据来源是：`
    }
    return `${verb} ${p.range} 区域。数据来源是：`
  }

  if (names.length > 0) {
    return `${verb} ${p.range}（${names.join('、')}）${TAIL}`
  }
  return `${verb} ${p.range}${TAIL}`
}

/** 合并块上下文:用于 prompt enrich,让 AI 知道选区跨到的合并单元格实际内容 */
interface MergeBlock {
  range: string // 合并块 ref,如 "A1:G1"
  value: string // master cell 的纯文本内容
}

/** 把 ExcelJS cell.value 转成可读纯文本(供 prompt 用,不含 HTML) */
function mergeMasterText(cell: ExcelJS.Cell): string {
  const v: any = cell.value
  if (v == null) return ''
  if (typeof v === 'object') {
    // 富文本
    if (Array.isArray(v.richText)) {
      return v.richText
        .map((rt: any) => String(rt.text || ''))
        .join('')
        .trim()
    }
    // 公式取计算结果
    if ('result' in v) return String(v.result ?? '').trim()
    // 超链接
    if ('text' in v && 'hyperlink' in v) {
      return String((v as any).text ?? '').trim()
    }
  }
  return String(v).trim()
}

/**
 * 找出选区跨到的所有合并块,返回 [{range, master 内容}]
 * 用于解决"D 列被选时,D1 实际是 A1:G1 合并的一部分,内容在 A1,AI 看不到"的问题
 */
function collectMergedBlocksForContent(
  content: SelectedContent,
  workbook: ExcelJS.Workbook | null,
  sheetIdx: number
): MergeBlock[] {
  if (!workbook || !content.rangePayload) return []
  if (content.docType !== 'xlsx') return []
  const ws = workbook.worksheets[sheetIdx]
  if (!ws) return []
  const mergesRaw = (ws as any).model?.merges as string[] | undefined
  if (!Array.isArray(mergesRaw) || mergesRaw.length === 0) return []

  const rangeRef = content.rangePayload.range
  const [startRef, endRef] = rangeRef.includes(':')
    ? rangeRef.split(':')
    : [rangeRef, rangeRef]
  const sR = parseInt(startRef.match(/\d+/)?.[0] || '0', 10)
  const sC = colLetterToNum(startRef.match(/[A-Z]+/)?.[0] || '')
  const eR = parseInt(endRef.match(/\d+/)?.[0] || '0', 10)
  const eC = colLetterToNum(endRef.match(/[A-Z]+/)?.[0] || '')
  if (!sR || !sC || !eR || !eC) return []
  const selMinR = Math.min(sR, eR)
  const selMaxR = Math.max(sR, eR)
  const selMinC = Math.min(sC, eC)
  const selMaxC = Math.max(sC, eC)

  const result: MergeBlock[] = []
  for (const mergeRef of mergesRaw) {
    const [s, e] = mergeRef.split(':')
    if (!s || !e) continue
    const msR = parseInt(s.match(/\d+/)?.[0] || '0', 10)
    const msC = colLetterToNum(s.match(/[A-Z]+/)?.[0] || '')
    const meR = parseInt(e.match(/\d+/)?.[0] || '0', 10)
    const meC = colLetterToNum(e.match(/[A-Z]+/)?.[0] || '')
    if (!msR || !msC) continue
    // 合并块与选区有交集吗?
    const overlap =
      msR <= selMaxR && meR >= selMinR && msC <= selMaxC && meC >= selMinC
    if (!overlap) continue
    const master = ws.getCell(msR, msC)
    const value = mergeMasterText(master)
    if (!value) continue
    result.push({ range: mergeRef, value })
  }
  return result
}

/** 把合并块上下文 append 到 prompt 末尾 */
function appendMergedContextToPrompt(
  prompt: string,
  blocks: MergeBlock[]
): string {
  if (blocks.length === 0) return prompt
  const lines = blocks.map((b) => `- ${b.range}: "${b.value}"`).join('\n')
  return `${prompt}\n\n(选区涉及以下合并单元格,内容供参考)\n${lines}`
}

/** 从 SelectedContent.cellRef 提取列字母(用于整列 prompt) */
function colLetterFromContent(content: SelectedContent): string {
  const m = (content.cellRef || '').match(/^([A-Z]+)\d+/)
  return m ? m[1] : '当前列'
}

/** 从 SelectedContent.cellRef 提取行号(用于整行 prompt) */
function rowNumberFromContent(content: SelectedContent): string {
  const m = (content.cellRef || '').match(/^[A-Z]+(\d+)/)
  return m ? m[1] : '当前行'
}

/** 单 cell ref(整列/整行 prompt 中也复用 cellRef 中第一个 cell 的 ref) */
function singleCellRef(content: SelectedContent): string {
  const ref = content.cellRef || ''
  // "A1:A100" → "A1"
  return ref.split(':')[0] || ref || '当前单元格'
}

/**
 * Quick action 执行类型,由 handleActionClick 路由:
 *   - 'prompt'                 - 默认,buildPrompt → onQuickAction(prefill 模式,预填到输入框)
 *   - 'send-prompt'            - buildPrompt → onQuickAction(send-now,直接发送)
 *   - 'confirm-send'           - 弹二次确认 → onQuickAction(send-now,直接发送)
 *   - 'preview-image'          - 前端弹 ImagePreview,不发 AI
 *   - 'quick-edit-image'       - 触发 quick-edit-modal 图片模式(单格)
 *   - 'quick-edit-image-batch' - 触发 quick-edit-modal 批量图片模式(多格)
 *   - 'quick-edit-text'        - 触发 quick-edit-modal 文字模式
 */
type QuickActionKind =
  | 'prompt'
  | 'send-prompt'
  | 'confirm-send'
  | 'preview-image'
  | 'quick-edit-image'
  | 'quick-edit-image-batch'
  | 'quick-edit-text'

type QuickActionEntry = QuickAction & {
  buildPrompt: (content: SelectedContent) => string
  /** 命中条件;不传则永远显示 */
  isVisible?: (content: SelectedContent) => boolean
  /** 执行类型,默认 'prompt' */
  kind?: QuickActionKind
  /** 破坏性操作:浮窗按钮 hover 才变红;通常配合 kind='confirm-send' */
  danger?: boolean
  /** 二次确认弹窗标题(kind='confirm-send' 用) */
  confirmTitle?: (content: SelectedContent) => string
  /** 二次确认弹窗内容(kind='confirm-send' 用) */
  confirmContent?: (content: SelectedContent) => string
}

const isCellShape = (c: SelectedContent) =>
  (c.selectionShape ?? 'cell') === 'cell' && c.source !== 'cell-range'
const isEmptyCell = (c: SelectedContent) =>
  isCellShape(c) && c.cellState === 'empty'
const isImageCell = (c: SelectedContent) =>
  isCellShape(c) && c.cellState === 'image'
const isTextCell = (c: SelectedContent) =>
  isCellShape(c) && c.cellState === 'text'
const isWholeCol = (c: SelectedContent) => c.selectionShape === 'whole-col'
const isWholeRow = (c: SelectedContent) => c.selectionShape === 'whole-row'

// 多 cell 选区(拖选 ≥ 2 个 cell):按 cellState 区分 action 可见性
const isMultiCellRange = (c: SelectedContent) =>
  (c.selectionShape ?? 'cell') === 'cell' && c.source === 'cell-range'
const isMultiEmpty = (c: SelectedContent) =>
  isMultiCellRange(c) && c.cellState === 'all-empty'
const isMultiImage = (c: SelectedContent) =>
  isMultiCellRange(c) && c.cellState === 'all-image'
const isMultiText = (c: SelectedContent) =>
  isMultiCellRange(c) && c.cellState === 'all-text'

// 纯文字选区(docx 段落 / xlsx cell 内 selection),没有 rangePayload
const isTextSelection = (c: SelectedContent) => c.source === 'text-selection'

// 非表格图片选区(docx 段落内 <img> 点击),没有 rangePayload
const isImageSelection = (c: SelectedContent) => c.source === 'image-selection'

// 选中文字过长时截断展示用
const truncate = (s: string, n: number) =>
  s.length > n ? `${s.slice(0, n)}…` : s

const QUICK_ACTIONS: Array<QuickActionEntry> = [
  // ─── 空单元格 ─────────────────────────────────────────────
  {
    id: 'add-image',
    icon: <PictureOutlined />,
    label: '添加图片',
    prompt: '',
    isVisible: isEmptyCell,
    kind: 'quick-edit-image',
    buildPrompt: () => ''
  },
  {
    id: 'add-text',
    icon: <EditOutlined />,
    label: '添加文字',
    prompt: '',
    isVisible: isEmptyCell,
    kind: 'quick-edit-text',
    buildPrompt: () => ''
  },

  // ─── 图片单元格 ──────────────────────────────────────────
  {
    id: 'replace-image',
    icon: <SyncOutlined />,
    label: '换图',
    prompt: '',
    isVisible: isImageCell,
    kind: 'quick-edit-image',
    buildPrompt: () => ''
  },
  {
    // 纯前端预览,不发 prompt 给 AI。仅单格 image cell 用 — 多 cell 选区里
    // 即便都是图片也不显示"查看原图"(用户多选场景已经不在意单张原图了)。
    id: 'preview-image',
    icon: <EyeOutlined />,
    label: '查看原图',
    prompt: '',
    isVisible: isImageCell,
    kind: 'preview-image',
    buildPrompt: () => ''
  },
  {
    id: 'delete-cell-image',
    icon: <DeleteOutlined />,
    label: '删除',
    prompt: '',
    isVisible: isImageCell,
    kind: 'confirm-send',
    danger: true,
    confirmTitle: (c) => `确认删除 ${singleCellRef(c)} 这一格的图片?`,
    confirmContent: () => '该单元格的图片将被清空,确认后会直接发送给 AI 执行',
    buildPrompt: (c) => `删除 ${singleCellRef(c)} 这一格的图片`
  },

  // ─── 文字单元格 ──────────────────────────────────────────
  {
    id: 'modify-text',
    icon: <EditOutlined />,
    label: '修改',
    prompt: '',
    isVisible: isTextCell,
    kind: 'quick-edit-text',
    buildPrompt: () => ''
  },
  {
    id: 'delete-cell-text',
    icon: <DeleteOutlined />,
    label: '删除',
    prompt: '',
    isVisible: isTextCell,
    kind: 'confirm-send',
    danger: true,
    confirmTitle: (c) => `确认删除 ${singleCellRef(c)} 这一格的文字?`,
    confirmContent: () => '该单元格的文字将被清空,确认后会直接发送给 AI 执行',
    buildPrompt: (c) => `删除 ${singleCellRef(c)} 这一格的文字`
  },

  // ─── 整列(点列号 A/B/C 触发) ──────────────────────────
  {
    id: 'insert-col-left',
    icon: <VerticalRightOutlined />,
    label: '左侧插入列',
    prompt: '',
    isVisible: isWholeCol,
    kind: 'send-prompt',
    buildPrompt: (c) => `在 ${colLetterFromContent(c)} 列左侧插入一个空列`
  },
  {
    id: 'insert-col-right',
    icon: <VerticalLeftOutlined />,
    label: '右侧插入列',
    prompt: '',
    isVisible: isWholeCol,
    kind: 'send-prompt',
    buildPrompt: (c) => `在 ${colLetterFromContent(c)} 列右侧插入一个空列`
  },
  {
    id: 'clear-col',
    icon: <RestOutlined />,
    label: '清除内容',
    prompt: '',
    isVisible: isWholeCol,
    kind: 'confirm-send',
    confirmTitle: (c) => `确认清除 ${colLetterFromContent(c)} 列内容?`,
    confirmContent: () =>
      '保留列结构,仅清空所有 cell 数据。确认后直接发送给 AI 执行',
    buildPrompt: (c) =>
      `清除 ${colLetterFromContent(c)} 列的所有内容（保留列结构）`
  },
  {
    id: 'delete-col',
    icon: <DeleteOutlined />,
    label: '删除列',
    prompt: '',
    isVisible: isWholeCol,
    kind: 'confirm-send',
    danger: true,
    confirmTitle: (c) => {
      const col = colLetterFromContent(c)
      const headerName = c.rangePayload?.headers?.[col] || ''
      return headerName
        ? `确认删除 ${col} 列「${headerName}」?`
        : `确认删除 ${col} 列?`
    },
    confirmContent: () =>
      '将移除整列(包含所有 cell 数据 + 列结构),后续列号会左移。确认后直接发送给 AI',
    buildPrompt: (c) => `删除 ${colLetterFromContent(c)} 列 整列`
  },

  // ─── 整行(点行号 1/2/3 触发) ──────────────────────────
  {
    id: 'insert-row-above',
    icon: <ArrowUpOutlined />,
    label: '上方插入行',
    prompt: '',
    isVisible: isWholeRow,
    kind: 'send-prompt',
    buildPrompt: (c) => `在第 ${rowNumberFromContent(c)} 行上方插入一个空行`
  },
  {
    id: 'insert-row-below',
    icon: <ArrowDownOutlined />,
    label: '下方插入行',
    prompt: '',
    isVisible: isWholeRow,
    kind: 'send-prompt',
    buildPrompt: (c) => `在第 ${rowNumberFromContent(c)} 行下方插入一个空行`
  },
  {
    id: 'clear-row',
    icon: <RestOutlined />,
    label: '清除内容',
    prompt: '',
    isVisible: isWholeRow,
    kind: 'confirm-send',
    confirmTitle: (c) => `确认清除第 ${rowNumberFromContent(c)} 行内容?`,
    confirmContent: () =>
      '保留行结构,仅清空所有 cell 数据。确认后直接发送给 AI 执行',
    buildPrompt: (c) =>
      `清除第 ${rowNumberFromContent(c)} 行的所有内容（保留行结构）`
  },
  {
    id: 'delete-row',
    icon: <DeleteOutlined />,
    label: '删除行',
    prompt: '',
    isVisible: isWholeRow,
    kind: 'confirm-send',
    danger: true,
    confirmTitle: (c) => `确认删除第 ${rowNumberFromContent(c)} 行?`,
    confirmContent: () =>
      '将移除整行(包含所有 cell 数据 + 行结构),后续行号会上移。确认后直接发送给 AI',
    buildPrompt: (c) => `删除第 ${rowNumberFromContent(c)} 行`
  },

  // ─── 多 cell 区域 - 全空 ──────────────────────────────
  // "AI 帮填" 主按钮由 SelectionMenu primary 承担,这里只做差异化辅助 action
  {
    id: 'add-images-batch',
    icon: <PictureOutlined />,
    label: '添加图片',
    prompt: '',
    isVisible: isMultiEmpty,
    kind: 'quick-edit-image-batch',
    buildPrompt: () => ''
  },
  {
    id: 'add-text-batch',
    icon: <EditOutlined />,
    label: '添加文字',
    prompt: '',
    isVisible: isMultiEmpty,
    kind: 'quick-edit-text',
    buildPrompt: () => ''
  },

  // ─── 多 cell 区域 - 全图 ──────────────────────────────
  {
    id: 'replace-images-batch',
    icon: <SyncOutlined />,
    label: '换图',
    prompt: '',
    isVisible: isMultiImage,
    kind: 'quick-edit-image-batch',
    buildPrompt: () => ''
  },
  {
    id: 'delete-multi-image',
    icon: <DeleteOutlined />,
    label: '删除',
    prompt: '',
    isVisible: isMultiImage,
    kind: 'send-prompt',
    danger: true,
    buildPrompt: (c) =>
      `删除 ${c.rangePayload?.range || c.cellRef} 区域的所有图片`
  },

  // ─── 多 cell 区域 - 全文 ──────────────────────────────
  {
    id: 'delete-multi-text',
    icon: <DeleteOutlined />,
    label: '删除',
    prompt: '',
    isVisible: isMultiText,
    kind: 'send-prompt',
    danger: true,
    buildPrompt: (c) =>
      `删除 ${c.rangePayload?.range || c.cellRef} 区域的所有文字`
  },

  // ─── 纯文字选区(docx 段落 / xlsx cell 内文字 selection) ────────
  // "AI 帮填" 由 SelectionMenu primary 承担,这里加修改 / 删除
  {
    id: 'modify-text-selection',
    icon: <EditOutlined />,
    label: '修改',
    prompt: '',
    isVisible: isTextSelection,
    kind: 'quick-edit-text',
    buildPrompt: () => ''
  },
  {
    id: 'delete-text-selection',
    icon: <DeleteOutlined />,
    label: '删除',
    prompt: '',
    isVisible: isTextSelection,
    kind: 'confirm-send',
    danger: true,
    confirmTitle: () => '确认删除选中的文字?',
    confirmContent: (c) => `将从文档中删除选中内容: "${truncate(c.text, 40)}"`,
    buildPrompt: (c) => `把文档中的这段内容删掉: "${c.text}"`
  },

  // ─── 非表格图片选区(docx 段落里的 <img>) ────────────────────
  {
    id: 'replace-image-selection',
    icon: <SyncOutlined />,
    label: '换图',
    prompt: '',
    isVisible: isImageSelection,
    kind: 'quick-edit-image',
    buildPrompt: () => ''
  },
  {
    id: 'preview-image-selection',
    icon: <EyeOutlined />,
    label: '查看原图',
    prompt: '',
    isVisible: isImageSelection,
    kind: 'preview-image',
    buildPrompt: () => ''
  },
  {
    id: 'delete-image-selection',
    icon: <DeleteOutlined />,
    label: '删除',
    prompt: '',
    isVisible: isImageSelection,
    kind: 'confirm-send',
    danger: true,
    confirmTitle: (c) => {
      const n = c.imageSrcs?.length || 0
      return n > 1 ? `确认删除选中的 ${n} 张图片?` : '确认删除选中的图片?'
    },
    confirmContent: (c) => {
      const n = c.imageSrcs?.length || 0
      return n > 1
        ? `将从文档中删除这 ${n} 张图片,确认后会直接发送给 AI 执行`
        : '将从文档中删除这张图片,确认后会直接发送给 AI 执行'
    },
    buildPrompt: (c) => {
      // src 走 quote 块,prompt 用 ViewPath 定位
      const paths = (c.imageViewPaths || []).filter(Boolean)
      const n = c.imageSrcs?.length || 0
      if (n > 1 && paths.length === n)
        return `把文档里以下 ${n} 张图片删掉(${paths.join(', ')})`
      if (n > 1) return `把文档里这 ${n} 张图片删掉`
      if (paths.length === 1) return `把文档里图片 ${paths[0]} 删掉`
      return '把文档里这张图片删掉'
    }
  }
]

interface PendingSelection {
  content: SelectedContent
  /** 选区 viewport rect:antd Popover 的锚点,arrow 用 arrowPointAtCenter 自动对齐 */
  rect: { left: number; top: number; right: number; bottom: number }
  /**
   * 浮窗摆放方位:在父组件基于"预览容器"边界算出来的(不是视口)。
   * antd 自带 autoAdjustOverflow 只考虑视口,容器有内部滚动/zoom 时会跑出容器;
   * 这里前置选好方位,Popover 关掉 autoAdjustOverflow 直接用这个 placement。
   */
  placement: 'top' | 'right' | 'bottom' | 'left'
}

interface CellPos {
  r: number // 1-indexed
  c: number // 1-indexed
  /** docx 下区分多个 table，xlsx 留空 */
  tableId?: string
}
interface CellRange {
  anchor: CellPos
  focus: CellPos
}

/**
 * 把原始 [anchor, focus] 范围按 DOM 里真实的合并单元格（rowspan/colspan）
 * 扩张到"整个合并块整体进/出选区"的边界。
 *
 * 迭代扩张：每轮扫描 tds，凡是合并块的 bbox 与当前范围有交集，就把范围扩展到
 * 包含该块的完整 bbox。若本轮有扩展，则再扫一轮（可能触发链式扩张：新扩进来
 * 的区域又带进来别的合并块）。直到稳定为止。
 */
function expandRangeByMergedCells(
  range: CellRange,
  tds: ArrayLike<HTMLElement>,
  opts: { lockColumns?: boolean; lockRows?: boolean } = {}
): { minR: number; maxR: number; minC: number; maxC: number } {
  let minR = Math.min(range.anchor.r, range.focus.r)
  let maxR = Math.max(range.anchor.r, range.focus.r)
  let minC = Math.min(range.anchor.c, range.focus.c)
  let maxC = Math.max(range.anchor.c, range.focus.c)
  // lockColumns: 整列选区(点列号触发) — C 范围必须保持,只允许 R 扩张
  // lockRows:    整行选区(点行号触发) — R 范围必须保持,只允许 C 扩张
  //
  // 解决场景:第一行/列是跨列/跨行合并(如"标题行 A:G 合并"),原扩张会把
  // 单列/单行选区误扩到整表。锁定后保留用户意图(只选这一列/一行)
  const lockColumns = !!opts.lockColumns
  const lockRows = !!opts.lockRows
  const list: HTMLElement[] = Array.from(tds)
  for (let iter = 0; iter < 16; iter++) {
    let changed = false
    for (const td of list) {
      const rs = parseInt(td.getAttribute('rowspan') || '1', 10)
      const cs = parseInt(td.getAttribute('colspan') || '1', 10)
      if (rs === 1 && cs === 1) continue
      const r = parseInt(td.dataset.row || '0', 10)
      const c = parseInt(td.dataset.col || '0', 10)
      if (!r || !c) continue
      const tdMaxR = r + rs - 1
      const tdMaxC = c + cs - 1
      // 与当前范围有交集？
      const overlap = tdMaxR >= minR && r <= maxR && tdMaxC >= minC && c <= maxC
      if (!overlap) continue
      if (!lockRows) {
        if (r < minR) {
          minR = r
          changed = true
        }
        if (tdMaxR > maxR) {
          maxR = tdMaxR
          changed = true
        }
      }
      if (!lockColumns) {
        if (c < minC) {
          minC = c
          changed = true
        }
        if (tdMaxC > maxC) {
          maxC = tdMaxC
          changed = true
        }
      }
    }
    if (!changed) break
  }
  return { minR, maxR, minC, maxC }
}

// localStorage key：用户是否已经发现"拖选 → AI"流程
// 目的是给新用户在文档区底部展示一条引导提示，看过 / 用过一次后自动消失
const DOC_HINT_LS_KEY = 'aiClerk:docHint:seen'

function readDocHintSeen(): boolean {
  try {
    return window.localStorage.getItem(DOC_HINT_LS_KEY) === '1'
  } catch {
    // 隐私模式 / SSR 兜底：默认当作已看过，不强行打扰
    return true
  }
}

function markDocHintSeen(): void {
  try {
    window.localStorage.setItem(DOC_HINT_LS_KEY, '1')
  } catch {
    /* noop */
  }
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  source,
  className,
  colSizing,
  onContentSelected,
  onQuickAction,
  onCellQuickEdit,
  zoom = 0.7,
  fallback,
  onLoadingStateChange,
  editing = false,
  syncing = false,
  onDismissSync
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  // 直接 fetch OSS URL（依赖 OSS 已开启 CORS）
  const { buffer, loading, error, loadedSourceKey, progress } =
    useFileLoader(source)
  const [renderError, setRenderError] = useState<Error | null>(null)
  const [excelHtml, setExcelHtml] = useState<string>('')
  /** docx 第一页的"自然宽度"px (已除掉当前 zoom),供 ResizeObserver 复算 fit zoom.
   *  渲染完一次性测量,后续 resize 重算 fit 时复用,不依赖再次 DOM 测量. */
  const naturalDocxWidthPxRef = useRef<number | null>(null)
  /** docx 撑满预览栏后实际生效的 zoom; null 表示不需要撑满 (页面已 >= 预览栏)
   *  或 docx 还未渲染. xlsx / 无 docx 时不读这个值. */
  const [docxFitZoom, setDocxFitZoom] = useState<number | null>(null)
  const [pending, setPending] = useState<PendingSelection | null>(null)
  // "查看原图" 弹层用的 src;只在选中图片 cell + 点 preview-image action 时设置
  const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)
  const [renderedPreviewKind, setRenderedPreviewKind] = useState<
    'docx' | 'xlsx' | null
  >(null)
  // 文档区底部的"拖选 → AI"引导提示是否显示。
  // lazy initializer 只读一次 localStorage，老用户进来直接 false 不打扰。
  const [showDocHint, setShowDocHint] = useState<boolean>(
    () => !readDocHintSeen()
  )

  // xlsx 选区（拖拽框选）
  const [cellRange, setCellRange] = useState<CellRange | null>(null)
  const cellRangeRef = useRef<CellRange | null>(null)
  // xlsx 工作簿（解析后保留给 payload 构造用）
  const workbookRef = useRef<ExcelJS.Workbook | null>(null)
  // WPS DISPIMG 内嵌图片表 (imageId → dataURL),buildRangePayload 需要它把
  // DISPIMG cell 识别成 image 类型——否则双击/选中 DISPIMG cell 会误以为是
  // 文本 cell,弹出修改文本弹窗,且不出"换图/查看原图/删除"action
  const cellImagesRef = useRef<CellImagesMap>(new Map())
  // 当前活动 sheet idx（后续多 sheet 切换用，先默认 0）
  const activeSheetIdxRef = useRef(0)
  // 拖拽状态：区分单击 vs 拖拽，避免文字选中/点击菜单冲突
  const dragRef = useRef<{ dragging: boolean; moved: boolean }>({
    dragging: false,
    moved: false
  })
  // 非表格图片拖选(docx 段落里的 inline <img>):记录 mousedown 起点坐标 +
  // 第一张图,mousemove 时算 rubber-band rect 跟所有非表格 img 的 bbox 求交,
  // 把命中的 src 放进 pending.imageSrcs 多选高亮 + 多图 action。
  const imageDragRef = useRef<{
    active: boolean
    startX: number
    startY: number
  }>({ active: false, startX: 0, startY: 0 })
  const lastPointerSingleCellContentRef = useRef<SelectedContent | null>(null)
  const lastSingleCellContentRef = useRef<SelectedContent | null>(null)
  // 拖到容器边缘时自动滚动：记录最后鼠标位置 + 当前速度 + RAF 句柄
  const lastMouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const scrollVelRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const scrollRafRef = useRef<number | null>(null)

  const docType = source ? detectDocType(source) : 'unknown'
  const currentSourceKey = getFileSourceKey(source)
  const keepPreviousPreview =
    loading &&
    !!source &&
    renderedPreviewKind === docType &&
    (docType === 'docx' || docType === 'xlsx')

  // source URL 的"主体路径"(剥掉 ?t=nonce 等 query):用于区分
  //   - 真的换了文件 (URL 主体不同) → 重置选区 / pending
  //   - 同文件刷新 (work/index.tsx 通过 refreshNonce 触发预览重拉) → 保留选区
  // 不能直接用 currentSourceKey 比较 —— getFileSourceKey 对 string source
  // 返回完整 URL,nonce 变化也算"换 source",会误清选区。
  const sourceUrlBase = useMemo<string | null>(() => {
    if (!source) return null
    if (typeof source !== 'string') return source.name
    return source.split('?')[0]
  }, [source])
  const prevSourceUrlBaseRef = useRef<string | null>(null)

  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange({
        busy: loading || rendering,
        phase: loading ? 'downloading' : rendering ? 'rendering' : 'idle',
        progress: loading ? progress : null
      })
    }
  }, [loading, rendering, progress, onLoadingStateChange])

  // 用户第一次拖出选区 → 视为已学会"拖选 → AI"流程，永久隐藏底部引导
  useEffect(() => {
    if (!showDocHint || !cellRange) return
    setShowDocHint(false)
    markDocHintSeen()
  }, [cellRange, showDocHint])

  // ─── docx 撑满预览栏:容器宽度变化时重算 fit zoom ────────────────────
  // 触发场景:窗口 resize, 右侧聊天栏宽度变 (虽然现在固定 460px), devtools 开/关.
  // 只在 docx + 已渲染完成 + 拿到 naturalWidth 后启动 observer;xlsx 不参与.
  useEffect(() => {
    if (renderedPreviewKind !== 'docx') return
    const container = containerRef.current
    if (!container) return
    const ro = new ResizeObserver(() => {
      const natural = naturalDocxWidthPxRef.current
      if (!natural) return
      const containerWidth = container.clientWidth - DOCX_FIT_SAFE_MARGIN_PX
      if (containerWidth <= 0) return
      const newFitZoom = Math.max(zoom, containerWidth / natural)
      // 跟 baseZoom 相等说明页面已经比预览栏宽,fit 不需要,用 null 退回外部 zoom.
      // 用 setState 函数式比较避免每次都触发 re-render.
      setDocxFitZoom((prev) => {
        const next = newFitZoom <= zoom ? null : newFitZoom
        if (prev === next) return prev
        // 浮点漂移防抖:两次差异 < 0.5% 就当没变,避免观察自身引起的微小再触发循环.
        if (
          prev !== null &&
          next !== null &&
          Math.abs(prev - next) / prev < 0.005
        ) {
          return prev
        }
        return next
      })
    })
    ro.observe(container)
    return () => ro.disconnect()
  }, [renderedPreviewKind, zoom])

  // ─── 浮窗锚点 + 方位:都基于"预览容器"算 ────────────────────
  //
  // 为什么不直接用 antd Popover 的 autoAdjustOverflow:
  //   - autoAdjustOverflow 基于"视口"翻转,但预览容器有自己的内部滚动 + zoom,
  //     视口空间够 != 容器空间够 → 气泡会跑出容器(贴在 toolbar / 边栏上)
  //   - 在父组件前置算好方位,Popover 关掉 autoAdjustOverflow 直接用 → 气泡永远在容器内
  //
  // 锚点裁剪(关键):合并单元格 / 跨列宽 cell 可能 rect 超出容器可视区(横向滚动场景)。
  // 直接喂原 rect 给 antd:箭头按 cell 中心算,中心在容器外 → 气泡被推出可见区。
  // 解决:把 cell rect 跟容器 rect 求交集,用"可见部分"当锚点;
  // arrowPointAtCenter 就自动对齐到可见区中心,气泡留在容器内。
  //
  // placement 策略:优先 top → 顶部不够 right → 右侧不够 bottom → 都不够 left
  // 浮窗估算尺寸(随 quick action 多寡变化,用最大值兜底):宽 480 / 高 50
  const computeAnchor = useCallback(
    (rect: {
      left: number
      right: number
      top: number
      bottom: number
    }): {
      rect: { left: number; top: number; right: number; bottom: number }
      placement: 'top' | 'right' | 'bottom' | 'left'
    } => {
      const MENU_W = 480
      const MENU_H = 50
      const GAP = 10
      const EDGE = 8
      const container = containerRef.current
      const cr = container
        ? container.getBoundingClientRect()
        : {
            left: 0,
            right: window.innerWidth,
            top: 0,
            bottom: window.innerHeight
          }
      // 裁剪到容器可视区 — 合并单元格横/纵向超出时只保留可见部分
      const clipped = {
        left: Math.max(rect.left, cr.left),
        right: Math.min(rect.right, cr.right),
        top: Math.max(rect.top, cr.top),
        bottom: Math.min(rect.bottom, cr.bottom)
      }
      // 裁完成空 rect(选区完全在容器外):退化用容器中心 1×1 锚点兜底,
      // 至少气泡能显示在容器中心,而不是飞到外面
      if (clipped.right <= clipped.left || clipped.bottom <= clipped.top) {
        const cx = (cr.left + cr.right) / 2
        const cy = (cr.top + cr.bottom) / 2
        return {
          rect: { left: cx, right: cx + 1, top: cy, bottom: cy + 1 },
          placement: 'top'
        }
      }
      const spaceTop = clipped.top - cr.top
      const spaceBottom = cr.bottom - clipped.bottom
      const spaceRight = cr.right - clipped.right
      const spaceLeft = clipped.left - cr.left
      let placement: 'top' | 'right' | 'bottom' | 'left'
      if (spaceTop >= MENU_H + GAP + EDGE) placement = 'top'
      else if (spaceRight >= MENU_W + GAP + EDGE) placement = 'right'
      else if (spaceBottom >= MENU_H + GAP + EDGE) placement = 'bottom'
      else if (spaceLeft >= MENU_W + GAP + EDGE) placement = 'left'
      else {
        // 都装不下:选剩余空间最大的一边(宁可挤一点,也别飞出容器)
        const max = Math.max(spaceTop, spaceRight, spaceBottom, spaceLeft)
        if (max === spaceTop) placement = 'top'
        else if (max === spaceRight) placement = 'right'
        else if (max === spaceBottom) placement = 'bottom'
        else placement = 'left'
      }
      return { rect: clipped, placement }
    },
    []
  )

  // ─── 表格拖拽框选（xlsx 原生 + docx table）：从 <td> 解析 row/col 坐标
  // docx 非表格的纯段落文字选区也走 handleTableMouseUp，没命中 td 就走 text-selection 分支
  const getCellPosFromEvent = (
    e: React.MouseEvent | MouseEvent
  ): { td: HTMLElement; pos: CellPos } | null => {
    const container = containerRef.current
    const target = e.target as HTMLElement | null
    const clientX = 'clientX' in e ? e.clientX : 0
    const clientY = 'clientY' in e ? e.clientY : 0

    let td: HTMLElement | null = null

    // 1) 坐标 bbox 命中 — 最权威:鼠标到底在哪个 td 几何区域内,就算哪个 td。
    //    优先放在最前,因为 docx-preview 给图片 wrapper 的尺寸 = 原图自然尺寸,
    //    经常溢出 td 边界。后续用 elementsFromPoint + closest 拿到的"图片归属
    //    cell"可能跟视觉位置上的 cell 不一致 — 表现就是用户从 cell A 拖向
    //    cell B 时,DOM 上鼠标还停在 A 的 overflow img 上,focus 不更新,drag
    //    被误判成单击(用户报 10_照片打印.docx 网格图片无法拖选)。
    if (container) {
      const cells = container.querySelectorAll<HTMLElement>(
        '[data-row][data-col]'
      )
      for (const cell of Array.from(cells)) {
        const rect = cell.getBoundingClientRect()
        if (
          clientX >= rect.left &&
          clientX < rect.right &&
          clientY >= rect.top &&
          clientY < rect.bottom
        ) {
          td = cell
          break
        }
      }
    }

    // 2) bbox 找不到再走 elementsFromPoint(冷门 case: cell 被 transform 改了位
    //    置 / 父级有 clip-path 等,bbox 跟视觉位置不一致时还能兜)
    if (!td && typeof document.elementsFromPoint === 'function') {
      const elements = document.elementsFromPoint(clientX, clientY)
      for (const el of elements) {
        if (!(el instanceof HTMLElement)) continue
        if (container && !container.contains(el)) continue
        const hit = el.closest('[data-row][data-col]') as HTMLElement | null
        if (hit) {
          td = hit
          break
        }
      }
    }

    // 3) 最后兜底:从 target 节点 ancestor 链找
    if (!td && target) {
      td = target.closest('[data-row][data-col]') as HTMLElement | null
    }

    if (!td) return null
    const r = parseInt(td.dataset.row || '0', 10)
    const c = parseInt(td.dataset.col || '0', 10)
    if (!r || !c) return null
    // docx 场景取 table id 区分多个 table（xlsx 用不到，留 undefined）
    const tableId = td.dataset.docxTableId
    return { td, pos: { r, c, tableId } }
  }

  const getCellPosFromSelection = useCallback((): {
    td: HTMLElement
    pos: CellPos
  } | null => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null

    const container = containerRef.current
    const nodes = [selection.anchorNode, selection.focusNode].filter(
      Boolean
    ) as Node[]
    for (const node of nodes) {
      const el =
        node.nodeType === Node.ELEMENT_NODE
          ? (node as HTMLElement)
          : (node.parentElement as HTMLElement | null)
      if (!el) continue
      if (container && !container.contains(el)) continue
      const td = el.closest('[data-row][data-col]') as HTMLElement | null
      if (!td) continue
      const r = parseInt(td.dataset.row || '0', 10)
      const c = parseInt(td.dataset.col || '0', 10)
      if (!r || !c) continue
      return {
        td,
        pos: { r, c, tableId: td.dataset.docxTableId }
      }
    }

    return null
  }, [])

  const buildSelectedContentFromRange = useCallback(
    (
      rawRange: CellRange,
      opts: { lockColumns?: boolean; lockRows?: boolean } = {}
    ): SelectedContent | null => {
      if (docType !== 'xlsx' && docType !== 'docx') return null

      let effectiveRange = rawRange
      if (containerRef.current) {
        const isSingleCell =
          rawRange.anchor.r === rawRange.focus.r &&
          rawRange.anchor.c === rawRange.focus.c &&
          rawRange.anchor.tableId === rawRange.focus.tableId

        if (isSingleCell) {
          const tableSelector = rawRange.anchor.tableId
            ? `[data-docx-table-id="${rawRange.anchor.tableId}"] `
            : ''
          const selfCell = containerRef.current.querySelector<HTMLElement>(
            `${tableSelector}[data-row="${rawRange.anchor.r}"][data-col="${rawRange.anchor.c}"]`
          )
          const rs = parseInt(selfCell?.getAttribute('rowspan') || '1', 10)
          const cs = parseInt(selfCell?.getAttribute('colspan') || '1', 10)
          effectiveRange = {
            anchor: {
              r: rawRange.anchor.r,
              c: rawRange.anchor.c,
              tableId: rawRange.anchor.tableId
            },
            focus: {
              r: rawRange.anchor.r + Math.max(1, rs) - 1,
              c: rawRange.anchor.c + Math.max(1, cs) - 1,
              tableId: rawRange.anchor.tableId
            }
          }
        } else {
          const cells = containerRef.current.querySelectorAll<HTMLElement>(
            '[data-row][data-col]'
          )
          const exp = expandRangeByMergedCells(rawRange, cells, opts)
          effectiveRange = {
            anchor: {
              r: exp.minR,
              c: exp.minC,
              tableId: rawRange.anchor.tableId
            },
            focus: {
              r: exp.maxR,
              c: exp.maxC,
              tableId: rawRange.focus.tableId
            }
          }
        }
      }

      let payload: RangePayload | null = null
      if (docType === 'xlsx' && workbookRef.current) {
        payload = buildRangePayload(
          workbookRef.current,
          activeSheetIdxRef.current,
          effectiveRange,
          cellImagesRef.current
        )
      } else if (docType === 'docx' && containerRef.current) {
        payload = buildDocxRangePayload(containerRef.current, effectiveRange)
      }
      if (!payload) return null

      const cellCount = countCellsInRange(effectiveRange)
      return buildSelectedContentFromPayload(
        payload,
        docType as 'docx' | 'xlsx',
        cellCount
      )
    },
    [docType]
  )

  // 表格拖拽框选（xlsx 原生 + docx 里的 <table>，复用同一套 handler）
  const handleTableMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (docType !== 'xlsx' && docType !== 'docx') return
      if (e.button !== 0) return

      // ─── 列头/行头点击：构造整列/整行选区并立即弹浮窗（xlsx 专属）
      if (docType === 'xlsx') {
        const target = e.target as HTMLElement | null
        const colhead = target?.closest<HTMLElement>(
          '.aiClerk-xlsx-colhead[data-colhead]'
        )
        const rowhead = !colhead
          ? target?.closest<HTMLElement>('.aiClerk-xlsx-rowhead[data-rowhead]')
          : null
        if (colhead || rowhead) {
          const ws = workbookRef.current?.worksheets[activeSheetIdxRef.current]
          if (!ws) return
          let nextRange: CellRange | null = null
          if (colhead) {
            const c = parseInt(colhead.getAttribute('data-colhead') || '0', 10)
            if (c >= 1 && ws.rowCount >= 1) {
              nextRange = {
                anchor: { r: 1, c },
                focus: { r: ws.rowCount, c }
              }
            }
          } else if (rowhead) {
            const r = parseInt(rowhead.getAttribute('data-rowhead') || '0', 10)
            if (r >= 1 && ws.columnCount >= 1) {
              nextRange = {
                anchor: { r, c: 1 },
                focus: { r, c: ws.columnCount }
              }
            }
          }
          if (!nextRange) return

          e.preventDefault() // 防止 th 上误触文本选区
          dragRef.current = { dragging: false, moved: false }
          lastPointerSingleCellContentRef.current = null
          lastSingleCellContentRef.current = null
          cellRangeRef.current = nextRange
          setCellRange(nextRange)

          // 整列/整行选区:扩张时锁定对应轴,避免跨列/跨行合并单元格
          // (如第一行 A:G 合并)把单列选区误扩到整表
          const content = buildSelectedContentFromRange(nextRange, {
            lockColumns: !!colhead,
            lockRows: !!rowhead
          })
          if (content) {
            // 注入 selectionShape:浮窗按"整列/整行"分支显示对应 quick action
            const shape: 'whole-col' | 'whole-row' = colhead
              ? 'whole-col'
              : 'whole-row'
            // 整列/整行场景:用 header(列号 A / 行号 1) 的 rect 当锚点
            const rect = (colhead || rowhead)!.getBoundingClientRect()
            setPending({
              content: { ...content, selectionShape: shape },
              ...computeAnchor(rect)
            })
          } else {
            setPending(null)
          }
          return
        }
      }

      const hit = getCellPosFromEvent(e)
      lastPointerSingleCellContentRef.current = null
      lastSingleCellContentRef.current = null
      if (!hit) {
        // 非 cell 命中:先看是不是 docx 段落里的非表格 <img>。
        // 命中 → 起手做"图片拖选"模式:初始 pending 单图选中,后续 mousemove
        // 若拖到其他非表格 img 上,会扩展 pending.imageSrcs 实现多选。
        const tgt = e.target as HTMLElement | null
        const imgEl = tgt?.closest<HTMLImageElement>('img')
        if (
          docType === 'docx' &&
          imgEl &&
          containerRef.current?.contains(imgEl) &&
          !imgEl.closest('table')
        ) {
          e.preventDefault()
          e.stopPropagation()
          const rect = imgEl.getBoundingClientRect()
          const src = imgEl.getAttribute('src') || ''
          const viewPath = getNonTableImgViewPath(containerRef.current, imgEl)
          cellRangeRef.current = null
          setCellRange(null)
          // 启动 image-drag 模式 + dragRef(让 mouseup 走 wasDragging 分支,
          // 避免被当成"点空白处"清掉 pending)
          imageDragRef.current = {
            active: true,
            startX: e.clientX,
            startY: e.clientY
          }
          dragRef.current = { dragging: true, moved: false }
          setPending({
            content: {
              text: '[图片]',
              source: 'image-selection',
              docType: 'docx',
              imageSrcs: [src],
              imageViewPaths: [viewPath]
            },
            ...computeAnchor(rect)
          })
          return
        }
        cellRangeRef.current = null
        setCellRange(null)
        setPending(null)
        return
      }
      dragRef.current = { dragging: true, moved: false }
      const nextRange = { anchor: hit.pos, focus: hit.pos }
      cellRangeRef.current = nextRange
      lastPointerSingleCellContentRef.current =
        buildSelectedContentFromRange(nextRange)
      setCellRange(nextRange)
      setPending(null)
    },
    [docType, buildSelectedContentFromRange]
  )

  // 拖拽到容器边缘时自动滚动（Excel-like）
  const ensureAutoScrollRunning = useCallback(() => {
    if (scrollRafRef.current != null) return
    const step = () => {
      const c = containerRef.current
      if (!c || !dragRef.current.dragging) {
        scrollRafRef.current = null
        return
      }
      const { x: vx, y: vy } = scrollVelRef.current
      if (vx === 0 && vy === 0) {
        scrollRafRef.current = null
        return
      }
      c.scrollLeft += vx
      c.scrollTop += vy
      // 滚过之后，cursor 下的 cell 变了 —— 重新命中 focus
      const { x, y } = lastMouseRef.current
      const el = document.elementFromPoint(x, y) as HTMLElement | null
      const td = el?.closest('[data-row][data-col]') as HTMLElement | null
      if (td) {
        const r = parseInt(td.dataset.row || '0', 10)
        const col = parseInt(td.dataset.col || '0', 10)
        const tid = td.dataset.docxTableId
        setCellRange((prev) => {
          if (!prev) return prev
          if (prev.anchor.tableId !== tid) return prev
          if (prev.focus.r === r && prev.focus.c === col) return prev
          if (!dragRef.current.moved) {
            dragRef.current.moved = true
            const sel = window.getSelection()
            if (sel) sel.removeAllRanges()
          }
          lastPointerSingleCellContentRef.current = null
          const nextRange = {
            anchor: prev.anchor,
            focus: { r, c: col, tableId: tid }
          }
          cellRangeRef.current = nextRange
          return nextRange
        })
      }
      scrollRafRef.current = requestAnimationFrame(step)
    }
    scrollRafRef.current = requestAnimationFrame(step)
  }, [])

  const handleTableMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragRef.current.dragging) return
      lastMouseRef.current = { x: e.clientX, y: e.clientY }
      const container = containerRef.current

      // ─── 非表格图片拖选模式 ─────────────────────────────────────
      // mousedown 落在非表格 img 上时开了 image-drag。mousemove 算 rubber-band
      // rect,跟容器内所有非表格 img 的 bbox 求交,命中的 src + ViewPath 进
      // pending.imageSrcs / imageViewPaths。
      if (imageDragRef.current.active && container) {
        e.preventDefault()
        const { startX, startY } = imageDragRef.current
        const minX = Math.min(startX, e.clientX)
        const maxX = Math.max(startX, e.clientX)
        const minY = Math.min(startY, e.clientY)
        const maxY = Math.max(startY, e.clientY)
        const matched: {
          src: string
          rect: DOMRect
          viewPath: string
        }[] = []
        container.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
          if (img.closest('table')) return // 表格内的图不参与非表格拖选
          const r = img.getBoundingClientRect()
          if (
            r.right < minX ||
            r.left > maxX ||
            r.bottom < minY ||
            r.top > maxY
          )
            return // 不相交
          const src = img.getAttribute('src') || ''
          if (!src) return
          const viewPath = getNonTableImgViewPath(container, img)
          matched.push({ src, rect: r, viewPath })
        })
        if (matched.length > 0) {
          if (matched.length > 1) dragRef.current.moved = true
          // 浮窗锚点 = 所有命中 img 的 union bbox(让浮窗能定位到选区中心)
          let l = Infinity
          let t = Infinity
          let r = -Infinity
          let b = -Infinity
          for (const m of matched) {
            if (m.rect.left < l) l = m.rect.left
            if (m.rect.top < t) t = m.rect.top
            if (m.rect.right > r) r = m.rect.right
            if (m.rect.bottom > b) b = m.rect.bottom
          }
          const unionRect = { left: l, top: t, right: r, bottom: b }
          const srcs = matched.map((m) => m.src)
          const viewPaths = matched.map((m) => m.viewPath)
          setPending((prev) =>
            prev && prev.content.source === 'image-selection'
              ? {
                  ...prev,
                  content: {
                    ...prev.content,
                    imageSrcs: srcs,
                    imageViewPaths: viewPaths
                  },
                  ...computeAnchor(unionRect as DOMRect)
                }
              : prev
          )
        }
        return // image-drag 模式吃掉 mousemove,不走 cell drag 那条
      }

      // 计算边缘 auto-scroll 速度
      if (container) {
        const rect = container.getBoundingClientRect()
        const EDGE = 40 // 触发边缘宽度 (px)
        const MAX = 24 // 每帧最大滚动 (px)
        let vx = 0
        let vy = 0
        const rx = e.clientX - rect.left
        const ry = e.clientY - rect.top
        if (rx < EDGE) vx = -((EDGE - rx) / EDGE) * MAX
        else if (rx > rect.width - EDGE)
          vx = ((rx - (rect.width - EDGE)) / EDGE) * MAX
        if (ry < EDGE) vy = -((EDGE - ry) / EDGE) * MAX
        else if (ry > rect.height - EDGE)
          vy = ((ry - (rect.height - EDGE)) / EDGE) * MAX
        scrollVelRef.current = { x: vx, y: vy }
        if (vx !== 0 || vy !== 0) ensureAutoScrollRunning()
      }

      const hit = getCellPosFromEvent(e)
      if (!hit) return
      setCellRange((prev) => {
        if (!prev) return prev
        // 跨 table 拖拽不支持 —— 离开 anchor 所在的 table 就不更新 focus
        if (prev.anchor.tableId !== hit.pos.tableId) return prev
        if (prev.focus.r === hit.pos.r && prev.focus.c === hit.pos.c)
          return prev
        if (!dragRef.current.moved) {
          dragRef.current.moved = true
          const sel = window.getSelection()
          if (sel) sel.removeAllRanges()
        }
        if (dragRef.current.moved) {
          e.preventDefault()
        }
        lastPointerSingleCellContentRef.current = null
        const nextRange = { anchor: prev.anchor, focus: hit.pos }
        cellRangeRef.current = nextRange
        return nextRange
      })
    },
    [ensureAutoScrollRunning]
  )

  const handleTableMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (docType !== 'xlsx' && docType !== 'docx') return
      const wasDragging = dragRef.current.dragging
      const didDragAcrossCells = dragRef.current.moved
      const wasImageDrag = imageDragRef.current.active
      dragRef.current = { dragging: false, moved: false }
      imageDragRef.current = { active: false, startX: 0, startY: 0 }
      // 停掉 auto-scroll
      scrollVelRef.current = { x: 0, y: 0 }
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }

      // 图片拖选刚松手:pending 已在 mousemove 期间被更新成正确的 imageSrcs,
      // 这里不需要再做什么 — 直接 return,不走下面 cell range 的收尾流程
      // (后者会读 cellRangeRef.current → 我们图片拖选已经把它清空了)
      if (wasImageDrag) {
        void didDragAcrossCells // ESLint 静默,这里用不上
        return
      }

      // 没发生拖拽（用户点空白 / 非 cell 区域）—— 尝试纯文字选区菜单
      if (!wasDragging) {
        const selection = window.getSelection()
        const text = selection?.toString().trim() || ''
        const container = containerRef.current
        if (text && onContentSelected && container) {
          const selRange =
            selection && selection.rangeCount > 0
              ? selection.getRangeAt(0)
              : null
          if (
            selRange &&
            container.contains(selRange.commonAncestorContainer)
          ) {
            const rect = selRange.getBoundingClientRect()
            setPending({
              content: {
                text,
                source: 'text-selection',
                docType: docType as 'docx' | 'xlsx'
              },
              ...computeAnchor(rect)
            })
          }
        }
        return
      }

      // 同一 cell 里松开 + 有文字选区 → 走 text-selection
      if (!didDragAcrossCells) {
        const currentCellRange = cellRangeRef.current
        const singleCellContent =
          currentCellRange &&
          currentCellRange.anchor.r === currentCellRange.focus.r &&
          currentCellRange.anchor.c === currentCellRange.focus.c &&
          currentCellRange.anchor.tableId === currentCellRange.focus.tableId
            ? buildSelectedContentFromRange(currentCellRange)
            : null
        lastSingleCellContentRef.current = singleCellContent

        const selection = window.getSelection()
        const text = selection?.toString().trim() || ''
        if (text && onContentSelected) {
          const selRange =
            selection && selection.rangeCount > 0
              ? selection.getRangeAt(0)
              : null
          if (selRange) {
            const rect = selRange.getBoundingClientRect()
            setPending({
              content: {
                text,
                source: 'text-selection',
                docType: docType as 'docx' | 'xlsx'
              },
              ...computeAnchor(rect)
            })
            cellRangeRef.current = null
            setCellRange(null)
            return
          }
        }
      } else {
        lastSingleCellContentRef.current = null
      }

      // 有选区 → 按 docType 构造 range payload
      const currentCellRange = cellRangeRef.current
      if (!currentCellRange || !onContentSelected) return
      // 按合并单元格扩张选区，再生成 payload，保证"看到的 = 发给 AI 的"
      const content = buildSelectedContentFromRange(currentCellRange)
      if (!content) return
      // 用整片选区(已被合并单元格扩张)的 union bbox 算浮窗位置:
      // 比 hit?.td 准 — 后者只是松开时鼠标下的单 td,如果用户拖到合并块边缘,
      // hit 可能落在视觉选区外部,浮窗就跟选区脱节
      const container = containerRef.current
      const tds = container
        ? Array.from(container.querySelectorAll<HTMLElement>('td.is-selected'))
        : []
      let rect: { left: number; right: number; top: number; bottom: number }
      if (tds.length > 0) {
        let l = Infinity
        let t = Infinity
        let r = -Infinity
        let b = -Infinity
        for (const td of tds) {
          const tr = td.getBoundingClientRect()
          if (tr.left < l) l = tr.left
          if (tr.top < t) t = tr.top
          if (tr.right > r) r = tr.right
          if (tr.bottom > b) b = tr.bottom
        }
        rect = { left: l, right: r, top: t, bottom: b }
      } else {
        const hit = getCellPosFromEvent(e)
        const fallback = hit?.td.getBoundingClientRect()
        rect = fallback || {
          left: e.clientX,
          right: e.clientX,
          top: e.clientY,
          bottom: e.clientY
        }
      }
      setPending({
        content,
        ...computeAnchor(rect)
      })
    },
    [docType, onContentSelected, buildSelectedContentFromRange, computeAnchor]
  )

  const handleTableDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (docType !== 'xlsx' && docType !== 'docx') return
      if (!onCellQuickEdit) return
      const pendingSingleCell =
        pending?.content?.rangePayload &&
        pending.content.rangePayload.rows === 1 &&
        pending.content.rangePayload.cols === 1
          ? pending.content
          : null
      const pointerSingleCellContent = lastPointerSingleCellContentRef.current
      const lastSingleCellContent = lastSingleCellContentRef.current
      const currentCellRange = cellRangeRef.current

      const rangeSingleCell =
        currentCellRange &&
        currentCellRange.anchor.r === currentCellRange.focus.r &&
        currentCellRange.anchor.c === currentCellRange.focus.c &&
        currentCellRange.anchor.tableId === currentCellRange.focus.tableId
          ? buildSelectedContentFromRange(currentCellRange)
          : null

      // 优先用鼠标事件坐标命中 cell —— 这是最准的来源。
      // 之前先看 window.getSelection() 反查 cell,但 WPS DISPIMG 这类纯图片
      // cell 内部没文本,双击时浏览器会把选区扩到隔壁文本 cell(常见就是
      // "点第二列图片但反查到第一列文字 cell"),弹出错误的文本 modal。
      // 改成 event-hit 优先,selection 仅做兜底。
      const eventHit = getCellPosFromEvent(e)
      let content = eventHit
        ? buildSelectedContentFromRange({
            anchor: eventHit.pos,
            focus: eventHit.pos
          })
        : null
      if (!content) {
        const selectionCell = getCellPosFromSelection()
        if (selectionCell) {
          content = buildSelectedContentFromRange({
            anchor: selectionCell.pos,
            focus: selectionCell.pos
          })
        }
      }
      if (!content) {
        content =
          pointerSingleCellContent ||
          pendingSingleCell ||
          lastSingleCellContent ||
          rangeSingleCell
      }

      if (!content) return

      e.preventDefault()
      e.stopPropagation()
      lastPointerSingleCellContentRef.current = null
      // 关掉 SelectionMenu 浮窗(避免它跟 quick-edit popover 同时挂),
      // **同时主动把 cellRange 重设到双击 cell** — 让蓝框 highlight 持续显示.
      // 跟工具栏"修改"action 触发的视觉一致(closeMenuKeepSelection 同语义).
      //
      // 必须主动设回,而不只是"不清掉":
      // 双击事件序列里, mouseup#2 会因"同 cell + 有文字选区"分支调
      // setCellRange(null)(见 handleTableMouseUp 那段);dblclick 在它之后触发,
      // 如果不主动 set 回来,cellRange 已经是 null,td.is-selected 蓝框消失.
      // 用 eventHit.pos 反算 anchor+focus 单 cell range 即可.
      setPending(null)
      if (eventHit) {
        const dblRange = { anchor: eventHit.pos, focus: eventHit.pos }
        cellRangeRef.current = dblRange
        setCellRange(dblRange)
      }
      // anchor:用被双击 cell 的 bounding rect,跟工具栏"修改"action 走的
      // pending.rect 同口径,箭头从 cell 底边中心画下来,视觉对齐.
      // (旧实现用 e.clientX/Y 单点,箭头会偏向鼠标双击落点,跟选中蓝框中心不一致)
      const cellRect = eventHit?.td.getBoundingClientRect()
      const anchor = cellRect
        ? {
            left: cellRect.left,
            top: cellRect.top,
            right: cellRect.right,
            bottom: cellRect.bottom
          }
        : {
            left: e.clientX,
            top: e.clientY,
            right: e.clientX,
            bottom: e.clientY
          }
      onCellQuickEdit(content, undefined, anchor)
    },
    [
      docType,
      onCellQuickEdit,
      pending,
      buildSelectedContentFromRange,
      getCellPosFromSelection
    ]
  )

  // ─── 主按钮(AI 帮填)点击 ─────────────────────────
  // 主按钮语义已从"加入到会话"改为"AI 帮填":
  //   - 调 onQuickAction(content, prompt) 而不是 onContentSelected
  //   - prompt 由 buildAutoFillPrompt 按选区结构(含表头/全空/有数据)生成
  // 函数名保留 handleAddToChat 以减少重命名连锁(prop 名 onAddToChat 在 SelectionMenu)
  const handleAddToChat = useCallback(() => {
    if (!pending || !onQuickAction) return
    // SelectionMenu primary "AI 帮填" click 埋点(string1=选中类型)
    trackClick('formFilling', 'ai_fill', {
      requirement_id: 1103,
      track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
      sessionID: readSessionId(),
      string1: pickAiFillSelectionKind(pending.content)
    })
    const basePrompt = buildAutoFillPrompt(pending.content)
    // 选区跨到合并块时,把 master 内容附在 prompt 末尾,让 AI 看见真实内容
    // (例如 D 列选区里 D1 是 A1:G1 合并的一部分,真实内容在 A1)
    const blocks = collectMergedBlocksForContent(
      pending.content,
      workbookRef.current,
      activeSheetIdxRef.current
    )
    onQuickAction(
      pending.content,
      appendMergedContextToPrompt(basePrompt, blocks)
    )
    lastPointerSingleCellContentRef.current = null
    lastSingleCellContentRef.current = null
    setPending(null)
    cellRangeRef.current = null
    setCellRange(null)
    // 清掉浏览器选区
    const sel = window.getSelection()
    if (sel) sel.removeAllRanges()
  }, [pending, onQuickAction])

  const handleCloseMenu = useCallback(() => {
    setPending(null)
  }, [])

  const clearPendingAndSelection = useCallback(() => {
    lastPointerSingleCellContentRef.current = null
    lastSingleCellContentRef.current = null
    setPending(null)
    cellRangeRef.current = null
    setCellRange(null)
    const sel = window.getSelection()
    if (sel) sel.removeAllRanges()
  }, [])

  // 关浮窗但保留 cellRange / td.is-selected 视觉
  // 用于打开 quickEdit Modal 的场景：让用户在 Modal 里仍能看到正在编辑哪个 cell
  const closeMenuKeepSelection = useCallback(() => {
    setPending(null)
    const sel = window.getSelection()
    if (sel) sel.removeAllRanges()
  }, [])

  const handleActionClick = useCallback(
    (action: QuickAction) => {
      if (!pending) return
      const entry = QUICK_ACTIONS.find((a) => a.id === action.id)
      if (!entry) return
      const content = pending.content
      const kind: QuickActionKind = entry.kind || 'prompt'

      // 工具栏 / quick edit menu click 埋点(对齐 ai_click_web_ai_form_filling)
      const trackItem = QA_ID_TO_TRACK_ITEM[action.id]
      if (trackItem) {
        trackClick('formFilling', trackItem, {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
          sessionID: readSessionId()
        })
      }

      // 1) 前端预览类:不发 prompt
      if (kind === 'preview-image') {
        const src = findImageSrcInContent(content)
        if (src) setPreviewImageSrc(src)
        clearPendingAndSelection()
        return
      }

      // 2) 触发 quick-edit-modal:把 content 上注 mode hint 后交给上层
      if (
        kind === 'quick-edit-image' ||
        kind === 'quick-edit-image-batch' ||
        kind === 'quick-edit-text'
      ) {
        const mode: 'image' | 'image-batch' | 'text' =
          kind === 'quick-edit-image'
            ? 'image'
            : kind === 'quick-edit-image-batch'
              ? 'image-batch'
              : 'text'
        // anchor:直接复用 pending.rect(选区已裁剪过的 viewport bbox),
        // 不再 querySelector(data-cell-ref) —— 那种做法在 docx 多 table 场景下
        // 会命中其他表里同 ref 的 cell,导致 quickEdit popover 飞到错误位置 / 不可见,
        // 表现为"换图点不出 picker"。直接用 pending.rect 既准确又规避了 tableId 上下文丢失。
        const anchor = pending.rect
        if (onCellQuickEdit) {
          onCellQuickEdit({ ...content, quickEditMode: mode }, mode, anchor)
        }
        // 打开 quickEdit Modal 后保留 cellRange，让 td.is-selected 蓝框依旧
        // 显示，用户在 Modal 里能看到自己正在编辑哪个 cell
        closeMenuKeepSelection()
        return
      }

      // prompt enrichment:把选区跨到的合并块 master 内容 append 进去,
      // 让 AI 看见(否则 D 列里的 D1 实际是 A1:G1 合并的一部分,AI 看不到)
      const enrich = (basePrompt: string) =>
        appendMergedContextToPrompt(
          basePrompt,
          collectMergedBlocksForContent(
            content,
            workbookRef.current,
            activeSheetIdxRef.current
          )
        )

      // 3) 破坏性:弹二次确认,确认后直接发送给 AI
      if (kind === 'confirm-send') {
        const title = entry.confirmTitle?.(content) || '确认操作?'
        const body = entry.confirmContent?.(content) || '该操作将提交给 AI 执行'
        Modal.confirm({
          title,
          content: body,
          okText: entry.danger ? '确认删除' : '确认',
          okType: entry.danger ? 'danger' : 'primary',
          cancelText: '取消',
          onOk: () => {
            if (onQuickAction) {
              onQuickAction(
                content,
                enrich(entry.buildPrompt(content)),
                'send-now'
              )
            }
            clearPendingAndSelection()
          }
        })
        // 浮窗立刻收掉,避免确认框遮挡时浮窗还露出
        setPending(null)
        return
      }

      // 4) send-prompt:命令式操作(插入列/插入行),直接发送
      if (kind === 'send-prompt') {
        if (onQuickAction) {
          onQuickAction(content, enrich(entry.buildPrompt(content)), 'send-now')
        }
        clearPendingAndSelection()
        return
      }

      // 5) prompt(默认):预填到输入框,等用户编辑后手动按发送
      const prompt = enrich(entry.buildPrompt(content))
      if (onQuickAction) {
        onQuickAction(content, prompt, 'prefill')
      } else if (onContentSelected) {
        onContentSelected(content)
      }
      clearPendingAndSelection()
    },
    [
      pending,
      onQuickAction,
      onContentSelected,
      onCellQuickEdit,
      clearPendingAndSelection
    ]
  )

  // ─── 选区可视化（直接 DOM 操作，避免重渲大表格）
  // 策略：选区内所有 cell 叠加相同的浅蓝背景；外边缘 cell 用 inline box-shadow
  // 组合出一个「包围整个选区」的粗蓝外框（顶 cell 只加顶边、角 cell 加两边…）
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // 清理上一次
    container.querySelectorAll<HTMLElement>('td.is-selected').forEach((td) => {
      td.classList.remove('is-selected')
      td.style.boxShadow = ''
    })
    if (!cellRange) return
    const anchorTableId = cellRange.anchor.tableId
    const BORDER = 'rgb(37, 99, 235)'
    // docx 场景按 anchor 所在 table 过滤；xlsx anchorTableId 为 undefined 则选全容器
    const scope = anchorTableId
      ? container.querySelector(`table[data-docx-table-id="${anchorTableId}"]`)
      : container
    const tds = (scope || container).querySelectorAll<HTMLElement>(
      '[data-row][data-col]'
    )
    // 推断选区是否是整列/整行(点列号/行号触发):此时扩张要锁定对应轴,
    // 否则首行/首列的跨列/跨行合并(如标题行 A:G 合并)会把单列/单行选区
    // 误扩为整表
    let lockColumns = false
    let lockRows = false
    const ws = workbookRef.current?.worksheets[activeSheetIdxRef.current]
    if (ws) {
      const r1 = Math.min(cellRange.anchor.r, cellRange.focus.r)
      const r2 = Math.max(cellRange.anchor.r, cellRange.focus.r)
      const c1 = Math.min(cellRange.anchor.c, cellRange.focus.c)
      const c2 = Math.max(cellRange.anchor.c, cellRange.focus.c)
      if (r1 === 1 && r2 === ws.rowCount && c1 === c2) lockColumns = true
      else if (c1 === 1 && c2 === ws.columnCount && r1 === r2) lockRows = true
    }
    // 先用合并单元格的 rowspan/colspan 扩张 range,确保整个合并块整体进/出选区
    const expanded = expandRangeByMergedCells(cellRange, tds, {
      lockColumns,
      lockRows
    })
    const { minR, maxR, minC, maxC } = expanded
    tds.forEach((td) => {
      const r = parseInt(td.dataset.row || '0', 10)
      const c = parseInt(td.dataset.col || '0', 10)
      const rs = parseInt(td.getAttribute('rowspan') || '1', 10)
      const cs = parseInt(td.getAttribute('colspan') || '1', 10)
      // td 的 bbox：[r..r+rs-1, c..c+cs-1]。与选区交叉即入选
      const tdMaxR = r + rs - 1
      const tdMaxC = c + cs - 1
      if (tdMaxR < minR || r > maxR || tdMaxC < minC || c > maxC) return
      td.classList.add('is-selected')
      const shadows: string[] = []
      if (r <= minR) shadows.push(`inset 0 2px 0 0 ${BORDER}`)
      if (tdMaxR >= maxR) shadows.push(`inset 0 -2px 0 0 ${BORDER}`)
      if (c <= minC) shadows.push(`inset 2px 0 0 0 ${BORDER}`)
      if (tdMaxC >= maxC) shadows.push(`inset -2px 0 0 0 ${BORDER}`)
      if (shadows.length > 0) td.style.boxShadow = shadows.join(', ')
    })
  }, [cellRange, excelHtml])

  // 预览里的图片默认会触发浏览器原生拖拽，抢走我们的表格框选手势。
  // 这里统一拦截图片 dragstart，让鼠标拖动继续落到 td range selection。
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container
      .querySelectorAll<HTMLElement>('img, svg image')
      .forEach((el) => el.setAttribute('draggable', 'false'))

    const onDragStart = (e: DragEvent) => {
      const target = e.target as Element | null
      if (!target) return
      const tag = target.tagName.toLowerCase()
      if (
        tag === 'img' ||
        tag === 'image' ||
        target.closest('img, svg image')
      ) {
        e.preventDefault()
      }
    }

    container.addEventListener('dragstart', onDragStart)
    return () => container.removeEventListener('dragstart', onDragStart)
  }, [excelHtml, renderedPreviewKind])

  // ─── 非表格图片选区视觉反馈
  // pending.source==='image-selection' 时,给文档里 src 命中 imageSrcs 列表的所有
  // img 加蓝色描边(支持多图拖选);浮窗关闭 / 选其他选区 / 文件刷新时自动清掉。
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // 清理上一轮
    container
      .querySelectorAll<HTMLImageElement>('img.is-image-selected')
      .forEach((el) => el.classList.remove('is-image-selected'))
    if (!pending || pending.content.source !== 'image-selection') return
    const srcs = pending.content.imageSrcs
    if (!srcs || srcs.length === 0) return
    const srcSet = new Set(srcs)
    // 用 querySelectorAll 而非 [src=] 选择器:src 里可能含特殊字符,getAttribute 比对更稳
    container.querySelectorAll<HTMLImageElement>('img').forEach((el) => {
      if (el.closest('table')) return
      const elSrc = el.getAttribute('src') || ''
      if (srcSet.has(elSrc)) {
        el.classList.add('is-image-selected')
      }
    })
  }, [pending])

  // ─── Sheet tab 切换（多 sheet xlsx）
  // <button.aiClerk-xlsx-tab[data-sheet-idx]> 由 dangerouslySetInnerHTML 渲染,
  // 没有 React 事件;这里用 click delegation 完成切换 + ref 同步 + 选区清理
  useEffect(() => {
    if (docType !== 'xlsx' || !excelHtml) return
    const root = containerRef.current
    if (!root) return

    const onClick = (e: MouseEvent) => {
      const tab = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        '.aiClerk-xlsx-tab[data-sheet-idx]'
      )
      if (!tab || !root.contains(tab)) return
      const nextIdx = parseInt(tab.getAttribute('data-sheet-idx') || '-1', 10)
      if (Number.isNaN(nextIdx) || nextIdx < 0) return
      if (nextIdx === activeSheetIdxRef.current) return

      // tabs active class
      root
        .querySelectorAll<HTMLElement>('.aiClerk-xlsx-tab[data-sheet-idx]')
        .forEach((btn) => {
          const idx = parseInt(btn.getAttribute('data-sheet-idx') || '-1', 10)
          btn.classList.toggle('active', idx === nextIdx)
        })

      // sheet 可见性
      root
        .querySelectorAll<HTMLElement>('.aiClerk-xlsx-sheet[data-sheet-idx]')
        .forEach((s) => {
          const idx = parseInt(s.getAttribute('data-sheet-idx') || '-1', 10)
          s.style.display = idx === nextIdx ? '' : 'none'
        })

      // 同步 ref(影响 buildRangePayload) + 清空跨 sheet 失效选区
      activeSheetIdxRef.current = nextIdx
      lastPointerSingleCellContentRef.current = null
      lastSingleCellContentRef.current = null
      cellRangeRef.current = null
      setCellRange(null)
      setPending(null)
    }

    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [docType, excelHtml])

  // 点击容器外关闭选区
  useEffect(() => {
    if (!cellRange) return
    const onDocClick = (e: MouseEvent) => {
      if (dragRef.current.dragging) return
      const container = containerRef.current
      const target = e.target as HTMLElement | null
      if (target?.closest('.aiClerk-selection-menu')) return
      // quick-edit popover 用 ReactDOM.createPortal 渲染到 body 外面,不在
      // container 内.用户双击 cell 后想点 popover textarea / 关闭按钮时,
      // mousedown target 走到 popover 上,会触发"点容器外"分支清掉 cellRange,
      // 蓝色选中框消失 —— 跟"我正在编辑这一格"的视觉语义冲突.
      // 显式排除 popover 自身的 mousedown.
      if (target?.closest('.aiClerk-work__quick-edit-popover')) return
      if (container && !container.contains(e.target as Node)) {
        lastPointerSingleCellContentRef.current = null
        lastSingleCellContentRef.current = null
        cellRangeRef.current = null
        setCellRange(null)
        setPending(null)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [cellRange])

  // 兜底：如果 mouseup 在容器外发生，React 的 onMouseUp 不会触发 →
  // 拖拽状态和 auto-scroll 会卡住。全局监听 mouseup 清理。
  useEffect(() => {
    const onDocMouseUp = (e: MouseEvent) => {
      if (!dragRef.current.dragging) return
      // 如果 mouseup 在容器内，React 的 onMouseUp 会处理完整逻辑（弹菜单等），
      // 这里必须跳过，否则抢先清掉 dragRef 会让 React handler 走进 wasDragging=false 分支，菜单不弹。
      const c = containerRef.current
      if (c && c.contains(e.target as Node)) return
      dragRef.current = { dragging: false, moved: false }
      scrollVelRef.current = { x: 0, y: 0 }
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }
    }
    document.addEventListener('mouseup', onDocMouseUp)
    return () => document.removeEventListener('mouseup', onDocMouseUp)
  }, [])

  // ─── 渲染逻辑 ──────────────────────────
  // 注意：loading 必须在 deps 里。React 16 不会 batch async setState，
  // useFileLoader 在 .then 里先 setBuffer 再 setLoading(false) → 中间
  // 会有一次 "buffer 有但 loading=true" 的 render，此时 doc-content 容器
  // 还没挂载。必须等 loading 变成 false 后 effect 重新 fire，才能拿到 ref。
  useEffect(() => {
    if (!buffer || !source || loading) return
    if (loadedSourceKey !== currentSourceKey) return
    let disposed = false

    // 是否真换了文件:url base 变化才算。同文件 nonce 刷新走"保留"分支
    const isFileSwap = prevSourceUrlBaseRef.current !== sourceUrlBase
    prevSourceUrlBaseRef.current = sourceUrlBase

    setRenderError(null)
    if (docType === 'docx') {
      // docx 必须有 container 才能渲染（docx-preview 操作 DOM）
      const container = containerRef.current
      if (!container) return
      setRendering(true)
      container.innerHTML = ''
      // 撑满状态在每次渲染前都清掉:页面尺寸可能跨文档变化(A4 portrait→A4 landscape),
      // 重渲染前不清的话会先用上一份的 fit zoom,出现一帧错位.
      naturalDocxWidthPxRef.current = null
      setDocxFitZoom(null)
      if (isFileSwap) {
        // 真换文件:清选区 / pending / cell content 缓存
        lastPointerSingleCellContentRef.current = null
        lastSingleCellContentRef.current = null
        cellRangeRef.current = null
        setCellRange(null)
        setPending(null)
      }
      // 同文件刷新:保留 cellRange 和 pending,让浮窗 / 选中边框在 AI 修改
      // 重新拉取预览期间不消失。
      // 格式预检:扩展名是 .docx 但实际可能是老 .doc 二进制(WPS 另存常见)。
      // 直接交给 docx-preview 会报"Can't find end of central directory"用户看
      // 不懂,这里前置一道嗅探给出明确提示。
      const detected = detectDocFormat(buffer)
      if (detected === 'doc-legacy') {
        setRenderError(
          new Error(
            '文件扩展名是 .docx,但内部实际是旧版 Word 二进制格式(WPS Office 另存时偶发的兼容性问题)。请用 Word / WPS 打开后,「另存为」→ 文件类型选「Word 文档 (*.docx)」(不要选「Word 97-2003 文档」),覆盖后重新上传。'
          )
        )
        setRendering(false)
        return
      }
      if (detected === 'unknown') {
        setRenderError(new Error('文件格式无法识别,请确认是 .docx 文件。'))
        setRendering(false)
        return
      }
      loadDocxPreview()
        .then(async (docx) => {
          // 提前解析 document.xml 找出 continuous section 索引(docx-preview 不区分
          // continuous / nextPage,会按 sectPr 数量渲染等量 <section>,导致单页文档
          // 被拆成多个 section)。在 renderAsync 之后用这个索引合并。
          const continuousIndices = await readContinuousSectionIndices(buffer)
          await docx.renderAsync(buffer, container, undefined, {
            className: 'aiClerk-docx',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            breakPages: true,
            useBase64URL: true
          })
          return continuousIndices
        })
        .then((continuousIndices) => {
          mergeContinuousSections(container, continuousIndices)
          // 把单元格里的 anchored 图片拉回内联流,避免 WPS 转出的 docx 出现
          // "图片偏到隔壁 cell"。必须在分页 + 居中之前做,后者的 rect 计算
          // 依赖图片已经回到正确的 inline 位置。
          normalizeAnchoredImagesInCells(container)
          // 还原 hRule=exact 行高 (Word 里固定行高, docx-preview 把含大照片的 row
          // inline height 撑到 img + padding, 导致分页比 Word 多). 必须在分页前跑.
          normalizeExactRowHeights(container)
          applyWholeTablePageBreaks(container)
          forceCenterDocxTables(container, zoom)
          requestAnimationFrame(() => forceCenterDocxTables(container, zoom))
          window.setTimeout(() => forceCenterDocxTables(container, zoom), 120)
          // docx 设了"首页不同"时 docx-preview 只在首页渲染 header/footer,
          // 这里强制把首页模板克隆到所有缺失的 page,让每一页都有页眉/页脚
          ensureHeaderFooterOnEveryDocxPage(container)
          // 给 docx-preview 渲出的 <table> 打上 data-row/data-col/data-cell-ref 属性，
          // 复用 xlsx 的拖拽框选 + RangePayload 逻辑
          decorateDocxTables(container)
          // 撑满策略:测页面自然宽度(已应用 zoom),反算回 100% 自然值缓存,
          // 然后跟 doc-content 可见宽度比较 → 比预览栏窄就拉大 zoom 把页面撑满.
          // 分页 / 拆表 / 居中已经按原 zoom 排好,DOM 已固化,这里改 zoom 只是
          // 视觉等比放大,不会重新触发任何排版逻辑,所以分页等结果不会被影响.
          measureAndApplyDocxFit(
            container,
            zoom,
            naturalDocxWidthPxRef,
            setDocxFitZoom
          )
          if (!disposed) {
            setRenderedPreviewKind('docx')
            setRendering(false)
          }
        })
        .catch((e: unknown) => {
          if (!disposed) {
            setRenderError(e instanceof Error ? e : new Error(String(e)))
            setRendering(false)
          }
        })
    } else if (docType === 'xlsx') {
      // 同步走一遍格式预检(.xls 老二进制同样会被 ExcelJS 报奇怪错)
      const detectedXlsx = detectDocFormat(buffer)
      if (detectedXlsx === 'doc-legacy') {
        setRenderError(
          new Error(
            '文件扩展名是 .xlsx,但内部实际是旧版 Excel 二进制格式(WPS Office 另存时偶发的兼容性问题)。请用 Excel / WPS 打开后,「另存为」→ 文件类型选「Excel 工作簿 (*.xlsx)」(不要选「Excel 97-2003 工作簿」),覆盖后重新上传。'
          )
        )
        setRendering(false)
        return
      }
      if (detectedXlsx === 'unknown') {
        setRenderError(new Error('文件格式无法识别,请确认是 .xlsx 文件。'))
        setRendering(false)
        return
      }
      // xlsx 用 excelHtml state 渲染，不依赖 containerRef
      setRendering(true)
      setExcelHtml('')
      workbookRef.current = null
      if (isFileSwap) {
        // 真换文件:清选区 / pending / cell content 缓存
        lastPointerSingleCellContentRef.current = null
        lastSingleCellContentRef.current = null
        cellRangeRef.current = null
        setCellRange(null)
        setPending(null)
      }
      // 同文件刷新(refreshNonce 变化)保留 cellRange 和 pending,让用户
      // 双击 cell 后弹的浮窗在 SSE 期间 AI 触发预览刷新时不闪退
      ;(async () => {
        try {
          // 规避 ExcelJS 解析坑：部分 xlsx 把 xmlns:r="..." 声明在 <a:blip>
          // 嵌套元素上而不是 drawing 根 <xdr:wsDr>，ExcelJS 的 saxes 解析器没
          // 正确继承嵌套命名空间，导致 r:embed 的 rId 找不到、rels[rId].Target
          // 抛 "Cannot read properties of undefined (reading 'Target')"。
          // 预处理 buffer：把 xmlns:r 提到根上。
          const normalizedBuffer = await normalizeDrawingNamespaces(buffer)
          let wb: ExcelJS.Workbook
          let effectiveBuffer = normalizedBuffer
          let rotations: ImageRotations = new Map()
          try {
            wb = new ExcelJS.Workbook()
            await wb.xlsx.load(normalizedBuffer)
            rotations = await parseImageRotations(normalizedBuffer)
          } catch (loadErr) {
            // 依然崩 → 降级：剥掉 drawings/media 再 load，丢图保表格
            // eslint-disable-next-line no-console
            console.warn(
              '[xlsx] 常规加载失败，降级为无图加载:',
              loadErr instanceof Error ? loadErr.message : loadErr
            )
            wb = await loadXlsxWithoutDrawings(normalizedBuffer)
            effectiveBuffer = normalizedBuffer
            rotations = new Map()
          }
          void effectiveBuffer
          // WPS 内嵌图片 (cellimages.xml) —— ExcelJS 不识别, 独立解析后跟 floating
          // drawings 一起渲染. 缺失或解析失败不阻断, parseCellImages 内部已 try/catch.
          const cellImages = await parseCellImages(normalizedBuffer)
          const html = renderXlsxAsHtml(wb, rotations, cellImages, colSizing)
          workbookRef.current = wb
          cellImagesRef.current = cellImages
          activeSheetIdxRef.current = 0
          if (!disposed) {
            setExcelHtml(html)
            setRenderedPreviewKind('xlsx')
            setRendering(false)
          }
        } catch (e) {
          if (!disposed) {
            setRenderError(e instanceof Error ? e : new Error(String(e)))
            setRendering(false)
          }
        }
      })()
    } else {
      setRenderError(new Error(`Unsupported file type: ${docType}`))
    }
    return () => {
      disposed = true
    }
  }, [buffer, source, docType, loading, sourceUrlBase, colSizing])

  // ─── 各种状态都用统一的容器外壳，保证宽高一致 ────────────────────
  const renderInState = (
    icon: string,
    title: string,
    detail?: string,
    kind: 'empty' | 'loading' | 'error' = 'empty'
  ) => (
    <div className={`aiClerk-doc-state aiClerk-doc-state--${kind}`}>
      <div className="aiClerk-doc-state__icon">{icon}</div>
      <div className="aiClerk-doc-state__title">{title}</div>
      {detail && <div className="aiClerk-doc-state__detail">{detail}</div>}
    </div>
  )

  let inner: React.ReactNode

  if (!source) {
    inner = fallback ?? renderInState('📄', '无预览', '请先上传文件')
  } else if (loading && !keepPreviousPreview) {
    // 加载中不再显示"⏳ 加载中…"占位，渲染空容器避免视觉跳变
    inner = null
  } else if (error) {
    inner = renderInState('⚠️', '加载文件失败', error.message, 'error')
  } else if (renderError) {
    inner = renderInState('⚠️', '渲染失败', renderError.message, 'error')
  } else if (docType === 'unknown') {
    inner = renderInState('🚫', '不支持的格式', '仅支持 .docx / .xlsx')
  } else if (docType === 'xlsx' && !excelHtml) {
    // xlsx 已下载但还没解析完：和加载中一样不显示占位，避免视觉跳变
    inner = null
  } else {
    // 正常预览
    // 给两个分支不同的 key + 用 source URL 当 sub-key：
    //   - 不同 docType（xlsx ↔ docx）切换：key 不同 → React 卸载老节点重建，
    //     避免 docx-preview 注入的子节点和 dangerouslySetInnerHTML 抢同一个
    //     DOM 位置导致 NotFoundError: removeChild on Node
    //   - 同文件刷新（refreshUrl 变 t=xxx）：key 也变，强制 unmount/remount，
    //     防 docx-preview 在已渲染过的容器里二次注入产生残留
    inner =
      docType === 'xlsx' ? (
        <div
          key="xlsx-preview"
          className="aiClerk-doc-content aiClerk-doc-content--xlsx"
          ref={containerRef}
          onMouseDown={handleTableMouseDown}
          onMouseMove={handleTableMouseMove}
          onMouseUp={handleTableMouseUp}
          onDoubleClick={handleTableDoubleClick}
          dangerouslySetInnerHTML={{ __html: excelHtml }}
        />
      ) : (
        <div
          key="docx-preview"
          className="aiClerk-doc-content aiClerk-doc-content--docx"
          ref={containerRef}
          onMouseDown={handleTableMouseDown}
          onMouseMove={handleTableMouseMove}
          onMouseUp={handleTableMouseUp}
          onDoubleClick={handleTableDoubleClick}
        />
      )
  }

  // 引导提示仅在"内容真正可交互"的时候显示：已渲染、不在 loading / rendering、
  // 没有错误，且当前没有正在进行的选区或浮窗。这样在 state 占位（loading / error /
  // unsupported）时不会和占位文案打架。
  const docHintVisible =
    showDocHint &&
    !loading &&
    !error &&
    !renderError &&
    !rendering &&
    !!renderedPreviewKind &&
    !cellRange &&
    !pending

  // docx 实际生效 zoom: 撑满命中时用 fit zoom,否则原 zoom.xlsx 一直用原 zoom.
  // 分页 / 拆表 / 居中已经在原 zoom 下排好(DOM 已固化),这里只是视觉等比放大,
  // 不会反向触发任何排版逻辑.
  const effectiveZoom =
    renderedPreviewKind === 'docx' && docxFitZoom !== null ? docxFitZoom : zoom

  return (
    <>
      <div
        className={`aiClerk-doc-frame ${className || ''}`}
        style={{ ['--aiClerk-doc-zoom' as any]: String(effectiveZoom) }}
      >
        {inner}
        {docHintVisible && (
          <div className="aiClerk-doc-hint" role="status" aria-live="polite">
            <span className="aiClerk-doc-hint__icon" aria-hidden="true">
              💡
            </span>
            <span className="aiClerk-doc-hint__text">
              拖选一片区域 → 让 AI 帮你处理
            </span>
            <button
              type="button"
              className="aiClerk-doc-hint__dismiss"
              aria-label="知道了，关闭提示"
              onClick={() => {
                setShowDocHint(false)
                markDocHintSeen()
              }}
            >
              ×
            </button>
          </div>
        )}
        {(editing || syncing) && (
          <div
            className="aiClerk-doc-edit-mask"
            role="status"
            aria-live="polite"
            aria-label={
              editing ? 'AI 正在编辑文档，请稍候' : '正在同步最新文档'
            }
            // 直接吞掉所有指针 / 选区 / 双击事件，避免穿透到底下预览
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <div className="aiClerk-doc-edit-mask__inner">
              <span
                className="aiClerk-doc-edit-mask__spinner"
                aria-hidden="true"
              />
              <span className="aiClerk-doc-edit-mask__text">
                {editing ? 'AI 正在编辑文档…' : '正在同步最新文档…'}
              </span>
              {!editing && syncing && onDismissSync && (
                <button
                  type="button"
                  className="aiClerk-doc-edit-mask__cancel"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismissSync()
                  }}
                  aria-label="我知道了，关闭同步提示"
                >
                  知道了
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <SelectionMenu
        rect={pending?.rect ?? null}
        placement={pending?.placement ?? 'top'}
        onAddToChat={handleAddToChat}
        onClose={handleCloseMenu}
        actions={(() => {
          // 之前用 rangePayload 当 gate 会导致 text-selection 没有 actions;
          // 改为依赖每条 action 自己的 isVisible,filter 后空数组返回 undefined。
          if (!pending?.content) return undefined
          const filtered = QUICK_ACTIONS.filter(
            (a) => !a.isVisible || a.isVisible(pending.content)
          )
          if (filtered.length === 0) return undefined
          return filtered.map((a) => ({
            id: a.id,
            icon: a.icon,
            label: a.label,
            prompt: a.prompt,
            danger: a.danger
          }))
        })()}
        onAction={handleActionClick}
      />
      <ImagePreview
        src={previewImageSrc}
        alt="原图预览"
        onClose={() => setPreviewImageSrc(null)}
      />
    </>
  )
}

// ─── ExcelJS → HTML 表格（高保真：背景/边框/合并/列宽/行高） ──────

function renderXlsxAsHtml(
  workbook: ExcelJS.Workbook,
  rotations: ImageRotations = new Map(),
  cellImages: CellImagesMap = new Map(),
  colSizing?: ColSizing
): string {
  const parts: string[] = []
  parts.push('<div class="aiClerk-xlsx">')

  // aiClerk 当前每个 Excel 文件只处理首个 sheet(后端 tool 链路也是),
  // sheet 切换 UI 不再展示;切换逻辑保留,留作后续多 sheet 支持的扩展点

  workbook.worksheets.forEach((sheet, idx) => {
    parts.push(
      renderSheet(sheet, workbook, idx, rotations, cellImages, colSizing)
    )
  })

  parts.push('</div>')
  return parts.join('')
}

// ─── 解析 xlsx 里图片的变换信息（ExcelJS 丢了）
// key: "tlCol-tlRow" (0-indexed), value: { rotDeg, flipH, flipV }
interface ImageTransform {
  rotDeg: number
  flipH: boolean
  flipV: boolean
}
type ImageRotations = Map<string, ImageTransform>

const DRAWING_NS =
  'http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing'
const MAIN_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main'

/**
 * 预处理 xlsx：把 xl/drawings/drawing*.xml 的 xmlns:r 提到根节点。
 *
 * 部分 xlsx（WPS / 某些 Excel 版本）把 xmlns:r="http://…/relationships"
 * 声明在嵌套的 <a:blip> 元素上，而 ExcelJS 的 saxes 解析器没正确继承
 * 嵌套命名空间前缀，导致解析 r:embed 时找不到 rId → rels[rId].Target
 * 抛 "Cannot read properties of undefined (reading 'Target')"。
 *
 * 规范化：如果根 <xdr:wsDr> 没有 xmlns:r，补上 xmlns:r="http://…/relationships"。
 * 纯字符串替换避免走一轮 DOMParser 再序列化（xlsx 对 XML 细节敏感）。
 *
 * 失败时返回原 buffer，不阻塞主流程。
 */
async function normalizeDrawingNamespaces(
  buffer: ArrayBuffer
): Promise<ArrayBuffer> {
  try {
    const zip = await JSZip.loadAsync(buffer)
    const REL_NS =
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
    let touched = false

    // ── 规范化 1：drawing 根 <xdr:wsDr> 补 xmlns:r
    //   部分文件把 xmlns:r 声明在嵌套 <a:blip> 上，提到根上避免 saxes 不继承
    const drawingPaths: string[] = []
    zip.forEach((relPath) => {
      if (/^xl\/drawings\/drawing\d+\.xml$/.test(relPath)) {
        drawingPaths.push(relPath)
      }
    })
    // 需要补 xdr: 前缀的"spreadsheet drawing"标签白名单
    // —— ExcelJS 的 XForm 硬编码用 xdr: 前缀匹配，文件用默认 ns（<wsDr xmlns="...">）
    // 时这些元素全部解析失败 → getImages() 返空。这里加一遍 xdr: 前缀让 XForm 认得。
    const SPREADSHEET_DRAWING_TAGS = [
      'wsDr',
      'oneCellAnchor',
      'twoCellAnchor',
      'absoluteAnchor',
      'from',
      'to',
      'pos',
      'ext',
      'col',
      'colOff',
      'row',
      'rowOff',
      'pic',
      'nvPicPr',
      'cNvPr',
      'cNvPicPr',
      'blipFill',
      'spPr',
      'clientData'
    ]
    for (const path of drawingPaths) {
      let xml = await zip.file(path)!.async('text')
      let pathTouched = false

      // ── 规范化 1A：默认 ns 的 <wsDr> 改写成 <xdr:wsDr>
      //   匹配 `<wsDr ...>` 且根属性里有 xmlns="...spreadsheetDrawing"
      const defaultNsRoot =
        /<wsDr\s+([^>]*\bxmlns\s*=\s*["']http:\/\/schemas\.openxmlformats\.org\/drawingml\/2006\/spreadsheetDrawing["'][^>]*)>/.exec(
          xml
        )
      if (defaultNsRoot) {
        // 加 xmlns:xdr 声明 + 把根改成 <xdr:wsDr>，全文每个白名单标签前加 xdr:
        for (const tag of SPREADSHEET_DRAWING_TAGS) {
          // 开标签：`<tag` → `<xdr:tag`（要求 tag 后面是空格或 > 或 / 才匹配，
          // 防止误伤 `<colOff>` 里的 col 等）
          const open = new RegExp(`<${tag}(?=[\\s/>])`, 'g')
          xml = xml.replace(open, `<xdr:${tag}`)
          // 闭标签
          const close = new RegExp(`</${tag}>`, 'g')
          xml = xml.replace(close, `</xdr:${tag}>`)
        }
        // 给根加 xmlns:xdr 声明（保留原 xmlns="..." 不动）
        if (!/\bxmlns:xdr\s*=/.test(xml)) {
          xml = xml.replace(
            /<xdr:wsDr\b/,
            `<xdr:wsDr xmlns:xdr="${DRAWING_NS}"`
          )
        }
        pathTouched = true
      }

      // ── 规范化 1B：根 <xdr:wsDr> 补 xmlns:r（部分文件把 xmlns:r 声明在
      //    嵌套 <a:blip> 上，提到根上避免 saxes 不继承）
      const m = /<xdr:wsDr\b([^>]*)>/.exec(xml)
      if (m && !/\bxmlns:r\s*=/.test(m[1])) {
        const newOpen = `<xdr:wsDr${m[1]} xmlns:r="${REL_NS}">`
        xml = xml.replace(m[0], newOpen)
        pathTouched = true
      }

      if (pathTouched) {
        zip.file(path, xml)
        touched = true
      }
    }

    // ── 规范化 2：sheet XML 中的"非 r:" 关系前缀统一成 r:
    //   某些导出器用 relationships:id="rId1" 而非 r:id="rId1"，
    //   ExcelJS 的 drawing-xform 硬编码 r:id，拿不到 → rels[undefined].Target 崩。
    //   扫描所有 sheet XML，找把 URI 映射到 relationships 的 xmlns 前缀（比如
    //   xmlns:relationships="…/relationships"），把前缀:id / 前缀:embed 全量替换成 r:
    const sheetPaths: string[] = []
    zip.forEach((p) => {
      if (/^xl\/worksheets\/sheet\d+\.xml$/.test(p)) sheetPaths.push(p)
    })
    for (const path of sheetPaths) {
      let xml = await zip.file(path)!.async('text')
      // 找所有 xmlns:前缀="...relationships" 的前缀（排除 r）
      const prefixes = new Set<string>()
      const nsRe = /xmlns:([a-zA-Z][\w-]*)="[^"]*\/relationships"/g
      let nm: RegExpExecArray | null
      while ((nm = nsRe.exec(xml)) !== null) {
        if (nm[1] !== 'r') prefixes.add(nm[1])
      }
      if (prefixes.size === 0) continue
      let localTouched = false
      prefixes.forEach((px) => {
        // 把 `px:attr=` 替换成 `r:attr=`（id / embed / link 等）
        const attrRe = new RegExp(`\\b${px}:([a-zA-Z][\\w-]*)\\s*=`, 'g')
        const next = xml.replace(attrRe, 'r:$1=')
        if (next !== xml) {
          xml = next
          localTouched = true
        }
      })
      // 保证根元素有 xmlns:r（常规 sheet 都有，保险起见兜一下）
      const rootM = /<worksheet\b([^>]*)>/.exec(xml)
      if (rootM && !/\bxmlns:r\s*=/.test(rootM[1])) {
        xml = xml.replace(
          rootM[0],
          `<worksheet${rootM[1]} xmlns:r="${REL_NS}">`
        )
        localTouched = true
      }
      if (localTouched) {
        zip.file(path, xml)
        touched = true
      }
    }

    // ── 规范化 3：把 WPS/钉钉 的非标准批注(comments)/vmlDrawing 规整成 ExcelJS 认的
    //   命名 + 相对路径，否则崩 "Cannot read properties of undefined (reading 'comments')"。
    //   原因：ExcelJS 读批注用正则 /xl\/(comments\d+)\.xml/（只认直接挂在 xl/ 下的
    //   xl/commentsN.xml），vmlDrawing 用 /xl\/drawings\/(vmlDrawing\d+)\.vml/；而 WPS
    //   导出成 xl/comments/comment1.xml + xl/drawings/commentsDrawing1.vml（子目录 +
    //   comment 单数命名），worksheet rels 的 Target 还写成绝对路径(/xl/...)。批注 part
    //   加载不到 → reconcile 时 options.comments[rel.Target] 为 undefined → 读 .comments
    //   抛错。这里把批注/vml 文件重命名成标准名 + rels Target 改成相对标准路径。
    const COMMENTS_REL_TYPE = `${REL_NS}/comments`
    const VML_REL_TYPE = `${REL_NS}/vmlDrawing`
    // worksheet rels 的 Target → zip 内实际 entry 路径(worksheet 在 xl/worksheets/ 下)
    const resolveWsTarget = (target: string): string => {
      if (target.startsWith('/')) return target.replace(/^\//, '')
      const out: string[] = []
      for (const seg of `xl/worksheets/${target}`.split('/')) {
        if (seg === '..') out.pop()
        else if (seg && seg !== '.') out.push(seg)
      }
      return out.join('/')
    }
    // 已占用的标准编号(避免重命名撞到已有的标准批注/vml)
    const usedCommentNum = new Set<number>()
    const usedVmlNum = new Set<number>()
    zip.forEach((p) => {
      const cm = /^xl\/comments(\d+)\.xml$/.exec(p)
      if (cm) usedCommentNum.add(Number(cm[1]))
      const vm = /^xl\/drawings\/vmlDrawing(\d+)\.vml$/.exec(p)
      if (vm) usedVmlNum.add(Number(vm[1]))
    })
    const nextFreeNum = (used: Set<number>): number => {
      let n = 1
      while (used.has(n)) n += 1
      used.add(n)
      return n
    }
    const renameOps: Array<{ from: string; to: string }> = []
    const wsRelsPaths: string[] = []
    zip.forEach((p) => {
      if (/^xl\/worksheets\/_rels\/sheet\d+\.xml\.rels$/.test(p)) {
        wsRelsPaths.push(p)
      }
    })
    for (const relsPath of wsRelsPaths) {
      const relsXml = await zip.file(relsPath)!.async('text')
      const nextRels = relsXml.replace(/<Relationship\b[^>]*?\/>/g, (tag) => {
        const typeM = /\bType="([^"]*)"/.exec(tag)
        const targetM = /\bTarget="([^"]*)"/.exec(tag)
        if (!typeM || !targetM) return tag
        const relType = typeM[1]
        const target = targetM[1]
        const entry = resolveWsTarget(target)
        if (relType === COMMENTS_REL_TYPE) {
          if (!/^xl\/comments\d+\.xml$/.test(entry) && zip.file(entry)) {
            const n = nextFreeNum(usedCommentNum)
            renameOps.push({ from: entry, to: `xl/comments${n}.xml` })
            return tag.replace(targetM[0], `Target="../comments${n}.xml"`)
          }
          if (target.startsWith('/') && /^xl\/comments\d+\.xml$/.test(entry)) {
            return tag.replace(
              targetM[0],
              `Target="../${entry.replace(/^xl\//, '')}"`
            )
          }
        } else if (relType === VML_REL_TYPE) {
          if (
            !/^xl\/drawings\/vmlDrawing\d+\.vml$/.test(entry) &&
            zip.file(entry)
          ) {
            const n = nextFreeNum(usedVmlNum)
            renameOps.push({
              from: entry,
              to: `xl/drawings/vmlDrawing${n}.vml`
            })
            return tag.replace(
              targetM[0],
              `Target="../drawings/vmlDrawing${n}.vml"`
            )
          }
          if (
            target.startsWith('/') &&
            /^xl\/drawings\/vmlDrawing\d+\.vml$/.test(entry)
          ) {
            return tag.replace(
              targetM[0],
              `Target="../drawings/${entry.replace(/^xl\/drawings\//, '')}"`
            )
          }
        }
        return tag
      })
      if (nextRels !== relsXml) {
        zip.file(relsPath, nextRels)
        touched = true
      }
    }
    // 重命名 zip 条目(复制到新名 + 删旧)。放在 rels 改写之后统一做，避免改 zip 影响遍历。
    for (const op of renameOps) {
      const f = zip.file(op.from)
      if (f) {
        const data = await f.async('uint8array')
        zip.file(op.to, data)
        zip.remove(op.from)
        touched = true
      }
    }

    if (!touched) return buffer
    return await zip.generateAsync({ type: 'arraybuffer' })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[xlsx normalize] failed', e)
    return buffer
  }
}

/**
 * 兜底降级：当 xlsx.load 依然抛错（通常是 drawings 相关解析崩溃），
 * 从 zip 里剥掉 xl/drawings/* 和相关 rels 条目，再 load 一次。
 * 丢图片保表格，总比整张白屏强。
 */
async function loadXlsxWithoutDrawings(
  buffer: ArrayBuffer
): Promise<ExcelJS.Workbook> {
  const zip = await JSZip.loadAsync(buffer)
  // 删除所有 drawings 和 media 文件
  const toRemove: string[] = []
  zip.forEach((p) => {
    if (/^xl\/drawings\//.test(p) || /^xl\/media\//.test(p)) {
      toRemove.push(p)
    }
  })
  toRemove.forEach((p) => zip.remove(p))
  // 清理 worksheet rels 里对 drawing 的引用
  const sheetRelPaths: string[] = []
  zip.forEach((p) => {
    if (/^xl\/worksheets\/_rels\/sheet\d+\.xml\.rels$/.test(p)) {
      sheetRelPaths.push(p)
    }
  })
  for (const p of sheetRelPaths) {
    const xml = await zip.file(p)!.async('text')
    const patched = xml.replace(
      /<Relationship\b[^>]*Type="[^"]*\/drawing"[^>]*\/?>(?:<\/Relationship>)?/g,
      ''
    )
    zip.file(p, patched)
  }
  // 清理 sheet XML 里的 <drawing r:id="..."/>
  const sheetPaths: string[] = []
  zip.forEach((p) => {
    if (/^xl\/worksheets\/sheet\d+\.xml$/.test(p)) sheetPaths.push(p)
  })
  for (const p of sheetPaths) {
    const xml = await zip.file(p)!.async('text')
    const patched = xml.replace(/<drawing\b[^>]*\/?>(?:<\/drawing>)?/g, '')
    zip.file(p, patched)
  }
  // 清理 [Content_Types].xml 里的 drawing 条目
  const ctPath = '[Content_Types].xml'
  const ctFile = zip.file(ctPath)
  if (ctFile) {
    const ct = await ctFile.async('text')
    const patched = ct.replace(
      /<Override\b[^>]*PartName="\/xl\/drawings\/[^"]+"[^>]*\/>/g,
      ''
    )
    zip.file(ctPath, patched)
  }
  const stripped = await zip.generateAsync({ type: 'arraybuffer' })
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(stripped)
  return wb
}

async function parseImageRotations(
  buffer: ArrayBuffer
): Promise<ImageRotations> {
  const rotations: ImageRotations = new Map()
  try {
    const zip = await JSZip.loadAsync(buffer)

    // 1. 读 workbook.xml + workbook.xml.rels，建立 sheet 顺序 → rId
    //    (这里不严格区分 sheet index，统一按 drawing 里的 from.col/row 作 key)
    //    简化：遍历所有 drawing*.xml，把每张图的 (tlCol, tlRow) → rotDeg 存下。
    //    这样同文件里如果有图在不同 sheet 相同位置会冲突，但我们文档场景一般一个 sheet，
    //    冲突概率低。后面有需要再做 sheet 映射。

    const drawingFiles: string[] = []
    zip.forEach((relPath) => {
      if (/^xl\/drawings\/drawing\d+\.xml$/.test(relPath))
        drawingFiles.push(relPath)
    })

    for (const path of drawingFiles) {
      const xmlStr = await zip.file(path)!.async('text')
      const doc = new DOMParser().parseFromString(xmlStr, 'application/xml')
      const anchors: Element[] = [
        ...Array.from(doc.getElementsByTagNameNS(DRAWING_NS, 'oneCellAnchor')),
        ...Array.from(doc.getElementsByTagNameNS(DRAWING_NS, 'twoCellAnchor')),
        ...Array.from(doc.getElementsByTagNameNS(DRAWING_NS, 'absoluteAnchor'))
      ]
      for (const anchor of anchors) {
        // 只关心含图片（<xdr:pic>）的 anchor
        const pics = anchor.getElementsByTagNameNS(DRAWING_NS, 'pic')
        if (pics.length === 0) continue

        // 起点 col/row（oneCell / twoCell 才有）
        const fromEl = anchor.getElementsByTagNameNS(DRAWING_NS, 'from')[0]
        if (!fromEl) continue
        const colText = fromEl.getElementsByTagNameNS(DRAWING_NS, 'col')[0]
          ?.textContent
        const rowText = fromEl.getElementsByTagNameNS(DRAWING_NS, 'row')[0]
          ?.textContent
        if (colText == null || rowText == null) continue
        const col = parseInt(colText, 10)
        const row = parseInt(rowText, 10)

        // 读 xfrm：rot (1/60000 度) / flipH / flipV
        const xfrm = pics[0].getElementsByTagNameNS(MAIN_NS, 'xfrm')[0]
        if (!xfrm) continue
        const rotAttr = xfrm.getAttribute('rot')
        const rotDeg = rotAttr ? parseInt(rotAttr, 10) / 60000 : 0
        const flipH = xfrm.getAttribute('flipH') === '1'
        const flipV = xfrm.getAttribute('flipV') === '1'
        if (!rotDeg && !flipH && !flipV) continue
        rotations.set(`${col}-${row}`, { rotDeg, flipH, flipV })
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[xlsx rotations] parse failed:', e)
  }
  return rotations
}

/**
 * WPS 2020+ 内嵌图片 (cellimages.xml) 解析.
 *
 * 跟传统 floating drawing 不同, WPS 内嵌图片:
 *   - 图片清单在 `xl/cellimages.xml` (不在 `xl/drawings/`)
 *   - 单元格用公式 `=DISPIMG("ID_xxx", N)` 引用 (不是 anchor)
 *   - ExcelJS getImages() 拿不到, 必须手动解析
 *
 * 返回 Map<imageId, dataURL>, 渲染时 renderCellValue 检测 DISPIMG 公式查表.
 */
type CellImagesMap = Map<string, string>

async function parseCellImages(buffer: ArrayBuffer): Promise<CellImagesMap> {
  const result: CellImagesMap = new Map()
  try {
    const zip = await JSZip.loadAsync(buffer)
    const cellImagesXml = zip.file('xl/cellimages.xml')
    const cellImagesRels = zip.file('xl/_rels/cellimages.xml.rels')
    if (!cellImagesXml || !cellImagesRels) return result

    // 1) rels: rId → media path (相对 xl/)
    const relsXml = await cellImagesRels.async('text')
    const relsDoc = new DOMParser().parseFromString(relsXml, 'application/xml')
    const rIdToTarget = new Map<string, string>()
    Array.from(relsDoc.getElementsByTagName('Relationship')).forEach((rel) => {
      const id = rel.getAttribute('Id')
      const target = rel.getAttribute('Target')
      if (id && target) rIdToTarget.set(id, target)
    })

    // 2) cellimages.xml: 每个 cellImage 拿 imageId (cNvPr.name) + rId (blip.r:embed)
    const xmlStr = await cellImagesXml.async('text')
    const doc = new DOMParser().parseFromString(xmlStr, 'application/xml')
    const REL_NS =
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
    const cellImageNodes = Array.from(
      doc.getElementsByTagNameNS('*', 'cellImage')
    )
    // 缓存 media path → dataURL, 同张图被多 cell 引用时复用
    const mediaCache = new Map<string, string>()

    for (const node of cellImageNodes) {
      const cNvPr = node.getElementsByTagNameNS(DRAWING_NS, 'cNvPr')[0]
      const imageId = cNvPr?.getAttribute('name')
      if (!imageId) continue
      const blip = node.getElementsByTagNameNS(MAIN_NS, 'blip')[0]
      const rId = blip?.getAttributeNS(REL_NS, 'embed')
      if (!rId) continue
      const target = rIdToTarget.get(rId)
      if (!target) continue

      // target 通常是 "media/image1.png"; 绝对路径 "../media/..." 兼容
      const mediaPath = target.startsWith('/')
        ? target.slice(1)
        : target.startsWith('../')
          ? `xl/${target.slice(3)}`
          : `xl/${target}`

      let dataUrl = mediaCache.get(mediaPath)
      if (!dataUrl) {
        const mediaFile = zip.file(mediaPath)
        if (!mediaFile) continue
        const arrBuf = await mediaFile.async('arraybuffer')
        const ext = (mediaPath.split('.').pop() || 'png').toLowerCase()
        const mime =
          ext === 'jpg' || ext === 'jpeg'
            ? 'image/jpeg'
            : ext === 'gif'
              ? 'image/gif'
              : ext === 'bmp'
                ? 'image/bmp'
                : ext === 'webp'
                  ? 'image/webp'
                  : 'image/png'
        dataUrl = `data:${mime};base64,${bufferToBase64(arrBuf)}`
        mediaCache.set(mediaPath, dataUrl)
      }
      result.set(imageId, dataUrl)
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[xlsx cellimages] parse failed:', e)
  }
  return result
}

function renderSheet(
  sheet: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook,
  idx: number,
  rotations: ImageRotations = new Map(),
  cellImages: CellImagesMap = new Map(),
  colSizing?: ColSizing
): string {
  const parts: string[] = []
  const sheetName = escapeHtml(sheet.name)

  parts.push(
    `<div class="aiClerk-xlsx-sheet" data-sheet-idx="${idx}" style="${
      idx === 0 ? '' : 'display:none'
    }">`
  )

  // ─── 合并单元格
  const skipCells = new Set<string>()
  const spanMap = new Map<string, { rowspan: number; colspan: number }>()
  const mergesRaw = (sheet as any).model?.merges as string[] | undefined
  if (Array.isArray(mergesRaw)) {
    mergesRaw.forEach((mergeRef) => {
      const [start, end] = mergeRef.split(':')
      if (!start || !end) return
      const sCol = colLetterToNum(start.match(/[A-Z]+/)?.[0] || '')
      const sRow = parseInt(start.match(/\d+/)?.[0] || '0', 10)
      const eCol = colLetterToNum(end.match(/[A-Z]+/)?.[0] || '')
      const eRow = parseInt(end.match(/\d+/)?.[0] || '0', 10)
      if (!sCol || !sRow || !eCol || !eRow) return
      spanMap.set(`${sRow}-${sCol}`, {
        rowspan: eRow - sRow + 1,
        colspan: eCol - sCol + 1
      })
      for (let r = sRow; r <= eRow; r++) {
        for (let c = sCol; c <= eCol; c++) {
          if (r === sRow && c === sCol) continue
          skipCells.add(`${r}-${c}`)
        }
      }
    })
  }

  // ─── 图片（浮动图片 / 老式 drawing）
  // ExcelJS getImages() 返回的 range.tl/br 里：
  //   nativeCol / nativeRow : 0 索引单元格
  //   nativeColOff / nativeRowOff : 在该单元格内的 EMU 偏移（1 px = 9525 EMU @96DPI）
  // 浮点 col/row 是 ExcelJS 估算，不可靠（两端值可能都等于 N.9999 导致宽度算出来 0）。
  //
  // 图片 absolute 定位在 tl 单元格里，位置 = (tlColOff, tlRowOff)（px），
  // 尺寸 = tl 列剩余 + 中间整列 + br 列前缀（行同理）。
  const imagesByCell = new Map<string, string[]>()
  try {
    const images = (sheet as any).getImages?.() as
      | Array<{
          imageId: string | number
          range: {
            tl: { col: number; row: number }
            br: { col: number; row: number }
          }
        }>
      | undefined
    if (Array.isArray(images)) {
      const EMU_PER_PX = 9525 // 1px @96DPI = 9525 EMU
      images.forEach((imgInfo: any) => {
        const tl = imgInfo.range?.tl
        const br = imgInfo.range?.br
        // oneCellAnchor：只有 tl + ext（{width, height} px），没有 br
        const ext0 = imgInfo.range?.ext as
          | { width?: number; height?: number }
          | undefined
        if (!tl) return
        if (!br && !ext0) return

        // 优先用 native*（文件里 EMU 精确值），没有再退回 col/row 浮点
        const tlColIdx =
          typeof tl.nativeCol === 'number'
            ? tl.nativeCol
            : Math.floor(tl.col || 0)
        const tlRowIdx =
          typeof tl.nativeRow === 'number'
            ? tl.nativeRow
            : Math.floor(tl.row || 0)

        const tlColOffPx = (tl.nativeColOff || 0) / EMU_PER_PX
        const tlRowOffPx = (tl.nativeRowOff || 0) / EMU_PER_PX

        const tlC = tlColIdx + 1
        const tlR = tlRowIdx + 1

        const media = (workbook as any).getImage?.(imgInfo.imageId) as
          | { buffer?: ArrayBuffer | Uint8Array; extension?: string }
          | undefined
        if (!media || !media.buffer) return
        const b64 = bufferToBase64(media.buffer)
        const ext = media.extension || 'png'

        let widthPx: number
        let heightPx: number

        if (br) {
          // twoCellAnchor：用 br - tl 计算尺寸
          const brColIdx =
            typeof br.nativeCol === 'number'
              ? br.nativeCol
              : Math.floor(br.col || 0)
          const brRowIdx =
            typeof br.nativeRow === 'number'
              ? br.nativeRow
              : Math.floor(br.row || 0)
          const brColOffPx = (br.nativeColOff || 0) / EMU_PER_PX
          const brRowOffPx = (br.nativeRowOff || 0) / EMU_PER_PX
          const brC = brColIdx + 1
          const brR = brRowIdx + 1

          // ── 宽度：tl 列剩余 + 中间整列 + br 列前缀
          if (tlColIdx === brColIdx) {
            widthPx = brColOffPx - tlColOffPx
          } else {
            widthPx = getColumnWidthPx(sheet, tlC) - tlColOffPx
            for (let c = tlC + 1; c < brC; c++)
              widthPx += getColumnWidthPx(sheet, c)
            widthPx += brColOffPx
          }
          // ── 高度
          if (tlRowIdx === brRowIdx) {
            heightPx = brRowOffPx - tlRowOffPx
          } else {
            heightPx = getRowHeightPx(sheet, tlR) - tlRowOffPx
            for (let r = tlR + 1; r < brR; r++)
              heightPx += getRowHeightPx(sheet, r)
            heightPx += brRowOffPx
          }
        } else {
          // oneCellAnchor：尺寸直接来自 ext
          widthPx = ext0!.width || 0
          heightPx = ext0!.height || 0
        }

        widthPx = Math.max(4, Math.round(widthPx))
        heightPx = Math.max(4, Math.round(heightPx))

        // 旋转 + 翻转（xlsx xfrm 定义：rot 绕中心 + flipH/flipV 镜像）
        const xf = rotations.get(`${tlColIdx}-${tlRowIdx}`)
        let transformStyle = ''
        if (xf && (xf.rotDeg || xf.flipH || xf.flipV)) {
          const parts2: string[] = []
          if (xf.rotDeg) parts2.push(`rotate(${xf.rotDeg}deg)`)
          if (xf.flipH) parts2.push('scaleX(-1)')
          if (xf.flipV) parts2.push('scaleY(-1)')
          transformStyle = `transform:${parts2.join(
            ' '
          )};transform-origin:center center;`
        }

        // containImages(照片王预览专用):浮动图改走单元格内嵌图结构(cell-imgbox 绝对
        // 填满 td + object-fit:contain + td overflow:hidden),把图收进单元格、不跨列溢出;
        // 复用已有的 has-cell-img / cell-imgbox 样式。其他调用方仍走原 absolute 精确 px 渲染。
        const imgHtml = colSizing?.containImages
          ? `<div class="aiClerk-xlsx-cell-imgbox">` +
            `<img class="aiClerk-xlsx-cell-img" src="data:image/${ext};base64,${b64}" ` +
            `style="${transformStyle}" alt="" /></div>`
          : `<img class="aiClerk-xlsx-img" src="data:image/${ext};base64,${b64}" ` +
            `style="position:absolute;left:${Math.round(
              tlColOffPx
            )}px;top:${Math.round(tlRowOffPx)}px;` +
            `width:${widthPx}px;height:${heightPx}px;${transformStyle}" alt="" />`

        const key = `${tlR}-${tlC}`
        const arr = imagesByCell.get(key) || []
        arr.push(imgHtml)
        imagesByCell.set(key, arr)
      })
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[xlsx images] error:', e)
  }

  // ─── 列宽（colgroup）
  // 取值优先级：col.width（显式） → sheet.properties.defaultColWidth → 按内容最长字符数估算
  // 必须给 <table> 显式 total width，否则 table-layout:fixed 在某些容器下会
  // 按 shrink-to-fit 缩表，忽略 colgroup。
  let totalWidth = 42 // 行号列
  // 列宽下限:不传 colSizing=0(用文件原始列宽,其他调用方行为不变)。传了就取
  // max(绝对下限 minColWidthPx, 按窗口铺开宽度 fitWidthPx/min(列数,fitMaxCols)):
  //   - 列数 ≤ fitMaxCols:每列 = fitWidthPx/列数 → 铺满预览窗口(少列不再显小)
  //   - 列数 > fitMaxCols:每列 = fitWidthPx/fitMaxCols → 一屏约 fitMaxCols 列,整体横向滚动
  // colPxs 与 totalWidth 同步用钳后值,避免 table 总宽与列宽对不上。
  const colCount = Math.max(sheet.columnCount, 1)
  const fitMaxCols = colSizing?.fitMaxCols ?? 10
  const fitMin =
    colSizing?.fitWidthPx && colSizing.fitWidthPx > 0
      ? Math.floor(colSizing.fitWidthPx / Math.min(colCount, fitMaxCols))
      : 0
  const minColW = Math.max(colSizing?.minColWidthPx ?? 0, fitMin)
  const colPxs: number[] = []
  for (let c = 1; c <= sheet.columnCount; c++) {
    const w = Math.max(getColumnWidthPx(sheet, c), minColW)
    colPxs.push(w)
    totalWidth += w
  }
  parts.push(`<table style="width:${totalWidth}px"><colgroup>`)
  parts.push('<col style="width:42px" />')
  for (const w of colPxs) {
    parts.push(`<col style="width:${w}px" />`)
  }
  parts.push('</colgroup>')

  // ─── 列号头
  parts.push('<thead><tr><th class="aiClerk-xlsx-corner"></th>')
  for (let c = 1; c <= sheet.columnCount; c++) {
    parts.push(
      `<th class="aiClerk-xlsx-colhead" data-colhead="${c}">${getColumnLetter(
        c
      )}</th>`
    )
  }
  parts.push('</tr></thead><tbody>')

  // image cell 最小行高 (px). 横屏照片 16:9 fit 列宽后只剩 ~56px 高,
  // 强制 row ≥ 这个值留给 cell 上下 padding, 视觉不挤. 跟后端 batch_fill
  // IMG_MIN_ROW_HEIGHT_PT (60pt ≈ 80px) 同口径.
  const IMG_CELL_MIN_ROW_HEIGHT_PX = 80

  // ─── 行
  // 行高下限:与列宽对称。不传 colSizing=0(用文件原始行高,其他调用方行为不变)。传了取
  // max(绝对下限 minRowHeightPx, 按窗口铺开高度 fitHeightPx/min(行数,fitMaxRows)):
  //   - 行数 ≤ fitMaxRows:每行 = fitHeightPx/行数 → 铺满预览窗口高度
  //   - 行数 > fitMaxRows:每行 = fitHeightPx/fitMaxRows → 一屏约 fitMaxRows 行,整体纵向滚动
  const rowCount = Math.max(sheet.rowCount, 1)
  const fitMaxRows = colSizing?.fitMaxRows ?? 10
  const rowFitMin =
    colSizing?.fitHeightPx && colSizing.fitHeightPx > 0
      ? Math.floor(colSizing.fitHeightPx / Math.min(rowCount, fitMaxRows))
      : 0
  const minRowH = Math.max(colSizing?.minRowHeightPx ?? 0, rowFitMin)
  for (let r = 1; r <= sheet.rowCount; r++) {
    // 有图片的行，自动撑高到 tl 图片对应 range 的高度;再与窗口铺开下限取大
    let rowHeight = Math.max(getRowHeightPx(sheet, r), minRowH)
    // 该行含 image cell (IMAGE/DISPIMG/floating) 时, 强制 row 高度 ≥ MIN
    let rowHasImg = false
    for (let c = 1; c <= sheet.columnCount; c++) {
      if (imagesByCell.has(`${r}-${c}`)) {
        rowHasImg = true
        break
      }
      const cell = sheet.getCell(r, c)
      if (
        tryExtractImageFormulaUrl(cell) != null ||
        tryExtractDispImgId(cell) != null
      ) {
        rowHasImg = true
        break
      }
    }
    if (rowHasImg && rowHeight < IMG_CELL_MIN_ROW_HEIGHT_PX) {
      rowHeight = IMG_CELL_MIN_ROW_HEIGHT_PX
    }
    parts.push(
      `<tr style="height:${rowHeight}px"><th class="aiClerk-xlsx-rowhead" data-rowhead="${r}">${r}</th>`
    )
    for (let c = 1; c <= sheet.columnCount; c++) {
      const key = `${r}-${c}`
      if (skipCells.has(key)) continue

      const cell = sheet.getCell(r, c)
      const cellRef = `${getColumnLetter(c)}${r}`
      const span = spanMap.get(key)
      const spanAttrs = span
        ? ` rowspan="${span.rowspan}" colspan="${span.colspan}"`
        : ''
      const imgsInCell = imagesByCell.get(key)
      // 两种 class：has-img 浮动图（按 anchor 推算 px 渲染，容器 overflow:visible 允许跨列）；
      //            has-cell-img 单元格内嵌图 (IMAGE() 函数 / WPS DISPIMG)
      //            ——absolute inset:0 居中, 不会撑高行
      // 保持"浮动图优先"语义：一个 cell 只会进一种渲染。
      const hasFloatImg = imgsInCell && imgsInCell.length > 0
      const hasCellImg =
        !hasFloatImg &&
        (tryExtractImageFormulaUrl(cell) != null ||
          tryExtractDispImgId(cell) != null)
      const cellClassName = hasFloatImg
        ? colSizing?.containImages
          ? ' class="has-cell-img"' // 照片王:浮动图收进单元格,套用 has-cell-img 收纳样式
          : ' class="has-img"'
        : hasCellImg
          ? ' class="has-cell-img"'
          : ''
      const styleAttr = buildCellStyle(cell)
      const inner = hasFloatImg
        ? imgsInCell!.join('') + renderCellValue(cell, cellImages)
        : renderCellValue(cell, cellImages)

      parts.push(
        `<td${styleAttr}${spanAttrs}${cellClassName} data-cell-ref="${cellRef}" data-sheet-name="${sheetName}" data-row="${r}" data-col="${c}">${inner}</td>`
      )
    }
    parts.push('</tr>')
  }
  parts.push('</tbody></table></div>')

  return parts.join('')
}

// ─── 列宽：显式 > defaultColWidth > 按内容最长字符数估算
// 缓存每个 sheet 的 raw cols 解析结果 + 像素计算
const _colMetaCache = new WeakMap<
  ExcelJS.Worksheet,
  {
    raw: Array<{
      min: number
      max: number
      width?: number
      customWidth?: boolean
      hidden?: boolean
    }>
    px: Map<number, number>
  }
>()

/** 从 sheet.model.cols（OOXML 原始）读取该列的显式宽度字符数。
 * 这比 sheet.getColumn(n).width 可靠：
 *   - model.cols 是文件里真实存在的列定义
 *   - getColumn(n).width 在列未被 ExcelJS 加载/实例化时是 undefined
 */
function readExplicitColWidthChars(
  sheet: ExcelJS.Worksheet,
  colIdx: number
): number | null {
  let meta = _colMetaCache.get(sheet)
  if (!meta) {
    const raw = ((sheet as any).model?.cols as any[]) || []
    meta = { raw: raw as any, px: new Map() }
    _colMetaCache.set(sheet, meta)
  }
  for (const c of meta.raw) {
    if (
      c &&
      typeof c.min === 'number' &&
      typeof c.max === 'number' &&
      colIdx >= c.min &&
      colIdx <= c.max
    ) {
      if (typeof c.width === 'number' && c.width > 0) return c.width
    }
  }
  // 退路：getColumn(n).width（某些场景 model 里是 undefined 但 Column 对象里有）
  const col = sheet.getColumn(colIdx)
  if (col && typeof col.width === 'number' && col.width > 0) return col.width
  return null
}

// maxDigitWidth 用 canvas measureText 动态测真实字体的数字 0-9 最大宽度.
// 不再硬编码 7 (Calibri 11pt 值) - 之前导致中文字体场景列宽计算窄约 11%,
// 28pt 字号下短文本"上午"/"加班"在标称列宽里超 cell 边界被截断.
//
// .xlsx normal style 在国内项目多是微软雅黑/宋体,maxDigitWidth ≈ 8 px,
// macOS 苹方稍宽 ≈ 8.x.通过浏览器 font fallback 跑一次实测,缓存到模块级.
let _cachedMaxDigitWidthPx: number | null = null
function getMaxDigitWidthPx(): number {
  if (_cachedMaxDigitWidthPx != null) return _cachedMaxDigitWidthPx
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      _cachedMaxDigitWidthPx = 8
      return 8
    }
    // 11pt @ 96 DPI = 14.667 px, .xlsx maxDigitWidth 的标准定义就是 11pt 数字
    ctx.font =
      '14.667px "PingFang SC", "Microsoft YaHei", "微软雅黑", SimSun, Arial, sans-serif'
    let maxW = 0
    for (let i = 0; i <= 9; i++) {
      const m = ctx.measureText(String(i))
      if (m.width > maxW) maxW = m.width
    }
    // 实测值兜底:Calibri 7px / 中文字体 8-9px;偏保守取 floor 让列宽稍紧凑,
    // 但不能低于 7(否则 Calibri 场景反而变窄)
    _cachedMaxDigitWidthPx = Math.max(7, Math.floor(maxW))
    return _cachedMaxDigitWidthPx
  } catch {
    _cachedMaxDigitWidthPx = 8
    return 8
  }
}

function getColumnWidthPx(sheet: ExcelJS.Worksheet, colIdx: number): number {
  let meta = _colMetaCache.get(sheet)
  if (!meta) {
    const raw = ((sheet as any).model?.cols as any[]) || []
    meta = { raw: raw as any, px: new Map() }
    _colMetaCache.set(sheet, meta)
  }
  const cached = meta.px.get(colIdx)
  if (cached != null) return cached

  const explicitChars = readExplicitColWidthChars(sheet, colIdx)
  const defaultChars = (sheet.properties as any)?.defaultColWidth || 8.43

  // 跟 Excel 一致:
  //   - 文件存了显式列宽 → 用真实值 (反映用户在 Excel 里手动调宽的差异)
  //   - 没存 → 用 default (8.43 char ≈ 64 px), 跟 Excel 默认列宽一致.
  //     之前这里按 cell 内容最长字符数估算撑列宽, 导致所有列都接近上限
  //     (一刀切 ~99px), 视觉跟 Excel 不一致 + 抹平了原表的列宽差异语义.
  //     真实 Excel 长内容也是默认 truncate, 让 preview 保持同样行为, 列宽
  //     差异完全由 model.cols 的显式宽度驱动.
  const widthChars = explicitChars != null ? explicitChars : defaultChars

  // ── Excel 官方字符→像素换算公式
  //   pixels = Math.trunc( ((256·width + Math.trunc(128/MDW)) / 256) · MDW )
  // MDW 不再硬编码:动态测真实字体的 maxDigitWidth(中文字体约 8px,Calibri 7px)
  const MDW = getMaxDigitWidthPx()
  const pad = Math.trunc(128 / MDW)
  const px = Math.trunc(((256 * widthChars + pad) / 256) * MDW)
  meta.px.set(colIdx, px)
  return px
}

// ─── 行高：row.height (pt) > sheet.properties.defaultRowHeight > 20px 兜底
function getRowHeightPx(sheet: ExcelJS.Worksheet, rowIdx: number): number {
  const row = sheet.getRow(rowIdx)
  if (row && typeof row.height === 'number' && row.height > 0) {
    return Math.round(row.height * 1.33) // pt → px
  }
  const defaultPt = (sheet.properties as any)?.defaultRowHeight
  if (typeof defaultPt === 'number' && defaultPt > 0) {
    return Math.round(defaultPt * 1.33)
  }
  return 20
}

// ArrayBuffer/Buffer → base64
function bufferToBase64(buf: ArrayBuffer | Uint8Array | any): string {
  let bytes: Uint8Array
  if (buf instanceof Uint8Array) bytes = buf
  else if (buf && buf.buffer instanceof ArrayBuffer)
    bytes = new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength)
  else bytes = new Uint8Array(buf)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK))
    )
  }
  return btoa(binary)
}

/** 构造单元格 style 属性（背景/字体/边框/对齐/换行） */
function buildCellStyle(cell: ExcelJS.Cell): string {
  const s: string[] = []

  // 背景色
  const bg = extractColor(cell.fill)
  if (bg) s.push(`background-color:${bg}`)

  // 字体
  const font = cell.font
  if (font) {
    const fc = colorToRgb(font.color as any)
    if (fc) s.push(`color:${fc}`)
    if (font.bold) s.push('font-weight:bold')
    if (font.italic) s.push('font-style:italic')
    if (font.underline) s.push('text-decoration:underline')
    // ExcelJS cell.font.size 单位是 pt, 不是 px. Excel default 11pt = 14.66px.
    // 直接当 px 用会让所有字号统一矮一档. ×1.333 标准 pt→px 换算.
    if (font.size)
      s.push(`font-size:${Math.round(Number(font.size) * 1.333)}px`)
    if (font.name)
      s.push(`font-family:'${escapeHtml(String(font.name))}', sans-serif`)
  }

  // 对齐
  const al = cell.alignment
  if (al?.horizontal) s.push(`text-align:${al.horizontal}`)
  if (al?.vertical) {
    const v =
      al.vertical === 'middle'
        ? 'middle'
        : al.vertical === 'bottom'
          ? 'bottom'
          : 'top'
    s.push(`vertical-align:${v}`)
  }
  if (al?.wrapText) s.push('white-space:pre-wrap', 'word-break:break-word')
  else s.push('white-space:nowrap')

  // 边框
  const b: any = cell.border
  if (b) {
    const bs = (side: any): string => {
      if (!side || !side.style) return ''
      // 默认边色用浅灰 (WPS/Excel 默认网格线视觉), 不用 #000 太重
      const color = colorToRgb(side.color) || '#d4d4d8'
      const styleName = String(side.style)
      const width =
        styleName === 'thick' ? '2px' : styleName === 'medium' ? '1.5px' : '1px'
      const style =
        styleName === 'dashed' || styleName === 'dotted' ? styleName : 'solid'
      return `${width} ${style} ${color}`
    }
    if (b.top) s.push(`border-top:${bs(b.top)}`)
    if (b.bottom) s.push(`border-bottom:${bs(b.bottom)}`)
    if (b.left) s.push(`border-left:${bs(b.left)}`)
    if (b.right) s.push(`border-right:${bs(b.right)}`)
  }

  return s.length ? ` style="${s.join(';')}"` : ''
}

/** ExcelJS Fill → CSS color */
function extractColor(fill: ExcelJS.Fill | undefined): string | null {
  if (!fill) return null
  if (fill.type === 'pattern' && fill.pattern === 'solid') {
    return colorToRgb(fill.fgColor as any) || colorToRgb(fill.bgColor as any)
  }
  if (fill.type === 'pattern' && fill.fgColor) {
    return colorToRgb(fill.fgColor as any)
  }
  if (fill.type === 'gradient') {
    // 简化处理：取第一个 stop
    const stops = (fill as any).gradient?.stops as any[] | undefined
    if (stops && stops[0]) return colorToRgb(stops[0].color)
  }
  return null
}

/** ExcelJS Color → CSS color string */
function colorToRgb(color: any): string | null {
  if (!color) return null
  // ARGB: "FF7C3AED" → "#7C3AED"
  if (color.argb) {
    const argb = String(color.argb)
    if (argb.length === 8) return `#${argb.slice(2)}`
    return `#${argb}`
  }
  if (color.rgb) return `#${String(color.rgb).slice(-6)}`
  // theme 颜色 + tint，简化处理：用对应 theme 色，忽略 tint
  if (typeof color.theme === 'number') {
    const themeColor = THEME_COLORS[color.theme] || '#000000'
    if (typeof color.tint === 'number') {
      return applyTint(themeColor, color.tint)
    }
    return themeColor
  }
  return null
}

// Office 默认主题色（与新建工作簿一致）
const THEME_COLORS = [
  '#FFFFFF', // 0 background1
  '#000000', // 1 text1
  '#E7E6E6', // 2 background2
  '#44546A', // 3 text2
  '#5B9BD5', // 4 accent1
  '#ED7D31', // 5 accent2
  '#A5A5A5', // 6 accent3
  '#FFC000', // 7 accent4
  '#4472C4', // 8 accent5
  '#70AD47' // 9 accent6
]

/** 应用 tint：正值变亮，负值变暗 */
function applyTint(hex: string, tint: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const apply = (v: number) => {
    const out = tint < 0 ? v * (1 + tint) : v + (255 - v) * tint
    return Math.max(0, Math.min(255, Math.round(out)))
      .toString(16)
      .padStart(2, '0')
  }
  return `#${apply(r)}${apply(g)}${apply(b)}`
}

/** 渲染单元格值（处理富文本/公式/日期等） */
// IMAGE() 函数：Excel 365 的单元格内链接式图片
// 形式：=IMAGE(url, [alt], [sizing], [height], [width])
// url 支持单引号/双引号，函数名大小写任意
const IMAGE_FORMULA_RE = /^\s*(?:_xlfn\.)?IMAGE\s*\(\s*["']([^"']+)["']/i

// DISPIMG() 函数：WPS 2020+ 单元格内嵌图片
// 形式：=DISPIMG("ID_xxx_xxx", N)  ← 第 2 参数是 sizing mode (1-4), 渲染忽略
// 兼容 _xlfn.DISPIMG 前缀和大小写
const DISPIMG_FORMULA_RE =
  /^\s*(?:_xlfn\.)?(?:_xlfn\.|_xlws\.)*DISPIMG\s*\(\s*["']([^"']+)["']/i

function tryExtractImageFormulaUrl(cell: ExcelJS.Cell): string | null {
  const v: any = cell.value
  const formula =
    (v &&
      typeof v === 'object' &&
      typeof v.formula === 'string' &&
      v.formula) ||
    (typeof (cell as any).formula === 'string' ? (cell as any).formula : '')
  if (!formula) return null
  const m = IMAGE_FORMULA_RE.exec(formula)
  return m ? m[1] : null
}

/** 检测 cell 是否含 DISPIMG 公式 + 返回 imageId; 否则 null */
function tryExtractDispImgId(cell: ExcelJS.Cell): string | null {
  const v: any = cell.value
  const formula =
    (v &&
      typeof v === 'object' &&
      typeof v.formula === 'string' &&
      v.formula) ||
    (typeof (cell as any).formula === 'string' ? (cell as any).formula : '')
  if (!formula) return null
  const m = DISPIMG_FORMULA_RE.exec(formula)
  return m ? m[1] : null
}

function renderCellValue(
  cell: ExcelJS.Cell,
  cellImages: CellImagesMap = new Map()
): string {
  const v: any = cell.value
  if (v == null) return ''

  // IMAGE() 函数 —— 直接按外链渲染 <img>
  // 注意：必须放在「公式取 result」分支之前，否则会被截到 result='#VALUE!'
  const imgUrl = tryExtractImageFormulaUrl(cell)
  if (imgUrl) {
    // 外包一层 position:absolute inset:0 的盒子，让 img 脱离 tr 高度计算，
    // 不会被 img 的 intrinsic 尺寸撑高行。
    return (
      `<div class="aiClerk-xlsx-cell-imgbox">` +
      `<img class="aiClerk-xlsx-cell-img" src="${escapeHtml(
        imgUrl
      )}" alt="" loading="lazy" />` +
      `</div>`
    )
  }

  // DISPIMG() 函数 —— WPS 内嵌图片, 查 cellImages 表拿到 dataURL
  // 同样必须放在「公式取 result」分支之前 (公式 result 通常是 '#VALUE!' / 占位)
  const dispImgId = tryExtractDispImgId(cell)
  if (dispImgId) {
    const dataUrl = cellImages.get(dispImgId)
    if (dataUrl) {
      return (
        `<div class="aiClerk-xlsx-cell-imgbox">` +
        `<img class="aiClerk-xlsx-cell-img" src="${dataUrl}" alt="" loading="lazy" />` +
        `</div>`
      )
    }
    // imageId 没在 cellimages.xml 里找到 → 居中占位 (后端漏注入 cellimages 时常见)
    return (
      `<div class="aiClerk-xlsx-cell-imgbox aiClerk-xlsx-cell-img-missing" ` +
      `title="图片缺失: ${escapeHtml(dispImgId)}">` +
      `<span class="aiClerk-xlsx-cell-img-missing__icon">🖼</span>` +
      `</div>`
    )
  }

  // 富文本
  if (typeof v === 'object' && Array.isArray(v.richText)) {
    return v.richText.map((rt: any) => escapeHtml(String(rt.text))).join('')
  }
  // 公式：取计算结果
  if (typeof v === 'object' && 'result' in v) {
    return escapeHtml(String(v.result ?? ''))
  }
  // 日期
  if (v instanceof Date) {
    return escapeHtml(formatDate(v))
  }
  // 错误值
  if (typeof v === 'object' && 'error' in v) {
    return escapeHtml(String(v.error))
  }
  // 超链接
  if (typeof v === 'object' && 'hyperlink' in v) {
    const text = escapeHtml(String(v.text || v.hyperlink))
    return `<a href="${escapeHtml(
      String(v.hyperlink)
    )}" target="_blank" rel="noopener">${text}</a>`
  }
  return escapeHtml(String(v))
}

function formatDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function colLetterToNum(letter: string): number {
  let n = 0
  for (let i = 0; i < letter.length; i++) {
    n = n * 26 + (letter.charCodeAt(i) - 64)
  }
  return n
}

function getColumnLetter(col: number): string {
  let s = ''
  let n = col
  while (n > 0) {
    s = String.fromCharCode(65 + ((n - 1) % 26)) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * 提取 cell 内文本,保留段落 / <br> 换行。
 * 用 `td.textContent` 会把 <p>段落1</p><p>段落2</p> 拼成 "段落1段落2"
 * (textContent 不在块级元素间插换行),双击带入 popover 时就丢了原文换行。
 * 这里克隆 td,把 <br> 换成 \n,把 <p>/<div>/<li> 等块级元素结尾追加 \n,
 * 再读 textContent 拿到带换行的纯文本。
 */
function getCellTextPreservingBreaks(td: Element | null | undefined): string {
  if (!td) return ''
  const clone = td.cloneNode(true) as HTMLElement
  clone.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n')
  })
  clone.querySelectorAll('p, div, li').forEach((el) => {
    el.appendChild(el.ownerDocument!.createTextNode('\n'))
  })
  return (clone.textContent || '')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * 解析 CSS `content` 表达式 + 解析其中的 `counter(name [, style])` 调用。
 * docx-preview 给 numPr 段落生成的 ::before content 形如:
 *   `"" counter(aiClerk-docx-num-1-0, decimal) "、"`
 * 浏览器 getComputedStyle.content 返回的是**未解析**的字面串(counter()
 * 不会被 resolve 成实际数字),要自己 tokenize + 替换。
 */
function resolveCssContentExpression(
  raw: string,
  counters: Map<string, number>
): string {
  let out = ''
  let i = 0
  const n = raw.length
  while (i < n) {
    // 跳空白
    while (i < n && /\s/.test(raw[i])) i++
    if (i >= n) break
    const ch = raw[i]
    if (ch === '"' || ch === "'") {
      // 引号字符串(支持 \ 转义)
      const quote = ch
      i++
      let s = ''
      while (i < n && raw[i] !== quote) {
        if (raw[i] === '\\' && i + 1 < n) {
          s += raw[i + 1]
          i += 2
        } else {
          s += raw[i++]
        }
      }
      i++ // skip closing quote
      out += s
    } else if (raw.substr(i, 8) === 'counter(') {
      i += 8
      const end = raw.indexOf(')', i)
      if (end < 0) break
      const args = raw.slice(i, end).split(',')
      const name = args[0].trim()
      // 第 2 参 (style) 暂时只支持 decimal/默认,其他 list-style-type 简化处理
      out += String(counters.get(name) ?? 0)
      i = end + 1
    } else if (raw.substr(i, 9) === 'counters(') {
      i += 9
      const end = raw.indexOf(')', i)
      if (end < 0) break
      const args = raw.slice(i, end).split(',')
      const name = args[0].trim()
      out += String(counters.get(name) ?? 0)
      i = end + 1
    } else {
      i++
    }
  }
  return out
}

/** 把 "name1 1 name2 2" 这种 counter-* 值串解析成 (name, num) 对 */
function parseCounterDecl(
  raw: string,
  defaultVal: number
): Array<[string, number]> {
  if (!raw || raw === 'none') return []
  const parts = raw.split(/\s+/).filter(Boolean)
  const result: Array<[string, number]> = []
  let k = 0
  while (k < parts.length) {
    const name = parts[k++]
    let val = defaultVal
    if (k < parts.length && /^-?\d+$/.test(parts[k])) {
      val = parseInt(parts[k++], 10)
    }
    if (name) result.push([name, val])
  }
  return result
}

/**
 * 全文档单次 tree walk 把每个元素的 counter 值算出来,记录 td 子树内每个
 * 元素的 ::before 解析后字串(prefixMap)。
 *
 * 关键点:docx-preview 把 counter-increment 应用在 `<selector>:before` 而不
 * 是元素自身上(见 vendor 源码 styleToString(`${n}:before`, {"counter-increment":...}))。
 * 所以必须用 getComputedStyle(el, '::before').counterIncrement 读;
 * 读元素本身的 cs.counterIncrement 会拿到 'none',counter 永远停在 0,
 * 用户看到的就是 "0、位置信息..." "0、疑点情况..." 全是 0。
 *
 * counter-reset 在 rootSelector 上,通常体现在 doc 顶层元素的 cs.counterReset 上。
 * 为保险起见,元素自身 + ::before 两侧 reset / increment 都读一遍。
 */
function buildCounterPrefixMap(td: Element): Map<Element, string> {
  const doc = td.ownerDocument
  if (!doc) return new Map()
  const win = doc.defaultView
  if (!win) return new Map()
  const counters = new Map<string, number>()
  const prefixMap = new Map<Element, string>()
  const root: Element = doc.body || doc.documentElement
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT)
  let cur: Node | null = walker.currentNode
  while (cur) {
    const el = cur as Element
    let cs: CSSStyleDeclaration | null = null
    let csBefore: CSSStyleDeclaration | null = null
    try {
      cs = win.getComputedStyle(el)
    } catch {
      cs = null
    }
    try {
      csBefore = win.getComputedStyle(el, '::before')
    } catch {
      csBefore = null
    }

    // counter-reset:元素自身 + ::before 都读;reset 一次性 set 给定值
    const resets = [
      ...parseCounterDecl(cs?.counterReset || '', 0),
      ...parseCounterDecl(csBefore?.counterReset || '', 0)
    ]
    for (const [name, val] of resets) counters.set(name, val)

    // counter-increment:元素 + ::before;累加(默认 +1)
    const incrs = [
      ...parseCounterDecl(cs?.counterIncrement || '', 1),
      ...parseCounterDecl(csBefore?.counterIncrement || '', 1)
    ]
    for (const [name, val] of incrs) {
      counters.set(name, (counters.get(name) ?? 0) + val)
    }

    // td 子树内才记 prefix
    if (td.contains(el)) {
      const raw = csBefore?.content || ''
      if (raw && raw !== 'none' && raw !== 'normal' && raw !== '""') {
        const resolved = resolveCssContentExpression(raw, counters)
        if (resolved) prefixMap.set(el, resolved)
      }
    }
    cur = walker.nextNode()
  }
  return prefixMap
}

/**
 * 仅给 "修改"弹窗 textarea 初始值用——按 SelectedContent 反查文档里实际渲染
 * 的 cell DOM,把 docx-preview 通过 CSS counter + ::before 注入的自动编号
 * (1、/2、/3、 numPr 列表)拼回纯文本。
 *
 * 用户报 62_海洋协管巡查事项的 cell:预览展示 "1、位置信息... / 2、疑点情况...
 * / 3、初步判断... / 4、处理方式..." 但双击修改弹窗只显示裸文字。
 *
 * 为什么不在 displayText 阶段处理:displayText 还被 AI quote / formatSelectedAsQuote
 * 等场景共用,那些地方期望"原始 cell 文本"而不是"渲染态文本"。这里单独
 * 抽一个仅供弹窗用的接口,各管各场景。
 *
 * 找不到 DOM (DOM 已经被 SSE 重渲过 / cell 实际不存在) 时返回 null,调用方
 * 应回退到 firstCell.displayText 保证不空。
 */
export function getRenderedCellTextForEditModal(
  content: SelectedContent
): string | null {
  const firstCell = content.rangePayload?.cells[0]?.[0]
  const ref = firstCell?.ref || content.cellRef
  if (!ref) return null

  // docx 多 table 场景:同一 ref 在不同 table 都有,必须用 sheet=表格${tableId}
  // 缩 scope;xlsx 单表全局唯一 ref
  let scopeSelector = ''
  if (content.docType === 'docx') {
    const sheet = content.sheetName || content.rangePayload?.sheet
    const m = sheet ? /^表格(.+)$/.exec(sheet) : null
    if (m && m[1]) scopeSelector = `table[data-docx-table-id="${m[1]}"] `
  }
  const safeRef =
    typeof (window as any).CSS?.escape === 'function'
      ? (window as any).CSS.escape(ref)
      : ref.replace(/"/g, '\\"')
  const td = document.querySelector(
    `${scopeSelector}[data-cell-ref="${safeRef}"]`
  ) as HTMLElement | null
  if (!td) return null

  // 计算每个内部元素的 ::before resolved 字符串(把 counter() 替换成实际数字)
  const prefixMap = buildCounterPrefixMap(td)

  // clone + 注入 prefix + 走原 getCellTextPreservingBreaks 的换行规则
  const origAll = Array.from(td.querySelectorAll('*'))
  const clone = td.cloneNode(true) as HTMLElement
  const cloneAll = Array.from(clone.querySelectorAll('*'))
  cloneAll.forEach((el, idx) => {
    const origEl = origAll[idx]
    const prefix = prefixMap.get(origEl)
    if (prefix && el.ownerDocument) {
      el.insertBefore(el.ownerDocument.createTextNode(prefix), el.firstChild)
    }
  })
  clone.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n')
  })
  clone.querySelectorAll('p, div, li').forEach((el) => {
    el.appendChild(el.ownerDocument!.createTextNode('\n'))
  })
  return (clone.textContent || '')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** 把 SelectedContent 格式化为给 AI 的引用文本 */
/** 把 SelectedContent 序列化成发给 AI 的 quote 文本。
 * 对于富 rangePayload：输出结构化 Markdown-like 块，让 AI 一眼看懂选了什么列、什么行、哪里为空。
 */
export function formatSelectedAsQuote(content: SelectedContent): string {
  const docxSingleCell = getDocxSingleSelectedCell(content)
  if (docxSingleCell) {
    const value = docxSingleCell.isEmpty
      ? '(空)'
      : docxSingleCell.displayText || ''
    return `${humanizeRef(docxSingleCell.ref)}：${value}`
  }
  if (content.rangePayload) {
    return formatRangePayloadAsQuote(content.rangePayload)
  }
  if (content.source === 'cell-click' && content.cellRef) {
    return `${humanizeRef(content.cellRef)}：${content.text}`
  }
  // 非表格图片选区:只序列化 ViewPath,不带 src。
  // 之前同时带 src + path 是冗余 — agent 拿到 body/p[N]/img[M] 就能在 docx
  // XML tree 里直接定位 <w:drawing>,base64 data URL 又长又重(100KB+ 一张),
  // 塞进 chat history 既挤显示也浪费 token / API 体积。
  // imageSrcs 还留在 in-memory state 里给前端的 "is-image-selected" 高亮 +
  // 查看原图 lightbox 用,但不进 quote 文本。
  if (content.source === 'image-selection') {
    const n = content.imageSrcs?.length ?? 0
    const paths = (content.imageViewPaths || []).filter(Boolean)
    if (n === 0) return '[图片]'
    if (n === 1) {
      return paths[0] ? `图片 ${paths[0]}` : '图片'
    }
    if (paths.length === n) {
      return [`图片(${n} 张):`, ...paths.map((p) => `- ${p}`)].join('\n')
    }
    // path 数对不上 src 数(罕见,某张 img 没在标准段落里),用占位
    const lines = Array.from({ length: n }, (_, i) => {
      const p = paths[i]
      return `- ${p || `#${i + 1}`}`
    })
    return [`图片(${n} 张):`, ...lines].join('\n')
  }
  return content.text
}

// docx 选区内部 ref 是 0-based RxCy (R0=第1行); xlsx 是 A1。给 AI 的文本一律翻成
// 用户视角 —— 因为 deepseek-v4-flash 实测读不懂 0-based RxCy 的行号 (eval table-selection:
// R3 被当成第3行、R2:R4 被重排成 [1,2,3], 3/3 全错), 改 1-based 后 3/3 修好。
// xlsx 的 A1 实测本就好懂 (3/3) 且 excel_edit 真吃 A1, 不动。

/** 单格 ref 翻人话: RxCy → "第N行第M列"; A1/其他 → 原样 */
function humanizeRef(ref: string): string {
  const m = /^R(\d+)C(\d+)$/.exec((ref || '').trim())
  return m ? `第${Number(m[1]) + 1}行第${Number(m[2]) + 1}列` : ref
}

/** 选区范围翻人话: RxCy → "第A~B行第C~D列"; A1 → 原样 */
function displayRange(range: string): string {
  const m = /^R(\d+)C(\d+):R(\d+)C(\d+)$/.exec((range || '').trim())
  if (!m) return range
  const r1 = Number(m[1]) + 1
  const c1 = Number(m[2]) + 1
  const r2 = Number(m[3]) + 1
  const c2 = Number(m[4]) + 1
  const rp = r1 === r2 ? `第${r1}行` : `第${r1}~${r2}行`
  const cp = c1 === c2 ? `第${c1}列` : `第${c1}~${c2}列`
  return `${rp}${cp}`
}

const CELL_TYPE_ZH: Record<string, string> = {
  image: '图片',
  number: '数字',
  date: '日期',
  boolean: '是否',
  formula: '公式',
  hyperlink: '链接'
}

function formatRangePayloadAsQuote(p: RangePayload): string {
  const isDocx = /^R\d+C\d+:R\d+C\d+$/.test((p.range || '').trim())
  return isDocx
    ? formatDocxRangePayloadAsQuote(p)
    : formatXlsxRangePayloadAsQuote(p)
}

/** docx 选区: 行列号一律用户视角 (第N行第M列), 每行带行号, 列名带类型。 */
function formatDocxRangePayloadAsQuote(p: RangePayload): string {
  const lines: string[] = []
  const m = /^R(\d+)C(\d+):R(\d+)C(\d+)$/.exec(p.range.trim())
  const firstRow1 = m ? Number(m[1]) + 1 : 1
  const cellCount = p.rows * p.cols
  lines.push(
    `[表格选区 · ${p.sheet} · ${displayRange(p.range)} · 共${cellCount}格]`
  )
  // 列头 (列号转 1-based, 带类型)
  const colKeys = Object.keys(p.headers)
  if (colKeys.length) {
    const headerStr = colKeys
      .map((col) => {
        const cm = /^C(\d+)$/.exec(col)
        const colNum = cm ? Number(cm[1]) + 1 : col
        const t = p.columnTypes[col]
        const typeLabel =
          t && t !== 'text' && t !== 'empty' ? `(${CELL_TYPE_ZH[t] ?? t})` : ''
        return `第${colNum}列=${p.headers[col]}${typeLabel}`
      })
      .join(' | ')
    lines.push(`列: ${headerStr}`)
  }
  // 单元格: 每行带行号 (图片→[图片]/[图片 url], 空→(空))
  const fmt = (c: CellSnapshot): string => {
    if (c.imageSrc) {
      return c.imageSrc.startsWith('data:') ? '[图片]' : `[图片 ${c.imageSrc}]`
    }
    return c.isEmpty ? '(空)' : c.displayText || ''
  }
  const rowLine = (row: CellSnapshot[], rowNum1: number) =>
    `第${rowNum1}行: ${row.map(fmt).join(' | ')}`
  if (p.cells.length <= 20) {
    p.cells.forEach((row, i) => lines.push(rowLine(row, firstRow1 + i)))
  } else {
    p.cells
      .slice(0, 10)
      .forEach((row, i) => lines.push(rowLine(row, firstRow1 + i)))
    lines.push(`… (省略 ${p.cells.length - 12} 行)`)
    const tailStart = p.cells.length - 2
    p.cells
      .slice(-2)
      .forEach((row, i) => lines.push(rowLine(row, firstRow1 + tailStart + i)))
  }
  // 空格 (待填), refs 转 1-based
  if (p.emptyRefs.length > 0) {
    const refs = p.emptyRefs.map(humanizeRef)
    const preview =
      refs.length <= 20
        ? refs.join(', ')
        : refs.slice(0, 20).join(', ') + ` … (共 ${refs.length} 个)`
    lines.push(`待填: ${preview}`)
  }
  // 参考样本 (选区上方已填行)
  if (p.sampleRows && p.sampleRows.length > 0) {
    lines.push('参考样本(上方已填):')
    for (const row of p.sampleRows) {
      const rm = row[0]?.ref ? /^R(\d+)C\d+$/.exec(row[0].ref) : null
      const prefix = rm ? `第${Number(rm[1]) + 1}行: ` : '  '
      lines.push(
        prefix +
          row.map((c) => (c.isEmpty ? '(空)' : c.displayText || '')).join(' | ')
      )
    }
  }
  return lines.join('\n')
}

/** xlsx 选区: 保留原 A1 渲染 (A1 实测本就好懂, excel_edit 也吃 A1)。 */
function formatXlsxRangePayloadAsQuote(p: RangePayload): string {
  const lines: string[] = []
  const cellCount = p.rows * p.cols
  lines.push(`[表格选区 · ${p.sheet} · ${p.range} · ${cellCount} 个单元格]`)
  // 列头（带类型推断）
  const colLetters = Object.keys(p.headers)
  if (colLetters.length) {
    const headerStr = colLetters
      .map((col) => {
        const t = p.columnTypes[col]
        const typeLabel = t && t !== 'text' && t !== 'empty' ? `<${t}>` : ''
        return `${col}:${p.headers[col]}${typeLabel}`
      })
      .join(' | ')
    lines.push(`列: ${headerStr}`)
  }
  // 单元格内容（tab 分隔）。图片 cell 特殊处理：外链给 URL，浮动图只给 [图片 ref]
  const fmt = (c: CellSnapshot): string => {
    if (c.imageSrc) {
      return c.imageSrc.startsWith('data:')
        ? `[图片 ${c.ref}]`
        : `[图片 ${c.ref} ${c.imageSrc}]`
    }
    return c.isEmpty ? '(空)' : c.displayText || ''
  }
  if (p.cells.length <= 20) {
    for (const row of p.cells) {
      lines.push(row.map(fmt).join('\t'))
    }
  } else {
    for (const row of p.cells.slice(0, 10)) {
      lines.push(row.map(fmt).join('\t'))
    }
    lines.push(`... (省略 ${p.cells.length - 12} 行)`)
    for (const row of p.cells.slice(-2)) {
      lines.push(row.map(fmt).join('\t'))
    }
  }
  // 空缺位置
  if (p.emptyRefs.length > 0) {
    const preview =
      p.emptyRefs.length <= 20
        ? p.emptyRefs.join(', ')
        : p.emptyRefs.slice(0, 20).join(', ') +
          ` ... (共 ${p.emptyRefs.length} 个)`
    lines.push(`待填: ${preview}`)
  }
  // 样本行（选区上方已填）
  if (p.sampleRows && p.sampleRows.length > 0) {
    lines.push('参考样本:')
    for (const row of p.sampleRows) {
      lines.push(
        '  ' +
          row.map((c) => (c.isEmpty ? '(空)' : c.displayText || '')).join('\t')
      )
    }
  }
  return lines.join('\n')
}

function getDocxSingleSelectedCell(
  content: SelectedContent
): CellSnapshot | null {
  if (content.docType !== 'docx') return null
  if (content.source !== 'cell-click') return null
  const p = content.rangePayload
  if (!p || p.rows !== 1 || p.cols !== 1) return null
  return p.cells[0]?.[0] || null
}

// ─── 选区 payload 构造（核心） ─────────────────────

/** 统计选区内的真实 cell 数量（考虑矩形） */
function countCellsInRange(range: CellRange): number {
  const rows = Math.abs(range.focus.r - range.anchor.r) + 1
  const cols = Math.abs(range.focus.c - range.anchor.c) + 1
  return rows * cols
}

function buildSelectedContentFromPayload(
  payload: RangePayload,
  docType: 'docx' | 'xlsx',
  cellCount: number
): SelectedContent {
  // 推断 cellState,让浮窗按"空/图/文"差异化展示 action
  // - 单 cell: empty / image / text
  // - 多 cell: all-empty / all-image / all-text / mixed(跳过合并占位)
  let cellState: SelectedContent['cellState']
  if (cellCount === 1) {
    const cell = payload.cells[0]?.[0]
    if (cell) {
      if (cell.imageSrc) cellState = 'image'
      else if (cell.isEmpty || (!cell.val && !cell.displayText))
        cellState = 'empty'
      else cellState = 'text'
    }
  } else if (cellCount > 1) {
    let nEmpty = 0
    let nImage = 0
    let nText = 0
    for (const row of payload.cells) {
      for (const c of row) {
        if (c.isMergedPart) continue
        if (c.imageSrc) nImage++
        else if (c.isEmpty || (!c.val && !c.displayText)) nEmpty++
        else nText++
      }
    }
    const total = nEmpty + nImage + nText
    if (total === 0) cellState = 'all-empty'
    else if (nImage === total) cellState = 'all-image'
    else if (nText === total) cellState = 'all-text'
    else if (nEmpty === total) cellState = 'all-empty'
    else cellState = 'mixed'
  }
  return {
    text: summarizeRangePayload(payload, cellCount),
    source: cellCount === 1 ? 'cell-click' : 'cell-range',
    docType,
    cellRef: payload.range,
    sheetName: payload.sheet,
    rangePayload: payload,
    selectionShape: 'cell',
    cellState
  }
}

/** 构造发给 AI 的结构化 payload */
// ─── docx 表格装饰 + payload 构造 ─────────────────────

function getDirectTableRows(table: Element): HTMLTableRowElement[] {
  const rows: HTMLTableRowElement[] = []

  Array.from(table.children).forEach((child) => {
    const tag = child.tagName.toUpperCase()
    if (tag === 'TR') {
      rows.push(child as HTMLTableRowElement)
      return
    }
    if (tag === 'THEAD' || tag === 'TBODY' || tag === 'TFOOT') {
      Array.from(child.children).forEach((row) => {
        if (row.tagName.toUpperCase() === 'TR') {
          rows.push(row as HTMLTableRowElement)
        }
      })
    }
  })

  return rows
}

function getDirectRowCells(row: Element): HTMLElement[] {
  return Array.from(row.children).filter((child) => {
    const tag = child.tagName.toUpperCase()
    return tag === 'TD' || tag === 'TH'
  }) as HTMLElement[]
}

/** 给 docx-preview 渲出的 <table> 打上 data-row/data-col/data-cell-ref/data-sheet-name
 *  属性，复用 xlsx 的拖拽框选 + RangePayload 管线。
 *
 *  合并单元格网格对齐：HTML 里 <tr> 只包含"实际存在的 <td>"，被前面行 rowspan
 *  占位的位置不会出 td。直接 colIdx+1 会错位。正确做法：维护一张"被上方
 *  rowspan 占掉的格子"表，每行游标遇到已占位置就跳过，计算出 td 在网格里的真实
 *  (r, c)；再按 td 自己的 rowspan/colspan 把未来会被占住的格子记下来。
 */
function decorateDocxTables(rootEl: HTMLElement): void {
  const tables = rootEl.querySelectorAll('table')
  tables.forEach((table, tableIdx) => {
    const tableId = tableIdx + 1
    table.setAttribute('data-docx-table-id', String(tableId))
    const rows = getDirectTableRows(table)
    // 被上方 rowspan 占位的格子：Set<"r-c">
    const occupied = new Set<string>()
    rows.forEach((tr, rowIdx) => {
      const r = rowIdx + 1
      const cells = getDirectRowCells(tr)
      let cursor = 1
      cells.forEach((cell) => {
        // 游标跳过被上方 rowspan 占位的格子
        while (occupied.has(`${r}-${cursor}`)) cursor++
        const c = cursor
        const rs = parseInt(cell.getAttribute('rowspan') || '1', 10)
        const cs = parseInt(cell.getAttribute('colspan') || '1', 10)
        cell.setAttribute('data-row', String(r))
        cell.setAttribute('data-col', String(c))
        cell.setAttribute('data-cell-ref', `R${r - 1}C${c - 1}`)
        cell.setAttribute('data-sheet-name', `表格${tableId}`)
        cell.setAttribute('data-docx-table-id', String(tableId))
        // 记录这个 td 会占掉的未来格子（自己除外）
        for (let rr = r; rr < r + rs; rr++) {
          for (let cc = c; cc < c + cs; cc++) {
            if (rr === r && cc === c) continue
            occupied.add(`${rr}-${cc}`)
          }
        }
        cursor += cs
      })
    })
  })
}

/** 从 docx 里选中的 cell range 构造 RangePayload（从 DOM 读，不走 ExcelJS） */
function buildDocxRangePayload(
  container: HTMLElement,
  range: CellRange
): RangePayload | null {
  // 用 range.anchor.tableId 直接定位 table —— 不要用 container.querySelector 去找
  // R1C1，因为多 table 场景 querySelector 总是返回第一个 table 的 cell，导致 payload
  // 取自错误的 table。
  const tableId = range.anchor.tableId
  if (!tableId) return null
  const table = container.querySelector(
    `table[data-docx-table-id="${tableId}"]`
  ) as HTMLElement | null
  if (!table) return null

  const minR = Math.min(range.anchor.r, range.focus.r)
  const maxR = Math.max(range.anchor.r, range.focus.r)
  const minC = Math.min(range.anchor.c, range.focus.c)
  const maxC = Math.max(range.anchor.c, range.focus.c)

  const qCell = (r: number, c: number) =>
    table.querySelector(
      `[data-docx-table-id="${tableId}"][data-row="${r}"][data-col="${c}"]`
    ) as HTMLElement | null

  // docx 表格大量是"标签 + 值"表单，不适合像 xlsx 一样给所有下方单元格强行套首行表头。
  // 只有当用户实际选中首行时，才把首行暴露为 header；否则不生成 header，避免出现
  // "点了 R6C2，引用里却先展示 R1C2=铺路" 这种误导。
  const headerRow = minR === 1 ? 1 : null
  const headers: Record<string, string> = {}
  if (headerRow != null) {
    for (let c = minC; c <= maxC; c++) {
      const cell = qCell(headerRow, c)
      const text = (cell?.textContent || '').trim()
      if (text) headers[`C${c - 1}`] = text
    }
  }

  // cells
  const cells: CellSnapshot[][] = []
  const emptyRefs: string[] = []
  for (let r = minR; r <= maxR; r++) {
    const row: CellSnapshot[] = []
    for (let c = minC; c <= maxC; c++) {
      const td = qCell(r, c)
      const ref = `R${r - 1}C${c - 1}`
      // qCell 拿不到 td 说明该坐标被左/上的合并 master 覆盖了(rowspan/colspan
      // 把这里"吃掉"了,DOM 里就一个 td)。标 isMergedPart,后续 cellState 计算
      // 把它跳掉,合并 cell 才能正确按"逻辑单格"来识别 — 否则用户点 rowspan=5
      // 的图片 cell 会被当成 5 个 cell 的多选区,cellState 算成 mixed,弹不出
      // "换图/删除" action(用户报 213_6_四必查范本.docx)。
      if (!td) {
        row.push({
          ref,
          val: null,
          displayText: '',
          type: 'empty',
          isEmpty: false,
          isMergedPart: true,
          imageSrc: undefined
        })
        continue
      }
      // 用 getCellTextPreservingBreaks 而非 td.textContent — 后者把多 <p> 段落
      // 拼成单串,丢了原文换行,双击带入 popover 时跟原文不一致
      const text = getCellTextPreservingBreaks(td)
      // docx-preview 里的图片不一定是 <img>：
      // - 常见 inline drawing 会是 <img>
      // - VML / shape 图片可能是 <svg><image href=.../></svg>
      //   视觉上有图，但 textContent 为空。只查 <img> 会把它误判成空白。
      const imageSrc = extractDocxCellImageSrc(td)
      const isEmpty = !imageSrc && !text
      if (isEmpty) emptyRefs.push(ref)
      const type: CellDataType = imageSrc ? 'image' : isEmpty ? 'empty' : 'text'
      row.push({
        ref,
        val: text || null,
        displayText: imageSrc ? '[图片]' : text,
        type,
        isEmpty,
        imageSrc
      })
    }
    cells.push(row)
  }

  // 样本行：选区顶部与第 1 行之间的最多 2 行非空行
  const sampleRows: CellSnapshot[][] = []
  if (headerRow != null && minR > headerRow + 1) {
    const sStart = Math.max(headerRow + 1, minR - 2)
    const sEnd = minR - 1
    for (let r = sStart; r <= sEnd && sampleRows.length < 2; r++) {
      if (r === headerRow) continue
      const row: CellSnapshot[] = []
      for (let c = minC; c <= maxC; c++) {
        const td = qCell(r, c)
        const text = getCellTextPreservingBreaks(td)
        row.push({
          ref: `R${r - 1}C${c - 1}`,
          val: text || null,
          displayText: text,
          type: text ? 'text' : 'empty',
          isEmpty: !text
        })
      }
      if (row.some((x) => !x.isEmpty)) sampleRows.push(row)
    }
  }

  // columnTypes：扫全表该列前 50 行看主流类型
  const columnTypes: Record<string, CellDataType> = {}
  for (let c = minC; c <= maxC; c++) {
    const counts: Partial<Record<CellDataType, number>> = {}
    const scanEnd = Math.min(50, getDirectTableRows(table).length || 50)
    for (let r = (headerRow ?? 0) + 1; r <= scanEnd; r++) {
      const td = qCell(r, c)
      if (!td) continue
      const hasImg = !!extractDocxCellImageSrc(td)
      const text = (td.textContent || '').trim()
      let t: CellDataType = 'empty'
      if (hasImg) t = 'image'
      else if (text) t = 'text'
      if (t === 'empty') continue
      counts[t] = (counts[t] || 0) + 1
    }
    const entries = Object.entries(counts) as Array<[CellDataType, number]>
    columnTypes[`C${c - 1}`] =
      entries.length === 0
        ? 'empty'
        : (entries.sort((a, b) => b[1] - a[1])[0][0] as CellDataType)
  }

  return {
    sheet: `表格${tableId}`,
    range: `R${minR - 1}C${minC - 1}:R${maxR - 1}C${maxC - 1}`,
    rows: maxR - minR + 1,
    cols: maxC - minC + 1,
    headers,
    columnTypes,
    cells,
    emptyRefs,
    sampleRows: sampleRows.length > 0 ? sampleRows : undefined
  }
}

function extractDocxCellImageSrc(cell: HTMLElement | null): string | undefined {
  if (!cell) return undefined

  const img = cell.querySelector('img[src]') as HTMLImageElement | null
  if (img?.src) return img.src

  // docx-preview 对部分 VML / shape 图片会输出成 <svg><image href="..."/></svg>
  const svgImage = cell.querySelector('svg image') as SVGImageElement | null
  if (svgImage) {
    const href =
      svgImage.getAttribute('href') ||
      svgImage.getAttribute('xlink:href') ||
      svgImage.getAttributeNS('http://www.w3.org/1999/xlink', 'href')
    if (href) return href
  }

  // 兜底：有些图片元素拿不到可读 URL，但 DOM 里已经明确存在视觉图片。
  // 返回 data: 哨兵值，让后续逻辑把它当成图片而不是空白。
  if (cell.querySelector('svg, canvas')) {
    return 'data:,'
  }

  return undefined
}

/** 强制把 docx 页面里的表格对齐到内容区中心线。
 *  这里不再区分 jc / tblInd / 超宽表，统一按最终 DOM 几何位置纠偏。
 */
/**
 * docx 设了"首页不同(titlePg)"时,docx-preview 只在首页渲染 header / footer,
 * 后续 page 没有。前端强制把首页的 header / footer 模板克隆到所有缺失的 page,
 * 让每页都展示页眉/页脚。
 *
 * 兼容场景:
 *   - 文档没设页眉/页脚:headerTpl/footerTpl 为 null,直接 return,不影响渲染
 *   - 文档设了 first + default 双套头:每页都有 header,克隆步骤会全部跳过
 *   - 只首页有 header(常见):把首页模板补到其他 page
 */
function ensureHeaderFooterOnEveryDocxPage(rootEl: HTMLElement): void {
  const pages = Array.from(
    rootEl.querySelectorAll<HTMLElement>('section.aiClerk-docx')
  )
  if (pages.length <= 1) return

  // 拿首个有 header / footer 的 page 作为模板
  let headerTpl: HTMLElement | null = null
  let footerTpl: HTMLElement | null = null
  for (const page of pages) {
    if (!headerTpl) {
      const h = page.querySelector<HTMLElement>(':scope > header')
      if (h) headerTpl = h
    }
    if (!footerTpl) {
      const f = page.querySelector<HTMLElement>(':scope > footer')
      if (f) footerTpl = f
    }
    if (headerTpl && footerTpl) break
  }

  if (!headerTpl && !footerTpl) return

  pages.forEach((page) => {
    if (headerTpl && !page.querySelector(':scope > header')) {
      const clone = headerTpl.cloneNode(true) as HTMLElement
      clone.setAttribute('data-cloned-header', '1')
      // header 必须在 page 顶部
      page.insertBefore(clone, page.firstChild)
    }
    if (footerTpl && !page.querySelector(':scope > footer')) {
      const clone = footerTpl.cloneNode(true) as HTMLElement
      clone.setAttribute('data-cloned-footer', '1')
      // footer 放 page 末尾
      page.appendChild(clone)
    }
  })
}

/**
 * 把表格单元格里的 anchored/floating drawing 拉回单元格内联流。
 *
 * 背景:
 *   WPS Office 生成的 docx(尤其 .doc 转 .docx)经常把单元格里的图片做成
 *   <wp:anchor> 而非 <wp:inline>,并带 positionH/V 偏移。docx-preview 直接
 *   把这些偏移翻译成 position:absolute + left/top,结果就是:照片虽然 DOM
 *   上还在原本的 td 里,但视觉上偏到隔壁 td 甚至下一行,出现 "不同 cell
 *   照片交错堆叠"(用户报 232_粤西光伏报告.docx)。
 *
 * 修复:
 *   遍历每个 td 里的 img 上溯到 td 之间的所有祖先 wrapper,把 absolute/fixed
 *   定位 + left/top/right/bottom + float 全部清掉,改成 relative 让图片按
 *   inline-block 自然布局在 cell 内。略微背离 Word 的精确像素位置,但是表
 *   单填报场景下"图片归属哪个 cell"比"像素级精确"重要。
 */
function normalizeAnchoredImagesInCells(rootEl: HTMLElement): void {
  const imgs = rootEl.querySelectorAll<HTMLImageElement>('td img, th img')
  imgs.forEach((img) => {
    const td = img.closest('td, th') as HTMLElement | null
    if (!td) return
    let cur: HTMLElement | null = img.parentElement
    while (cur && cur !== td) {
      const s = cur.style
      if (s.position === 'absolute' || s.position === 'fixed') {
        s.position = 'relative'
        s.left = ''
        s.top = ''
        s.right = ''
        s.bottom = ''
        s.zIndex = ''
      }
      if (s.float) s.float = ''
      cur = cur.parentElement
    }
  })
}

function forceCenterDocxTables(rootEl: HTMLElement, zoom: number): void {
  void zoom
  const tables = rootEl.querySelectorAll<HTMLElement>('table')
  tables.forEach((table) => {
    const page = table.closest('section.aiClerk-docx') as HTMLElement | null
    if (!page) return

    table.style.position = 'relative'
    table.style.left = '0px'
    table.style.transform = 'translateX(0px)'

    const pageWidth = page.clientWidth
    const tableWidth = table.offsetWidth
    const currentLeft = table.offsetLeft
    if (pageWidth <= 1 || tableWidth <= 1) return

    const desiredLeft = (pageWidth - tableWidth) / 2
    const delta = desiredLeft - currentLeft
    if (Math.abs(delta) <= 1) return

    table.style.left = `${delta}px`
  })
}

/**
 * 探测文件实际格式(不信扩展名):
 *   - 'docx' / 'xlsx': PK\x03\x04 ZIP 签名(OOXML 都是 zip 包)
 *   - 'doc-legacy':    D0CF11E0A1B11AE1 CFB 签名(Word 97-2003 老二进制格式)
 *   - 'unknown':       其他
 *
 * 用户场景:文件扩展名是 .docx 但实际是老 .doc 二进制格式(WPS / Word 另存为
 * 时勾错选项常见)。docx-preview 直接报"Can't find end of central directory"
 * 用户看不懂,前端先识别给出清晰提示。
 */
function detectDocFormat(
  buffer: ArrayBuffer
): 'ooxml' | 'doc-legacy' | 'unknown' {
  if (buffer.byteLength < 8) return 'unknown'
  const head = new Uint8Array(buffer, 0, 8)
  // PK\x03\x04
  if (
    head[0] === 0x50 &&
    head[1] === 0x4b &&
    head[2] === 0x03 &&
    head[3] === 0x04
  ) {
    return 'ooxml'
  }
  // D0 CF 11 E0 A1 B1 1A E1
  if (
    head[0] === 0xd0 &&
    head[1] === 0xcf &&
    head[2] === 0x11 &&
    head[3] === 0xe0 &&
    head[4] === 0xa1 &&
    head[5] === 0xb1 &&
    head[6] === 0x1a &&
    head[7] === 0xe1
  ) {
    return 'doc-legacy'
  }
  return 'unknown'
}

/** docx 撑满预览栏时给容器预留的安全边距 (避免触发横向滚动条).
 *  16px 是经验值:右侧 1px 内容也不裁,同时不会有明显的留白. */
const DOCX_FIT_SAFE_MARGIN_PX = 16

/**
 * 测 docx 第一页的自然宽度,跟 doc-content 可见宽度比,如果页面比容器窄就
 * 算个 fit zoom 让页面撑满预览栏.
 *
 * 触发时机:渲染管线最后一步,所有 DOM 操作 (mergeContinuous / pageBreaks /
 * centerTables / decorate 等) 都已经按原 baseZoom 完成.改 zoom 只是 CSS 视觉
 * 等比缩放,DOM 结构不动,所以不会反过来触发分页 / 拆表逻辑.
 *
 * 自然宽度计算:
 *   section 在 .aiClerk-docx-wrapper 下面,wrapper 上挂了 zoom: baseZoom.
 *   section.getBoundingClientRect().width 是 zoom 之后的渲染宽度.
 *   除掉 baseZoom 就是 100% 自然宽度,存到 ref 给后续 ResizeObserver 复用.
 *
 * 撑满策略:
 *   - 容器宽 - 16px (安全边距) > 自然宽 * baseZoom → 算 fit zoom = containerW/natural
 *   - 否则 → 保持 baseZoom (页面已 >= 预览栏宽,改了反而会触发横向滚动)
 */
function measureAndApplyDocxFit(
  container: HTMLElement,
  baseZoom: number,
  naturalWidthRef: { current: number | null },
  setFitZoom: (z: number | null) => void
): void {
  const firstSection = container.querySelector<HTMLElement>(
    'section.aiClerk-docx'
  )
  if (!firstSection) return
  const sectionRect = firstSection.getBoundingClientRect()
  if (sectionRect.width <= 0) return
  // section 已应用 baseZoom,反算回自然宽度
  const naturalWidth = sectionRect.width / baseZoom
  if (!Number.isFinite(naturalWidth) || naturalWidth <= 0) return
  naturalWidthRef.current = naturalWidth

  const containerWidth = container.clientWidth - DOCX_FIT_SAFE_MARGIN_PX
  if (containerWidth <= 0) return
  // 自然宽 * baseZoom = 当前已渲染宽度. 比可见宽度还窄就撑满.
  if (sectionRect.width >= containerWidth) return
  const fitZoom = containerWidth / naturalWidth
  if (fitZoom <= baseZoom) return
  setFitZoom(fitZoom)
}

/**
 * 提前解析 document.xml,定位"continuous section"在 sectPr 列表里的索引。
 *
 * 背景:
 *   OOXML 的 <w:sectPr w:type="continuous"/> 表示该 section 不另起新页,而是
 *   续在前一个 section 同一物理页上。docx-preview 不识别这个 flag——它无脑
 *   按 sectPr 数量渲染等量 <section> 元素,把本应同页的 section 变成多个独立
 *   <section>。表现:单页 docx 在我们预览里被渲成多页(用户报: 79_白鑫.docx)。
 *
 * 修复:
 *   渲染前用 JSZip 解析 word/document.xml,记下哪些 sectPr 是 continuous,
 *   渲染完后用 mergeContinuousSections 把对应 <section> 合并回前一个。
 *
 * 注意:
 *   docx-preview 已经把 jszip 注入到 window.JSZip,这里直接读全局即可,不需
 *   要额外打包成本。
 *   首个 sectPr 即便标了 continuous 也无意义(没有"前一个"section),忽略它。
 *   非 ZIP 文件(如老 .doc)直接返回 [],由上游格式预检负责报错。
 */
async function readContinuousSectionIndices(
  buffer: ArrayBuffer
): Promise<number[]> {
  if (detectDocFormat(buffer) !== 'ooxml') return []
  const JSZip = (window as any).JSZip
  if (!JSZip) return []
  try {
    // buffer.slice() 拷贝一份,避免下游 renderAsync 复用同一份 ArrayBuffer 时
    // 被 jszip 内部读取标记成 detached
    const zip = await JSZip.loadAsync(buffer.slice(0))
    const docFile = zip.file('word/document.xml')
    if (!docFile) return []
    const xml: string = await docFile.async('string')
    const sectPrRegex = /<w:sectPr\b[^>]*>([\s\S]*?)<\/w:sectPr>/g
    const continuousIndices: number[] = []
    let idx = 0
    let match: RegExpExecArray | null
    // eslint-disable-next-line no-cond-assign
    while ((match = sectPrRegex.exec(xml)) !== null) {
      const inner = match[1] || ''
      // <w:type w:val="continuous"/> 不允许首个 section
      if (idx > 0 && /<w:type[^>]*w:val="continuous"/i.test(inner)) {
        continuousIndices.push(idx)
      }
      idx++
    }
    return continuousIndices
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[aiClerk] readContinuousSectionIndices failed', e)
    return []
  }
}

/**
 * 把指定索引上的 section 合并到上一个 section:把它 <article> 的子节点搬到前
 * 一个 section 的 <article>,然后删除自己。从后往前处理,避免索引漂移。
 *
 * header / footer 用前一个 section 的(由 ensureHeaderFooterOnEveryDocxPage
 * 兜底克隆,这里不复制)。
 */
function mergeContinuousSections(
  rootEl: HTMLElement,
  continuousIndices: number[]
): void {
  if (continuousIndices.length === 0) return
  const sections = Array.from(
    rootEl.querySelectorAll<HTMLElement>('section.aiClerk-docx')
  )
  for (let i = continuousIndices.length - 1; i >= 0; i--) {
    const sectionIdx = continuousIndices[i]
    if (sectionIdx <= 0 || sectionIdx >= sections.length) continue
    const current = sections[sectionIdx]
    const previous = sections[sectionIdx - 1]
    const currentArticle = current.querySelector(
      ':scope > article'
    ) as HTMLElement | null
    const previousArticle = previous.querySelector(
      ':scope > article'
    ) as HTMLElement | null
    if (!currentArticle || !previousArticle) continue
    while (currentArticle.firstChild) {
      previousArticle.appendChild(currentArticle.firstChild)
    }
    current.remove()
  }
}

/** 有限分页兜底：
 *  1. 顶层表整体能放下一页时，整表下移
 *  2. 顶层大表本身跨页时，按 tr 边界把溢出的行移到下一页
 *
 *  仍不处理：
 *  - 单行本身就超过一页高度
 *  - 嵌套表内部再拆分
 */
/**
 * 还原 Word hRule="exact" 固定行高被 docx-preview 撑大的 row.
 *
 * 背景: Word XML 里 `<w:trHeight w:hRule="exact" w:val="N"/>` 表示该 row 固定 N
 * twips 高度, 内容超出截断. docx-preview 不实现 hRule (CSS 没等价语义), 看到
 * 含大照片 (img.height > 设置行高) 就把 row inline `height` 改成 img.height +
 * padding (e.g. word 设 170pt, 实际渲染 292pt). 累计撑大几行后 article 远超 A4
 * → 分页算法按渲染高度切, 拆出来的页数比 Word 真实多.
 *
 * 修法: 用同表里其他同模式 row 的 majority height (出现频次最高的值) 作 truth,
 * outlier row 还原到 majority + 给该 row 内 img/svg-image 加 max-height + object-fit
 * 让照片在 row 内等比缩放显示完整 (不被截断, 也不再撑大 row).
 *
 * 假设前提: 同一表里, photo 行高 word 模板都同一值 (常见照片留证 / 台账类模板符
 * 合). 不符合时 (e.g. word 设计本来就有 row 高度差异) 不动 — majority 检测自然
 * 把不同高度的 row 都当 majority 各自一类, 不会误改.
 */
function normalizeExactRowHeights(rootEl: HTMLElement): void {
  const OUTLIER_RATIO = 1.15 // 比 photo 真值高 15% 以上算被撑大

  const tables = rootEl.querySelectorAll<HTMLTableElement>(
    'section.aiClerk-docx article > table'
  )

  tables.forEach((table) => {
    const rows = Array.from(
      table.querySelectorAll<HTMLTableRowElement>(':scope > tr')
    )
    if (rows.length < 2) return

    // 1) 收集每个 row inline height (pt).
    const rowMeta: { row: HTMLTableRowElement; pt: number }[] = []
    rows.forEach((row) => {
      const h = row.style.height
      if (!h) return
      const m = h.match(/^([\d.]+)pt$/)
      if (!m) return
      rowMeta.push({ row, pt: parseFloat(m[1]) })
    })
    if (rowMeta.length < 2) return

    // 2) photo + label 模式假设:
    //    - label 真值 = 全表 row height 最小值 (label 永远比 photo 矮)
    //    - photo 真值 = "大于 label × 1.5" 的 row height 中的 最小值 (撑大只增不减)
    //    这样**完全不依赖 row 内是否有 img** — 空 placeholder photo row (无 img,
    //    但 row 高 = word 设的 photo 高) 也被正确识别为 photo 组成员.
    const heights = rowMeta.map((r) => r.pt)
    const labelTruth = Math.min(...heights)
    const photoHeights = heights.filter((h) => h > labelTruth * 1.5)
    const photoTruth =
      photoHeights.length > 0 ? Math.min(...photoHeights) : null
    if (photoTruth === null) return // 全表只一种 row, 没 photo / label 区分

    // 3) outlier: row.height > photoTruth × OUTLIER_RATIO → 还原到 photoTruth.
    //    label row 通常不被撑大 (label cell 内无 img), 不管它.
    rowMeta.forEach(({ row, pt }) => {
      if (pt <= photoTruth * OUTLIER_RATIO) return
      const closest = photoTruth
      row.style.height = `${closest}pt`
      // td 内的 img / svg image: 限制 max-height 在还原后 row 高度内, 等比缩放.
      const visuals = row.querySelectorAll<HTMLElement>('img, svg image')
      visuals.forEach((el) => {
        el.style.maxHeight = `${closest}pt`
        if (el.tagName.toLowerCase() === 'img') {
          ;(el as HTMLImageElement).style.objectFit = 'contain'
          // height inline 可能是 docx-preview 自己写的撑大值, 改成 auto 让 width 主导,
          // max-height 兜底; 同时 width 限 100% 防超出 cell.
          el.style.height = 'auto'
          el.style.width = 'auto'
          el.style.maxWidth = '100%'
        }
      })
      // img 外层 wrapper div (docx-preview 给 anchored image 套的 inline-block
      // 定位 div) 同样收回高度, 防 div 高度大于 row 时撑大 row 兜底失败.
      const wrappers = row.querySelectorAll<HTMLElement>(
        'td div[style*="height"]'
      )
      wrappers.forEach((d) => {
        const dh = d.style.height
        const dm = dh.match(/^([\d.]+)pt$/)
        if (dm && parseFloat(dm[1]) > closest) {
          d.style.height = 'auto'
          d.style.maxHeight = `${closest}pt`
        }
      })
    })
  })
}

/**
 * 找一个顶层表格"翻页时应一起带走"的起点:表格 + 它前面紧邻的标题 / 页码等引
 * 导段落。WPS / Word 自然分页会让"标题跟着它的表格"一起翻页,我们补上同样的版
 * 面规则(这类模板的 keepNext 常被导出成 val="0",指望不上)。
 *
 * 排除规则:向前收集到的最靠前段落,若紧跟在上一个表格后面,通常是上一个表格的
 * 尾随段落(如"采样/校核/审核"签名行),应留在上一页,起点回退一格。
 */
function findTableLeadingBlockStart(table: HTMLElement): HTMLElement {
  // 只把表格紧邻的「短段落」(标题 / 页码行) 当引导块一起带走;遇到长段落(正文)
  // 就停 —— 否则在「正文 + 表格」混排的文档里会把整段正文误推到下一页。
  const LEADING_TEXT_MAX = 30
  let start: HTMLElement = table
  let prev = start.previousElementSibling as HTMLElement | null
  while (prev && prev.tagName !== 'TABLE') {
    const text = (prev.textContent || '').replace(/\s+/g, '')
    if (text.length > LEADING_TEXT_MAX) break
    start = prev
    prev = start.previousElementSibling as HTMLElement | null
  }
  // start 紧跟上一个表格 → 它是上个表格的尾段(签名等),留在上一页,起点回退一格
  if (
    start !== table &&
    (start.previousElementSibling as HTMLElement | null)?.tagName === 'TABLE'
  ) {
    const next = start.nextElementSibling as HTMLElement | null
    if (next) start = next
  }
  return start
}

function applyWholeTablePageBreaks(rootEl: HTMLElement): void {
  // 每 pass 内层循环遇到 moved 就 break, 单 pass 最多拆 1 次. 一份 N 页的 docx
  // 至少需要 N-1 passes; 提到 30 容下 ~30 页内的文档拆完, 比之前的 12 (= 仅够
  // 1 节 docx 自然分 12 页, 实际填表类表格文档常超出).
  const MAX_PASSES = 30
  const TOLERANCE_PX = 8
  const PT_TO_PX = 96 / 72
  // 仅在 section 没有 inline 尺寸时兜底（A4 纵向，pt）。正常情况下纸张宽高都从
  // docx-preview 写在 section.style 上的真实值读取，横向 / 非 A4 纸张同样适用。
  const PAGE_HEIGHT_PT_DEFAULT = 841.9
  const PAGE_WIDTH_PT_DEFAULT = 595.3

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const pages = Array.from(
      rootEl.querySelectorAll<HTMLElement>('section.aiClerk-docx')
    )
    let moved = false

    for (const page of pages) {
      const article = getDirectPageChild(page, 'ARTICLE')
      if (!article) continue

      const footer = getDirectPageChild(page, 'FOOTER')
      const pageRect = page.getBoundingClientRect()
      const articleRect = article.getBoundingClientRect()
      const footerRect = footer?.getBoundingClientRect()

      // 这一页纸张的真实尺寸：docx-preview 把纸宽 / 纸高（pt）写在 section 的
      // inline style（width / min-height）上。横向纸张这里就是横向的宽高，所以
      // 后面所有分页边界都按真实纸张动态计算，不再假设固定 A4 纵向。
      const sectMinHeightPt =
        parseFloat(page.style.minHeight) || PAGE_HEIGHT_PT_DEFAULT
      const sectWidthPt = parseFloat(page.style.width) || PAGE_WIDTH_PT_DEFAULT
      // 纸张真实宽度（pt 换算成无缩放像素）与当前渲染宽度对比，得到实际缩放系数
      // （含父级 CSS zoom 和撑满 fit 缩放），再用它把纸高从 pt 换成屏幕真实像素。
      const sectWidthPxAtNoZoom = sectWidthPt * PT_TO_PX
      const zoomFactor =
        sectWidthPxAtNoZoom > 0 ? pageRect.width / sectWidthPxAtNoZoom : 1
      const pageHeightPxZoomed = sectMinHeightPt * PT_TO_PX * zoomFactor
      // 这一页纸张的理论下沿：内容撑大 section 时 pageRect.bottom 会跟着内容飘走，
      // 单看它判不出"撑大了"；用真实纸高算一个固定下沿当硬边界。
      const pageBottomPx = pageRect.top + pageHeightPxZoomed
      const sectStretched = pageRect.height > pageHeightPxZoomed + TOLERANCE_PX

      // 早期返回判定:
      // - 没撑大（内容塞进一页范围）→ 信任 docx-preview 布局, 用 pageRect.bottom
      //   兜底, 不强拆（防"1 页被误判成 2 页, 表格独占第 2 页"的旧 bug）
      // - 撑大了（docx-preview 没拆但内容超一页）→ 用 pageBottomPx 作硬边界,
      //   触发后续 split 逻辑按真实纸高切多页
      const realPageBottom = footerRect
        ? footerRect.top
        : sectStretched
          ? pageBottomPx
          : pageRect.bottom
      if (articleRect.bottom <= realPageBottom + TOLERANCE_PX) continue

      // 走到这里 = article 真的溢出一页。算"内容该停在哪"的标尺 contentBottom：
      // - 有页脚 → 页脚顶就是内容下界
      // - 无页脚 → 真实纸张下沿减去下页边距。docx-preview 把页边距设成 article
      //   的 padding，读 padding-bottom 并按 zoomFactor 换成屏幕像素。
      //   getComputedStyle 返回的 padding 是未经 CSS zoom 缩放的值，× zoomFactor
      //   才能和 getBoundingClientRect（已缩放）对齐。
      const articlePadBottomPx =
        (parseFloat(getComputedStyle(article).paddingBottom) || 0) * zoomFactor
      const contentLimitByPage = pageBottomPx - articlePadBottomPx
      const contentBottom = footerRect
        ? Math.min(footerRect.top, contentLimitByPage)
        : contentLimitByPage
      const fullPageContentHeight = contentBottom - articleRect.top

      const children = Array.from(article.children) as HTMLElement[]
      for (const child of children) {
        if (child.tagName !== 'TABLE') continue

        const childRect = child.getBoundingClientRect()
        if (childRect.bottom <= contentBottom + TOLERANCE_PX) continue

        if (
          splitOverflowingTopLevelTable(
            child as HTMLTableElement,
            page,
            article,
            contentBottom,
            fullPageContentHeight,
            TOLERANCE_PX
          )
        ) {
          moved = true
          break
        }

        if (child === article.firstElementChild) continue
        if (childRect.height >= fullPageContentHeight - TOLERANCE_PX) continue

        const nextArticle = ensureNextPageArticle(page, article)
        // 连同表格前面的标题 / 页码引导段落一起翻页,避免标题被孤立在上一页。
        // 若带上后会把整页搬空(起点已是页首),退回只搬表格本身。
        const moveStart = findTableLeadingBlockStart(child)
        moveNodeAndFollowingSiblings(
          moveStart === article.firstElementChild ? child : moveStart,
          nextArticle
        )
        moved = true
        break
      }

      if (moved) break
    }

    if (!moved) break
  }
}

function splitOverflowingTopLevelTable(
  table: HTMLTableElement,
  page: HTMLElement,
  article: HTMLElement,
  contentBottom: number,
  fullPageContentHeight: number,
  tolerancePx: number
): boolean {
  // docx-preview 渲出的 <table> 不带 <tbody>, TR 直接挂 table — table.tBodies[0]
  // 在这种结构下是 undefined, 旧实现取不到行直接 return false (= 拆分 silently 失败).
  // fallback: 没 tbody 时改用 :scope > tr 拿顶层 TR, 兼容两种结构.
  const body = table.tBodies[0]
  const rows = body
    ? Array.from(body.rows)
    : Array.from(table.querySelectorAll<HTMLTableRowElement>(':scope > tr'))
  if (rows.length <= 1) return false

  let splitIndex = -1
  for (let idx = 0; idx < rows.length; idx++) {
    const rowRect = rows[idx].getBoundingClientRect()
    if (rowRect.bottom > contentBottom + tolerancePx) {
      splitIndex = idx
      break
    }
  }

  if (splitIndex < 0) return false

  const overflowStartRow = rows[splitIndex]
  const rowRect = overflowStartRow.getBoundingClientRect()
  const rowHeight = rowRect.height

  // 当前页已经没有任何一行能放下：优先整表挪页，让下一轮再继续拆。
  if (splitIndex === 0) {
    if (table !== article.firstElementChild) {
      const nextArticle = ensureNextPageArticle(page, article)
      // 同 applyWholeTablePageBreaks:把表格的前置标题 / 页码一起带到下一页
      const moveStart = findTableLeadingBlockStart(table)
      moveNodeAndFollowingSiblings(
        moveStart === article.firstElementChild ? table : moveStart,
        nextArticle
      )
      return true
    }
    return false
  }

  // 单行本身就超过一页内容高度，前端兜底无法安全拆。
  if (rowHeight >= fullPageContentHeight - tolerancePx) return false

  const nextArticle = ensureNextPageArticle(page, article)
  const nextTable = cloneTableShell(table)
  const nextBody = ensureTableBody(nextTable)

  rows.slice(splitIndex).forEach((row) => {
    nextBody.appendChild(row)
  })

  const trailingNodes = collectFollowingSiblings(table)
  const anchor = nextArticle.firstChild
  nextArticle.insertBefore(nextTable, anchor)
  trailingNodes.forEach((node) => {
    nextArticle.insertBefore(node, anchor)
  })
  return true
}

function getDirectPageChild(
  page: HTMLElement,
  tagName: 'ARTICLE' | 'HEADER' | 'FOOTER'
): HTMLElement | null {
  const child = Array.from(page.children).find(
    (node) => node.tagName === tagName
  )
  return (child as HTMLElement | undefined) || null
}

function ensureNextPageArticle(
  page: HTMLElement,
  articleTemplate: HTMLElement
): HTMLElement {
  let nextPage = page.nextElementSibling as HTMLElement | null
  while (nextPage && !nextPage.matches('section.aiClerk-docx')) {
    nextPage = nextPage.nextElementSibling as HTMLElement | null
  }

  if (!nextPage) {
    nextPage = page.cloneNode(false) as HTMLElement
    Array.from(page.children).forEach((child) => {
      if (child.tagName === 'HEADER' || child.tagName === 'FOOTER') {
        nextPage!.appendChild(child.cloneNode(true))
      } else if (child.tagName === 'ARTICLE') {
        nextPage!.appendChild(articleTemplate.cloneNode(false))
      }
    })
    if (!getDirectPageChild(nextPage, 'ARTICLE')) {
      nextPage.appendChild(articleTemplate.cloneNode(false))
    }
    if (page.parentElement) {
      page.parentElement.insertBefore(nextPage, page.nextSibling)
    }
  }

  let nextArticle = getDirectPageChild(nextPage, 'ARTICLE')
  if (!nextArticle) {
    nextArticle = articleTemplate.cloneNode(false) as HTMLElement
    nextPage.appendChild(nextArticle)
  }
  return nextArticle
}

function cloneTableShell(source: HTMLTableElement): HTMLTableElement {
  // 只克隆 table 的"结构骨架" — colgroup / col / caption. **绝对不能深克隆 TR /
  // THEAD / TBODY / TFOOT**: docx-preview 渲的 table 把 TR 直接挂 table 当 children,
  // 旧实现 `cloneNode(true)` 把这些 TR 一并复制到 nextTable, 再叠加 split 流程
  // 把 source 的 row 移过来 = nextTable 既有克隆 row 又有移过来的 row, 总数翻倍,
  // 撑大新 page 触发再拆 再翻倍, 拆几次后 row 仍未减少. 现在只取 structural 配置,
  // row 全靠后续 split 流程主动 appendChild 移入.
  const cloned = source.cloneNode(false) as HTMLTableElement
  Array.from(source.children).forEach((child) => {
    if (
      child.tagName === 'COLGROUP' ||
      child.tagName === 'COL' ||
      child.tagName === 'CAPTION'
    ) {
      cloned.appendChild(child.cloneNode(true))
    }
  })
  if (!cloned.tBodies[0]) {
    cloned.appendChild(document.createElement('tbody'))
  }
  return cloned
}

function ensureTableBody(table: HTMLTableElement): HTMLTableSectionElement {
  const existing = table.tBodies[0]
  if (existing) return existing
  const body = document.createElement('tbody')
  table.appendChild(body)
  return body
}

function collectFollowingSiblings(startNode: HTMLElement): HTMLElement[] {
  const nodes: HTMLElement[] = []
  let node = startNode.nextElementSibling as HTMLElement | null
  while (node) {
    nodes.push(node)
    node = node.nextElementSibling as HTMLElement | null
  }
  return nodes
}

function moveNodeAndFollowingSiblings(
  startNode: HTMLElement,
  targetArticle: HTMLElement
): void {
  const nodes: HTMLElement[] = []
  let node: HTMLElement | null = startNode
  while (node) {
    nodes.push(node)
    node = node.nextElementSibling as HTMLElement | null
  }
  const anchor = targetArticle.firstChild
  nodes.forEach((item) => {
    targetArticle.insertBefore(item, anchor)
  })
}

function buildRangePayload(
  workbook: ExcelJS.Workbook,
  sheetIdx: number,
  range: CellRange,
  cellImages: CellImagesMap = new Map()
): RangePayload | null {
  const sheet = workbook.worksheets[sheetIdx]
  if (!sheet) return null

  const minR = Math.min(range.anchor.r, range.focus.r)
  const maxR = Math.max(range.anchor.r, range.focus.r)
  const minC = Math.min(range.anchor.c, range.focus.c)
  const maxC = Math.max(range.anchor.c, range.focus.c)

  // 合并单元格 master 查询（直接复用 sheet 内部）
  const mergesRaw = (sheet as any).model?.merges as string[] | undefined
  const mergedMasterOf = new Map<string, { r: number; c: number }>()
  if (Array.isArray(mergesRaw)) {
    mergesRaw.forEach((mref) => {
      const [start, end] = mref.split(':')
      if (!start || !end) return
      const sC = colLetterToNum(start.match(/[A-Z]+/)?.[0] || '')
      const sR = parseInt(start.match(/\d+/)?.[0] || '0', 10)
      const eC = colLetterToNum(end.match(/[A-Z]+/)?.[0] || '')
      const eR = parseInt(end.match(/\d+/)?.[0] || '0', 10)
      for (let r = sR; r <= eR; r++) {
        for (let c = sC; c <= eC; c++) {
          if (r === sR && c === sC) continue
          mergedMasterOf.set(`${r}-${c}`, { r: sR, c: sC })
        }
      }
    })
  }

  // 列头：优先选区顶行上方最近的「text」行；若选区包含第 1 行，就用第 1 行自身
  const headerRowR = findHeaderRow(sheet, minR, maxR, minC, maxC)
  const headers: Record<string, string> = {}
  if (headerRowR) {
    for (let c = minC; c <= maxC; c++) {
      const cell = sheet.getCell(headerRowR, c)
      // 跨列合并的非 master 位置:ExcelJS getCell().value 会返回 master 的值,
      // 如果不跳过,N 列 headers 都会填同一长串文本(chip / prompt 都会重复展示)
      const master: any = (cell as any).master
      if (master && master !== cell && master.col !== cell.col) continue
      const text = cellValueToDisplayText(cell.value)
      if (text) headers[getColumnLetter(c)] = text
    }
  }

  // 列类型推断：扫该列前 50 行看主流类型
  const columnTypes: Record<string, CellDataType> = {}
  for (let c = minC; c <= maxC; c++) {
    columnTypes[getColumnLetter(c)] = inferColumnType(sheet, c, headerRowR)
  }

  // 预扫 sheet 的所有浮动图片：按 tl cell 索引，方便快速查
  const floatImageByCell = buildFloatImageIndex(workbook, sheet)

  // 单元格矩阵
  const cells: CellSnapshot[][] = []
  const emptyRefs: string[] = []
  for (let r = minR; r <= maxR; r++) {
    const row: CellSnapshot[] = []
    for (let c = minC; c <= maxC; c++) {
      const ref = `${getColumnLetter(c)}${r}`
      const mergedMaster = mergedMasterOf.get(`${r}-${c}`)
      // 合并 non-master 位置:不复制 master 的内容到本 cell snapshot,
      // 否则合并块会让 cells 数组里出现 N 个携带同样 displayText/imageSrc 的 cell,
      // chip 和 prompt 都会重复展示同一份内容。master 位置正常携带,non-master 设空。
      if (mergedMaster) {
        row.push({
          ref,
          val: null,
          displayText: '',
          type: 'empty',
          // 不是真"空"(数据上属于 master),不进 emptyRefs 避免被 AI 当成填写目标
          isEmpty: false,
          isMergedPart: true,
          imageSrc: undefined
        })
        continue
      }
      const effectiveCell = sheet.getCell(r, c)
      // 图片 src 优先级：IMAGE() 公式 URL > WPS DISPIMG dataURL > 浮动图片 base64
      // DISPIMG 是 WPS 2020+ 把图片放进单元格的方式(=DISPIMG("ID", 1)),不走
      // 常规 drawings/floating 路径,必须单独查 cellImages 表
      const imgFormulaUrl = tryExtractImageFormulaUrl(effectiveCell)
      const dispImgId = tryExtractDispImgId(effectiveCell)
      const dispImgSrc = dispImgId ? cellImages.get(dispImgId) : undefined
      const floatImgSrc = floatImageByCell.get(`${r}-${c}`)
      const imageSrc = imgFormulaUrl || dispImgSrc || floatImgSrc
      const type = imageSrc ? 'image' : inferCellType(effectiveCell)
      const isEmpty = !imageSrc && type === 'empty'
      if (isEmpty) emptyRefs.push(ref)
      const displayText = imageSrc
        ? '[图片]'
        : cellValueToDisplayText(effectiveCell.value)
      row.push({
        ref,
        val: normalizeCellValue(effectiveCell.value),
        displayText,
        type,
        isEmpty,
        isMergedPart: false,
        imageSrc: imageSrc || undefined
      })
    }
    cells.push(row)
  }

  // 样本行：选区上方与列头之间的 2 行（或 headerRow 下方接壤的 2 行）
  const sampleRows: CellSnapshot[][] = []
  if (headerRowR) {
    const sampleStart = Math.max(headerRowR + 1, minR - 2)
    const sampleEnd = minR - 1
    for (let r = sampleStart; r <= sampleEnd && sampleRows.length < 2; r++) {
      if (r === headerRowR) continue
      const row: CellSnapshot[] = []
      for (let c = minC; c <= maxC; c++) {
        const cell = sheet.getCell(r, c)
        const type = inferCellType(cell)
        row.push({
          ref: `${getColumnLetter(c)}${r}`,
          val: normalizeCellValue(cell.value),
          displayText: cellValueToDisplayText(cell.value),
          type,
          isEmpty: type === 'empty'
        })
      }
      // 样本行全空就不加
      if (row.some((c) => !c.isEmpty)) sampleRows.push(row)
    }
  }

  // 列是否已有数据：先看选区内（除表头行）；不够再看选区下方最多 1000 行。
  // 用于让 quick action 区分"帮我填"与"帮我填（修改）"
  const columnHasData: Record<string, boolean> = {}
  const lastRowToScan = Math.min(
    Math.max((sheet as any).actualRowCount || 0, maxR),
    1000
  )
  for (let c = minC; c <= maxC; c++) {
    const colLetter = getColumnLetter(c)
    let has = false
    for (let r = minR; r <= maxR; r++) {
      if (r === headerRowR) continue
      if (inferCellType(sheet.getCell(r, c)) !== 'empty') {
        has = true
        break
      }
    }
    if (!has) {
      const startR = Math.max(maxR, headerRowR ?? 0) + 1
      for (let r = startR; r <= lastRowToScan; r++) {
        if (inferCellType(sheet.getCell(r, c)) !== 'empty') {
          has = true
          break
        }
      }
    }
    columnHasData[colLetter] = has
  }

  return {
    sheet: sheet.name,
    range: `${getColumnLetter(minC)}${minR}:${getColumnLetter(maxC)}${maxR}`,
    rows: maxR - minR + 1,
    cols: maxC - minC + 1,
    headers,
    columnTypes,
    cells,
    emptyRefs,
    sampleRows: sampleRows.length > 0 ? sampleRows : undefined,
    headerRowR: headerRowR ?? null,
    columnHasData
  }
}

/** 预扫 sheet 里所有浮动图片，按 tl cell (1-indexed "r-c") 建 base64 索引。
 * chip 展示缩略图用；tl cell 只取第一张图（一般也只有一张）。
 */
function buildFloatImageIndex(
  workbook: ExcelJS.Workbook,
  sheet: ExcelJS.Worksheet
): Map<string, string> {
  const map = new Map<string, string>()
  try {
    const images = (sheet as any).getImages?.() as any[] | undefined
    if (!Array.isArray(images)) return map
    for (const imgInfo of images) {
      const tl = imgInfo.range?.tl
      if (!tl) continue
      const tlR =
        (typeof tl.nativeRow === 'number'
          ? tl.nativeRow
          : Math.floor(tl.row || 0)) + 1
      const tlC =
        (typeof tl.nativeCol === 'number'
          ? tl.nativeCol
          : Math.floor(tl.col || 0)) + 1
      const key = `${tlR}-${tlC}`
      if (map.has(key)) continue
      const media = (workbook as any).getImage?.(imgInfo.imageId) as
        | { buffer?: ArrayBuffer | Uint8Array; extension?: string }
        | undefined
      if (!media || !media.buffer) continue
      const b64 = bufferToBase64(media.buffer)
      const ext = media.extension || 'png'
      map.set(key, `data:image/${ext};base64,${b64}`)
    }
  } catch {
    // 降级：没图就没图
  }
  return map
}

/** 给 UI chip 用的简短摘要 */
function summarizeRangePayload(p: RangePayload, cellCount: number): string {
  const r = displayRange(p.range)
  if (cellCount === 1) {
    const first = p.cells[0]?.[0]
    if (!first) return `单元格 ${r}`
    return first.isEmpty
      ? `${r} (空)`
      : `${r} · ${(first.displayText || '').slice(0, 30)}`
  }
  const filled = cellCount - p.emptyRefs.length
  const headerList = Object.values(p.headers).slice(0, 3).join(' · ')
  const tail = headerList ? ` · ${headerList}` : ''
  return `${r} · ${cellCount} 个单元格（${filled} 已填/${p.emptyRefs.length} 待填）${tail}`
}

// ─── 类型推断 / 列头查找 ─────────────────

/** 简单策略：选区顶行之上，往上扫最多 3 行，找第一行「全列都是 text 且非空」的当作 header。
 *  如果选区本身就包含第 1 行，认为第 1 行就是 header 返回 1。
 */
function findHeaderRow(
  sheet: ExcelJS.Worksheet,
  minR: number,
  maxR: number,
  minC: number,
  maxC: number
): number | null {
  if (minR === 1) return 1
  for (let r = minR - 1; r >= Math.max(1, minR - 3); r--) {
    let allText = true
    let allNonEmpty = true
    for (let c = minC; c <= maxC; c++) {
      const cell = sheet.getCell(r, c)
      const t = inferCellType(cell)
      if (t === 'empty') {
        allNonEmpty = false
        break
      }
      if (t !== 'text' && t !== 'hyperlink') {
        allText = false
        break
      }
    }
    if (allText && allNonEmpty) return r
  }
  // 退路:选区顶行-1 必须**所有 cell 都是文字/链接**才当 header
  // (旧实现只要 "非空" 就当 header,日期/数字 cell 会被误判 — 用户选 C6:C7
  //  时 C5 是日期 "2026-04-04",会被塞进 prompt 当列名;改成只接受 text)
  if (minR > 1) {
    let allTextish = true
    for (let c = minC; c <= maxC; c++) {
      const t = inferCellType(sheet.getCell(minR - 1, c))
      if (t !== 'text' && t !== 'hyperlink') {
        allTextish = false
        break
      }
    }
    if (allTextish) return minR - 1
  }
  return null
}

function inferColumnType(
  sheet: ExcelJS.Worksheet,
  c: number,
  headerRow: number | null
): CellDataType {
  const startR = headerRow ? headerRow + 1 : 1
  const endR = Math.min(sheet.rowCount, startR + 50)
  const counts: Partial<Record<CellDataType, number>> = {}
  for (let r = startR; r <= endR; r++) {
    const t = inferCellType(sheet.getCell(r, c))
    if (t === 'empty') continue
    counts[t] = (counts[t] || 0) + 1
  }
  const entries = Object.entries(counts) as Array<[CellDataType, number]>
  if (entries.length === 0) return 'empty'
  entries.sort((a, b) => b[1] - a[1])
  return entries[0][0]
}

function inferCellType(cell: ExcelJS.Cell): CellDataType {
  const v: any = cell.value
  if (v == null || v === '') return 'empty'
  // IMAGE() 公式
  if (tryExtractImageFormulaUrl(cell)) return 'image'
  if (typeof v === 'number') return 'number'
  if (v instanceof Date) return 'date'
  if (typeof v === 'boolean') return 'boolean'
  if (typeof v === 'object') {
    if ('formula' in v) return 'formula'
    if ('hyperlink' in v) return 'hyperlink'
    if (Array.isArray(v.richText)) return 'text'
    if ('result' in v) return 'formula'
    if ('error' in v) return 'empty'
  }
  if (typeof v === 'string') {
    if (v.trim() === '') return 'empty'
    return 'text'
  }
  return 'text'
}

function cellValueToDisplayText(v: any): string {
  if (v == null) return ''
  if (typeof v === 'string') return v.trim()
  if (typeof v === 'number') return String(v)
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
  if (v instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
  }
  if (typeof v === 'object') {
    if (Array.isArray(v.richText)) {
      return v.richText.map((rt: any) => String(rt.text || '')).join('')
    }
    if ('result' in v) return v.result == null ? '' : String(v.result)
    if ('hyperlink' in v) return String(v.text || v.hyperlink)
    if ('formula' in v) return String(v.formula || '')
    if ('error' in v) return String(v.error)
  }
  return String(v).trim()
}

function normalizeCellValue(v: any): string | number | null {
  if (v == null) return null
  if (typeof v === 'string' || typeof v === 'number') return v
  if (typeof v === 'boolean') return v ? 1 : 0
  if (v instanceof Date) return v.getTime()
  const text = cellValueToDisplayText(v)
  return text === '' ? null : text
}
